/**
 * Generated by orval v8.12.3 🍺
 * Do not edit manually.
 * TorqueTitan API
 * Auto repair shop management API
 * OpenAPI spec version: 1.0.0
 */
export type LoginResponse = {
  /** Short-lived JWT access token. Stored in memory by the client. */
  accessToken: string;
  /** Access token expiration time in seconds */
  expiresIn: number;
  /** Unix timestamp when the access token expires */
  expiresAt?: number;
  /** Token type, always bearer */
  tokenType: string;
};

/**
 * Field-level validation errors
 */
export type ApiErrorFields = { [key: string]: string[] };

export type ApiError = {
  /** Error message describing what went wrong */
  error: string;
  /** Field-level validation errors */
  fields?: ApiErrorFields;
};

export type LoginRequest = {
  /** User email address */
  email: string;
  /**
   * User password
   * @minLength 1
   * @maxLength 128
   */
  password: string;
};

/**
 * The authentication provider used for this account, or null if not found
 */
export type CheckIdentityResponseProvider =
  | (typeof CheckIdentityResponseProvider)[keyof typeof CheckIdentityResponseProvider]
  | null;

export const CheckIdentityResponseProvider = {
  google: 'google',
  email: 'email',
} as const;

export type CheckIdentityResponse = {
  /** Whether an account exists with this email */
  exists: boolean;
  /** The authentication provider used for this account, or null if not found */
  provider: CheckIdentityResponseProvider;
};

export type CheckIdentityRequest = {
  /** Email address to check */
  email: string;
};

export type SignupRequest = {
  /** User email address */
  email: string;
  /**
   * User password (min 8 chars, must include at least one uppercase letter, one lowercase letter, one number, and one special character)
   * @minLength 8
   * @maxLength 128
   * @pattern [a-z]
   */
  password: string;
  /**
   * User first name
   * @minLength 1
   */
  firstName: string;
  /**
   * User last name
   * @minLength 1
   */
  lastName: string;
};

export type GoogleLoginRequest = {
  /**
   * Google ID token (JWT) returned by Google Identity Services
   * @minLength 1
   */
  idToken: string;
};

export type RefreshRequest = { [key: string]: unknown };

export type MessageResponse = {
  /** Human-readable response message */
  message: string;
};

export type LogoutRequest = { [key: string]: unknown };

export type ForgotPasswordRequest = {
  /** Email address to send the reset link to */
  email: string;
};

export type ResendVerificationRequest = {
  /** Email address to re-send the verification link to */
  email: string;
};

export type ResetPasswordRequest = {
  /**
   * Password reset token received by email
   * @minLength 1
   */
  token: string;
  /**
   * New password (min 8 chars, must include at least one uppercase letter, one lowercase letter, one number, and one special character)
   * @minLength 8
   * @maxLength 128
   * @pattern [a-z]
   */
  password: string;
};

export type VerifyEmailRequest = {
  /**
   * Email verification token received by email
   * @minLength 1
   */
  token: string;
};

/**
 * User role
 */
export type BaseUserRole = (typeof BaseUserRole)[keyof typeof BaseUserRole];

export const BaseUserRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type BaseUser = {
  id: string;
  avatarUrl: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** User role */
  role: BaseUserRole;
};

export type Onboarding = {
  isCompleted: boolean;
  isDismissed: boolean;
};

export type TechnicianInfo = {
  isActive: boolean;
  color: string | null;
};

/**
 * User role
 */
export type UserRoleRole = (typeof UserRoleRole)[keyof typeof UserRoleRole];

export const UserRoleRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type UserRole = {
  /** User role */
  role: UserRoleRole;
  shopId: string | null;
};

export type Shop = {
  id: string;
  name: string;
  address: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  phone: string | null;
  phoneExtension: string | null;
  email: string | null;
  website: string | null;
  /** Business registration or license number */
  businessRegistrationNumber: string | null;
  shopType: string;
  /** Whether this shop runs in demo/test mode */
  isDemo: boolean;
  logoUrl: string | null;
  adminUserId: string;
  createdAt: string;
  updatedAt: string;
};

export type PermissionSetRepairOrders = {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
  approve: boolean;
  overrideApproved: boolean;
  forceComplete: boolean;
  viewAudit: boolean;
  viewFinancials: boolean;
};

export type PermissionSetCustomers = {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
};

export type PermissionSetVehicles = {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
};

export type PermissionSetInventory = {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
};

export type PermissionSetAppointments = {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
};

export type PermissionSetInvoices = {
  read: boolean;
  create: boolean;
  update: boolean;
  void: boolean;
  delete: boolean;
};

export type PermissionSetReports = {
  read: boolean;
  create: boolean;
};

export type PermissionSetMessages = {
  read: boolean;
  send: boolean;
  delete: boolean;
};

export type PermissionSetShopSettings = {
  read: boolean;
  update: boolean;
};

export type PermissionSetUsers = {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
  invite: boolean;
};

export type PermissionSetPricing = {
  read: boolean;
  update: boolean;
};

export type PermissionSetEstimateSettings = {
  read: boolean;
  update: boolean;
};

export type PermissionSetTaxSettings = {
  read: boolean;
  update: boolean;
};

export type PermissionSetAppointmentSettings = {
  read: boolean;
  update: boolean;
};

export type PermissionSetIntegrations = {
  read: boolean;
  update: boolean;
};

export type PermissionSetTimeEntries = {
  readOwn: boolean;
  readAll: boolean;
  clockSelf: boolean;
  clockOnBehalfOf: boolean;
  manualCreate: boolean;
  edit: boolean;
  editApproved: boolean;
  delete: boolean;
  approve: boolean;
};

export type PermissionSetTimeTrackingSettings = {
  read: boolean;
  update: boolean;
};

export type PermissionSetTags = {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
};

export type PermissionSetTechnicians = {
  read: boolean;
  update: boolean;
};

export type PermissionSetNotifications = {
  read: boolean;
  cancel: boolean;
};

export type PermissionSetDiscounts = {
  apply: boolean;
};

export type PermissionSetCoupons = {
  list: boolean;
  write: boolean;
  archive: boolean;
  apply: boolean;
};

export type PermissionSet = {
  repairOrders: PermissionSetRepairOrders;
  customers: PermissionSetCustomers;
  vehicles: PermissionSetVehicles;
  inventory: PermissionSetInventory;
  appointments: PermissionSetAppointments;
  invoices: PermissionSetInvoices;
  reports: PermissionSetReports;
  messages: PermissionSetMessages;
  shopSettings: PermissionSetShopSettings;
  users: PermissionSetUsers;
  pricing: PermissionSetPricing;
  estimateSettings: PermissionSetEstimateSettings;
  taxSettings: PermissionSetTaxSettings;
  appointmentSettings: PermissionSetAppointmentSettings;
  integrations: PermissionSetIntegrations;
  timeEntries: PermissionSetTimeEntries;
  timeTrackingSettings: PermissionSetTimeTrackingSettings;
  tags: PermissionSetTags;
  technicians: PermissionSetTechnicians;
  notifications: PermissionSetNotifications;
  discounts: PermissionSetDiscounts;
  coupons: PermissionSetCoupons;
};

/**
 * Subscription tier
 */
export type SubscriptionInfoTier = (typeof SubscriptionInfoTier)[keyof typeof SubscriptionInfoTier];

export const SubscriptionInfoTier = {
  free: 'free',
  pro: 'pro',
  elite: 'elite',
} as const;

/**
 * Subscription feature
 */
export type SubscriptionInfoFeaturesItem =
  (typeof SubscriptionInfoFeaturesItem)[keyof typeof SubscriptionInfoFeaturesItem];

export const SubscriptionInfoFeaturesItem = {
  basic_invoicing: 'basic_invoicing',
  customer_management: 'customer_management',
  customer_link_progress: 'customer_link_progress',
  dvi: 'dvi',
  dvi_photos: 'dvi_photos',
  dvi_video: 'dvi_video',
  reports: 'reports',
  sms: 'sms',
  system_notifications: 'system_notifications',
  multi_location: 'multi_location',
  ai_assistant: 'ai_assistant',
  ai_summary: 'ai_summary',
  ai_suggested_jobs: 'ai_suggested_jobs',
  labor_guide: 'labor_guide',
  gross_profit: 'gross_profit',
  time_tracking: 'time_tracking',
  tech_efficiency_report: 'tech_efficiency_report',
  marketing: 'marketing',
  dot_code_capture: 'dot_code_capture',
  inventory: 'inventory',
  team_management: 'team_management',
  option_groups: 'option_groups',
  quickbooks: 'quickbooks',
} as const;

export type SubscriptionInfoInterval = (typeof SubscriptionInfoInterval)[keyof typeof SubscriptionInfoInterval] | null;

export const SubscriptionInfoInterval = {
  month: 'month',
  year: 'year',
} as const;

export type SubscriptionInfo = {
  /** Subscription tier */
  tier: SubscriptionInfoTier;
  features: SubscriptionInfoFeaturesItem[];
  expiresAt: string | null;
  stripeCustomerId: string | null;
  interval: SubscriptionInfoInterval;
  /** Free RO allowance; -1 means unlimited. */
  freeRosAllowance: number;
  freeRosUsed: number;
  /** Free RO conversions left before metering; null when the allowance is unlimited. */
  freeRosRemaining: number | null;
  /** True when the org has an unlimited free-RO allowance (free_ros_allowance = -1). */
  freeRosUnlimited: boolean;
  paymentMethodOnFile: boolean;
  /** True when the subscription is scheduled to cancel at the end of the current billing period. */
  cancelAtPeriodEnd: boolean;
};

export type CurrentUserNullable = BaseUser &
  ({
    currentShopId: string | null;
    /** Whether the user has verified their email address via the signup verification link. */
    emailVerified: boolean;
    onboarding: Onboarding;
    technician: TechnicianInfo;
    roles: UserRole[];
    shops: Shop[];
    permissions: PermissionSet;
    subscription: SubscriptionInfo;
    /** Stripe publishable key to use on the frontend — matches the current shop's live/demo mode */
    stripePublishableKey: string;
    /** The user's organization-level role, derived from organization_members. Null when the user has no organization_members row (e.g., technicians with shop-only access). */
    orgRole: 'org_owner' | 'org_admin' | null;
    /** The id of the organization the user belongs to (one-org-per-user in v1). Null when the user has no organization_members row. */
    organizationId: string | null;
    /** Timestamp string (may be ISO 8601 or Postgres wire format depending on driver) until which the one-time 'we\'ve simplified onboarding' dashboard banner should render. NULL = banner is not eligible (typical case). Set by the Plan 3 migration to NOW() + 7 days for users with !onboarding_completed && !onboarding_dismissed; PATCHed to a past value by the dismiss endpoint. */
    setupAnnouncementShowUntil: string | null;
    /** Timestamp at which the user permanently dismissed the dashboard Setup panel. NULL = panel is still eligible to render. Set by POST /current-user/setup-panel/dismiss. One-way: no resurrection. */
    setupPanelDismissedAt: string | null;
  } | null);

export type UpdateProfile = {
  /** @minLength 1 */
  firstName: string;
  /** @minLength 1 */
  lastName: string;
  phone: string | null;
  avatarUrl: string | null;
};

export type UploadAvatarResponse = {
  avatarUrl: string;
};

export type SetCurrentShopBody = {
  shopId: string;
};

export type PublicInvitationResponse = {
  token: string;
  email: string;
  role: string;
  shopName: string;
  expiresAt: string;
  status: string;
};

/**
 * User role
 */
export type AcceptInvitationResponseUserRole =
  | (typeof AcceptInvitationResponseUserRole)[keyof typeof AcceptInvitationResponseUserRole]
  | null;

export const AcceptInvitationResponseUserRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type AcceptInvitationResponseUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  /** User role */
  role: AcceptInvitationResponseUserRole;
  shopId: string | null;
};

export type AcceptInvitationResponseSession = {
  accessToken: string;
  expiresIn: number;
};

export type AcceptInvitationResponse = {
  user: AcceptInvitationResponseUser;
  session: AcceptInvitationResponseSession;
};

export type AcceptInvitationRequest = {
  /**
   * First name of the user
   * @minLength 1
   */
  firstName: string;
  /**
   * Last name of the user
   * @minLength 1
   */
  lastName: string;
  /**
   * Password for the new account (minimum 8 characters)
   * @minLength 8
   */
  password: string;
  /** Phone number (optional) */
  phone?: string;
};

export type ShopType = {
  id: string;
  value: string;
  label: string;
  isActive: boolean;
  displayOrder: number;
};

export type ShopTypesResponse = ShopType[];

export type PublicOperatingHours = {
  /**
   * 0=Sunday, 6=Saturday
   * @minimum 0
   * @maximum 6
   */
  dayOfWeek: number;
  isActive: boolean;
  startTime: string;
  endTime: string;
};

export type PublicHoliday = {
  /** YYYY-MM-DD */
  date: string;
  name: string;
};

export type PublicShop = {
  id: string;
  name: string;
  phone: string | null;
  phoneExtension: string | null;
  address: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  email: string | null;
  website: string | null;
  logoUrl: string | null;
  /** Whether the shop requires customers to provide their address when booking */
  requireCustomerAddress: boolean;
  /** Weekly operating hours (one entry per day of week) */
  operatingHours: PublicOperatingHours[];
  /** Future full-day holidays where the shop is closed */
  holidays: PublicHoliday[];
  /** Default appointment duration in minutes */
  defaultDurationMinutes: number;
};

/**
 * Operating hours for the requested date (null if shop is closed)
 */
export type AvailableSlotsOperatingHours = {
  /** Shop opening time */
  startTime: string;
  /** Shop closing time */
  endTime: string;
} | null;

export type TimeSlot = {
  /** Slot start time */
  startTime: string;
  /** Slot end time */
  endTime: string;
};

export type AvailableSlots = {
  /** List of time slots for the requested date */
  slots: TimeSlot[];
  /** Operating hours for the requested date (null if shop is closed) */
  operatingHours: AvailableSlotsOperatingHours;
};

export type AppointmentResult = {
  /** Created appointment UUID */
  appointmentId: string;
  /** Associated repair order UUID */
  repairOrderId: string;
  /** Human-readable repair order number */
  repairOrderNumber: string | null;
  /** Appointment date */
  appointmentDate: string;
  /** Appointment start time */
  startTime: string;
  /** Appointment end time */
  endTime: string;
  /** Public token for the unified service-request link (/s/:publicToken) */
  publicToken: string;
};

export type CreatePublicAppointmentRequestCustomer = {
  /**
   * Customer first name
   * @minLength 1
   */
  firstName: string;
  /**
   * Customer last name
   * @minLength 1
   */
  lastName: string;
  /** Customer email address */
  email: string;
  /** Customer phone number */
  phone?: string;
  /** Customer street address (line 1) */
  address?: string;
  /** Customer address line 2 (apartment, suite, etc.) */
  addressLine2?: string;
  /** Customer city */
  city?: string;
  /** Customer state (2-letter code) */
  state?: string;
  /** Customer zip code */
  zipCode?: string;
};

export type CreatePublicAppointmentRequestVehicle = {
  /**
   * Vehicle model year
   * @minimum 1900
   * @maximum 2100
   */
  year: number;
  /**
   * Vehicle make
   * @minLength 1
   */
  make: string;
  /**
   * Vehicle model
   * @minLength 1
   */
  model: string;
  /**
   * Vehicle color (NCIC standard name) — used for AI image generation
   * @minLength 1
   */
  color?: string | null;
};

/**
 * Type of service
 */
export type CreatePublicAppointmentRequestAppointmentServiceType =
  (typeof CreatePublicAppointmentRequestAppointmentServiceType)[keyof typeof CreatePublicAppointmentRequestAppointmentServiceType];

export const CreatePublicAppointmentRequestAppointmentServiceType = {
  'drop-off': 'drop-off',
  wait: 'wait',
  'tow-in': 'tow-in',
} as const;

export type CreatePublicAppointmentRequestAppointment = {
  /**
   * Appointment date
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  date: string;
  /**
   * Appointment start time
   * @pattern ^\d{2}:\d{2}$
   */
  startTime: string;
  /**
   * Appointment end time
   * @pattern ^\d{2}:\d{2}$
   */
  endTime: string;
  /** Type of service */
  serviceType?: CreatePublicAppointmentRequestAppointmentServiceType;
  /**
   * Reason for the appointment
   * @minLength 1
   */
  reasonForVisit: string;
};

export type CreatePublicAppointmentRequest = {
  /** Shop UUID for the public booking */
  shopId: string;
  /**
   * Google reCAPTCHA verification token
   * @minLength 1
   */
  recaptchaToken: string;
  customer: CreatePublicAppointmentRequestCustomer;
  vehicle: CreatePublicAppointmentRequestVehicle;
  appointment: CreatePublicAppointmentRequestAppointment;
  /** Whether the customer opted in to receive SMS messages */
  smsOptIn?: boolean;
  /** Full consent text shown to the customer when they opted in */
  consentText?: string;
  /** Browser user agent string for consent audit trail */
  userAgent?: string;
};

export type PublicAppointmentStatus = (typeof PublicAppointmentStatus)[keyof typeof PublicAppointmentStatus];

export const PublicAppointmentStatus = {
  scheduled: 'scheduled',
  arrived: 'arrived',
  rescheduled: 'rescheduled',
  cancelled: 'cancelled',
  no_show: 'no_show',
} as const;

export type PublicAppointmentCustomer = {
  firstName: string;
  lastName: string;
};

export type PublicAppointmentVehicle = {
  year: number;
  make: string;
  model: string;
};

export type PublicAppointment = {
  id: string;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  status: PublicAppointmentStatus;
  reasonForVisit: string | null;
  customer: PublicAppointmentCustomer;
  vehicle: PublicAppointmentVehicle;
};

export type ReschedulePublicRequest = {
  /**
   * New date
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  date: string;
  /**
   * New start time
   * @pattern ^\d{2}:\d{2}$
   */
  startTime: string;
  /**
   * New end time
   * @pattern ^\d{2}:\d{2}$
   */
  endTime: string;
};

export type YearsResponse = number[];

export type MakesResponseItem = {
  makeId?: number;
  makeName?: string;
};

export type MakesResponse = MakesResponseItem[];

export type ModelsResponseItem = {
  modelId?: number;
  modelName?: string;
};

export type ModelsResponse = ModelsResponseItem[];

export type SubmodelsResponseItem = {
  submodelId?: number;
  submodelName?: string;
  region?: string | null;
  regions?: string[];
};

export type SubmodelsResponse = SubmodelsResponseItem[];

export type EnginesResponseItemEngineParams = { [key: string]: unknown | null };

export type EnginesResponseItem = {
  engineId?: number;
  engineName?: string;
  engineParams?: EnginesResponseItemEngineParams;
  region?: string | null;
  regions?: string[];
};

export type EnginesResponse = EnginesResponseItem[];

/**
 * Invoice lifecycle status
 */
export type PublicInvoiceStatus = (typeof PublicInvoiceStatus)[keyof typeof PublicInvoiceStatus];

export const PublicInvoiceStatus = {
  draft: 'draft',
  open: 'open',
  partially_paid: 'partially_paid',
  paid: 'paid',
  voided: 'voided',
  uncollectible: 'uncollectible',
} as const;

export type LineItem = {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  type: string | null;
};

export type PublicInvoice = {
  invoiceNumber: string;
  /** Invoice lifecycle status */
  status: PublicInvoiceStatus;
  customerName: string | null;
  amountSubtotal: number;
  amountTax: number;
  amountFet: number;
  amountTotal: number;
  balanceDue: number;
  currency: string;
  createdAt: string;
  sentAt: string | null;
  dueDate: string | null;
  customerAddressLine1: string | null;
  customerAddressLine2: string | null;
  customerCity: string | null;
  customerState: string | null;
  customerZipCode: string | null;
  taxRate: number | null;
  taxJurisdiction: string | null;
  shopName: string;
  shopAddress: string | null;
  shopAddressLine2: string | null;
  shopCity: string | null;
  shopState: string | null;
  shopZipCode: string | null;
  shopPhone: string | null;
  shopEmail: string | null;
  shopBusinessRegistrationNumber: string | null;
  stripeEnabled: boolean;
  lineItems: LineItem[];
};

export type StripeCheckoutResponse = {
  checkoutUrl: string;
};

export type CreateStripeCheckoutRequest = {
  /** URL to redirect to after successful payment */
  successUrl: string;
  /** URL to redirect to if payment is cancelled */
  cancelUrl: string;
};

export type VerifyPaymentResponseStatus =
  (typeof VerifyPaymentResponseStatus)[keyof typeof VerifyPaymentResponseStatus];

export const VerifyPaymentResponseStatus = {
  paid: 'paid',
  partially_paid: 'partially_paid',
  open: 'open',
  pending: 'pending',
} as const;

export type VerifyPaymentResponse = {
  status: VerifyPaymentResponseStatus;
};

export type WebhookResponse = {
  received: boolean;
};

export type PartsOrderCallbackResponse = {
  received: boolean;
};

export type PartsOrderCallbackBodyOrdersItemSupplier = {
  name?: string;
  id?: number;
  [key: string]: unknown | null;
};

export type PartsOrderCallbackBodyOrdersItemStore = {
  name?: string;
  id?: number;
  [key: string]: unknown | null;
};

export type PartsOrderCallbackBodyOrdersItemPartsItemPrice = {
  price?: number;
  cost?: number;
  list?: number;
  [key: string]: unknown | null;
};

export type PartsOrderCallbackBodyOrdersItemPartsItemBrand = {
  brandName?: string;
  brandId?: number;
  [key: string]: unknown | null;
};

export type PartsOrderCallbackBodyOrdersItemPartsItemTaxonomy = {
  categoryName?: string;
  partTypeName?: string;
  partTypeId?: number;
  [key: string]: unknown | null;
};

export type PartsOrderCallbackBodyOrdersItemPartsItem = {
  orderItemId?: string;
  partId?: string;
  partName?: string;
  partNumber?: string;
  quantity: number;
  price?: PartsOrderCallbackBodyOrdersItemPartsItemPrice;
  brand?: PartsOrderCallbackBodyOrdersItemPartsItemBrand;
  imageUrl?: string | null;
  taxonomy?: PartsOrderCallbackBodyOrdersItemPartsItemTaxonomy;
  partCategory?: string | null;
  tireSize?: string | null;
  vehicleName?: string | null;
  lineCardId?: number | null;
  [key: string]: unknown | null;
};

export type PartsOrderCallbackBodyOrdersItem = {
  supplier?: PartsOrderCallbackBodyOrdersItemSupplier;
  store?: PartsOrderCallbackBodyOrdersItemStore;
  totalPrice?: number;
  parts: PartsOrderCallbackBodyOrdersItemPartsItem[];
  poNumber?: string | null;
  [key: string]: unknown | null;
};

export type PartsOrderCallbackBodyUser = {
  username?: string;
  shopName?: string;
  [key: string]: unknown | null;
};

export type PartsOrderCallbackBodyAction =
  (typeof PartsOrderCallbackBodyAction)[keyof typeof PartsOrderCallbackBodyAction];

export const PartsOrderCallbackBodyAction = {
  SUBMIT_QUOTE: 'SUBMIT_QUOTE',
  PURCHASE: 'PURCHASE',
  TIRE_QUOTE: 'TIRE_QUOTE',
} as const;

export type PartsOrderCallbackBody = {
  sessionId: string;
  orders: PartsOrderCallbackBodyOrdersItem[];
  user?: PartsOrderCallbackBodyUser;
  localInventoryItems?: unknown | null;
  action?: PartsOrderCallbackBodyAction;
  selectedLaborItems?: unknown | null;
  [key: string]: unknown | null;
};

export type PublicApproveResponse = {
  approvalStatus: string;
  approvedAt: string;
  /** SHA-256 of the signature payload. Null when the shop disabled signature requirement on send. */
  signatureSha256: string | null;
  ipAddress: string | null;
  userAgent: string | null;
};

export type PublicApproveRequestJobsItemApprovalStatus =
  (typeof PublicApproveRequestJobsItemApprovalStatus)[keyof typeof PublicApproveRequestJobsItemApprovalStatus];

export const PublicApproveRequestJobsItemApprovalStatus = {
  approved: 'approved',
  declined: 'declined',
} as const;

export type PublicApproveRequestJobsItem = {
  id: string;
  approvalStatus: PublicApproveRequestJobsItemApprovalStatus;
};

/**
 * How the signature was captured
 */
export type PublicApproveRequestSignatureMethod =
  (typeof PublicApproveRequestSignatureMethod)[keyof typeof PublicApproveRequestSignatureMethod];

export const PublicApproveRequestSignatureMethod = {
  type: 'type',
  draw: 'draw',
} as const;

export type PublicApproveRequestOptionPicksItem = {
  groupId: string;
  partId: string;
};

export type PublicApproveRequest = {
  /** Digital signature (base64 image or typed name). Empty when shop disabled signature requirement. */
  digitalSignature?: string;
  /**
   * Per-job approval decisions
   * @minItems 1
   */
  jobs: PublicApproveRequestJobsItem[];
  /** Pre-calculated total for approved jobs */
  approvedJobsTotal: number;
  /** How the signature was captured */
  signatureMethod?: PublicApproveRequestSignatureMethod;
  /**
   * Printed name of the person signing. Falls back to the customer-of-record's name when omitted.
   * @minLength 1
   * @maxLength 120
   */
  signerName?: string;
  /**
   * Email of the person signing. Falls back to the customer-of-record's email when omitted.
   * @maxLength 254
   */
  signerEmail?: string;
  /** Customer's choice for each option group. Persisted on approval so the advisor's RO view collapses to the picked option. */
  optionPicks?: PublicApproveRequestOptionPicksItem[];
};

export type PublicApprovalPdfUrlResponse = {
  /** Short-lived signed URL to download the signed approval PDF */
  url: string;
};

export type PublicEstimateResponseEstimateStage =
  (typeof PublicEstimateResponseEstimateStage)[keyof typeof PublicEstimateResponseEstimateStage];

export const PublicEstimateResponseEstimateStage = {
  intake: 'intake',
  in_progress: 'in_progress',
  invoice: 'invoice',
  closed: 'closed',
} as const;

export type PublicEstimateResponseEstimateCustomerApprovalStatus =
  | (typeof PublicEstimateResponseEstimateCustomerApprovalStatus)[keyof typeof PublicEstimateResponseEstimateCustomerApprovalStatus]
  | null;

export const PublicEstimateResponseEstimateCustomerApprovalStatus = {
  pending: 'pending',
  approved: 'approved',
  declined: 'declined',
} as const;

export type PublicEstimateResponseEstimateDiscountType =
  | (typeof PublicEstimateResponseEstimateDiscountType)[keyof typeof PublicEstimateResponseEstimateDiscountType]
  | null;

export const PublicEstimateResponseEstimateDiscountType = {
  percentage: 'percentage',
  amount: 'amount',
} as const;

export type PublicEstimateResponseEstimateDiscountScope =
  | (typeof PublicEstimateResponseEstimateDiscountScope)[keyof typeof PublicEstimateResponseEstimateDiscountScope]
  | null;

export const PublicEstimateResponseEstimateDiscountScope = {
  parts: 'parts',
  labor: 'labor',
  both: 'both',
} as const;

export type PublicEstimateResponseEstimateCustomer = {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
};

export type PublicEstimateResponseEstimateVehicle = {
  year: number | null;
  make: string | null;
  model: string | null;
  color: string | null;
  licensePlate: string | null;
};

export type PublicEstimateResponseEstimateShop = {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  phone: string | null;
  phoneExtension: string | null;
  email: string | null;
  logoUrl: string | null;
  businessRegistrationNumber: string | null;
};

export type PublicEstimateResponseEstimate = {
  id: string;
  repairOrderNumber: string;
  stage: PublicEstimateResponseEstimateStage;
  isEstimate: boolean;
  /** True when the underlying repair order was created by the Starter estimate onboarding flow (synthetic demo data). Customer-facing UIs can use this to render a "demo" cue. */
  isSample: boolean;
  closureReason: string | null;
  customerApprovalStatus: PublicEstimateResponseEstimateCustomerApprovalStatus;
  customerApprovedAt: string | null;
  estimateExpiresAt: string | null;
  estimateToken: string;
  totalAmount: number;
  taxRate: number;
  applyToParts: boolean;
  applyToLabor: boolean;
  applyToTires: boolean;
  discountEnabled: boolean;
  discountType: PublicEstimateResponseEstimateDiscountType;
  discountValue: number;
  discountScope: PublicEstimateResponseEstimateDiscountScope;
  customerIsTaxable: boolean;
  customer: PublicEstimateResponseEstimateCustomer;
  vehicle: PublicEstimateResponseEstimateVehicle;
  shop: PublicEstimateResponseEstimateShop;
};

export type PublicEstimateResponseJobsItemCustomerApprovalStatus =
  | (typeof PublicEstimateResponseJobsItemCustomerApprovalStatus)[keyof typeof PublicEstimateResponseJobsItemCustomerApprovalStatus]
  | null;

export const PublicEstimateResponseJobsItemCustomerApprovalStatus = {
  approved: 'approved',
  declined: 'declined',
  pending: 'pending',
} as const;

export type PublicEstimateResponseJobsItemPartsItem = {
  id: string;
  partName: string;
  partNumber: string | null;
  partTypeName: string | null;
  quantity: number;
  unitPrice: number;
  coreCharge: number;
  optionGroupId: string | null;
  isSelectedOption: boolean;
  isTire: boolean;
  fet: number;
  brand: string | null;
  model: string | null;
  size: string | null;
  originalData?: unknown | null;
};

export type PublicEstimateResponseJobsItemLaborItemStatus =
  | (typeof PublicEstimateResponseJobsItemLaborItemStatus)[keyof typeof PublicEstimateResponseJobsItemLaborItemStatus]
  | null;

export const PublicEstimateResponseJobsItemLaborItemStatus = {
  not_started: 'not_started',
  in_progress: 'in_progress',
  paused: 'paused',
  on_hold: 'on_hold',
  complete: 'complete',
} as const;

export type PublicEstimateResponseJobsItemLaborItem = {
  id: string;
  description: string | null;
  hours: number;
  hourlyRate: number;
  totalCost: number;
  status?: PublicEstimateResponseJobsItemLaborItemStatus;
};

export type PublicEstimateResponseJobsItemFeesItemAmountType =
  (typeof PublicEstimateResponseJobsItemFeesItemAmountType)[keyof typeof PublicEstimateResponseJobsItemFeesItemAmountType];

export const PublicEstimateResponseJobsItemFeesItemAmountType = {
  fixed: 'fixed',
  percentage: 'percentage',
} as const;

export type PublicEstimateResponseJobsItemFeesItem = {
  id: string;
  name: string;
  amount: number;
  amountType: PublicEstimateResponseJobsItemFeesItemAmountType;
  isTaxable: boolean;
  displayOrder: number;
  catalogId: string | null;
};

export type PublicEstimateResponseJobsItemOptionGroupsItemPreset =
  | (typeof PublicEstimateResponseJobsItemOptionGroupsItemPreset)[keyof typeof PublicEstimateResponseJobsItemOptionGroupsItemPreset]
  | null;

export const PublicEstimateResponseJobsItemOptionGroupsItemPreset = {
  gbb: 'gbb',
  numbered: 'numbered',
} as const;

export type PublicEstimateResponseJobsItemOptionGroupsItem = {
  id: string;
  jobId: string;
  label: string;
  preset: PublicEstimateResponseJobsItemOptionGroupsItemPreset;
  displayOrder: number;
};

export type PublicEstimateResponseJobsItemSubletsItem = {
  id: string;
  description: string | null;
  customerPrice: number;
  taxable: boolean;
};

export type PublicEstimateResponseJobsItem = {
  id: string;
  name: string;
  customerApprovalStatus: PublicEstimateResponseJobsItemCustomerApprovalStatus;
  displayOrder: number;
  parts: PublicEstimateResponseJobsItemPartsItem[];
  labor: PublicEstimateResponseJobsItemLaborItem[];
  fees: PublicEstimateResponseJobsItemFeesItem[];
  optionGroups: PublicEstimateResponseJobsItemOptionGroupsItem[];
  sublets: PublicEstimateResponseJobsItemSubletsItem[];
};

export type PublicEstimateResponseSubletsItem = {
  id: string;
  description: string | null;
  customerPrice: number;
  taxable: boolean;
};

export type PublicEstimateResponseSettingsPartDisplayMode =
  | (typeof PublicEstimateResponseSettingsPartDisplayMode)[keyof typeof PublicEstimateResponseSettingsPartDisplayMode]
  | null;

export const PublicEstimateResponseSettingsPartDisplayMode = {
  full_description: 'full_description',
  part_type_only: 'part_type_only',
} as const;

export type PublicEstimateResponseSettings = {
  expirationDays: number;
  disclosuresEnabled: boolean | null;
  disclosuresText: string | null;
  partDisplayMode: PublicEstimateResponseSettingsPartDisplayMode;
} | null;

export type PublicEstimateResponseDisclosuresItem = {
  title: string;
  body: string;
  displayOrder: number;
};

export type PublicEstimateResponseLinkedDvi = {
  id: string;
  shareToken: string | null;
  customerSummary: string | null;
} | null;

export type PublicEstimateResponseSendOptions = {
  requireSignature: boolean;
  includeAiSummary: boolean;
  showInspection: boolean;
  showProgress: boolean;
};

export type PublicEstimateResponseActiveDiscountsItemSource =
  (typeof PublicEstimateResponseActiveDiscountsItemSource)[keyof typeof PublicEstimateResponseActiveDiscountsItemSource];

export const PublicEstimateResponseActiveDiscountsItemSource = {
  customer: 'customer',
  coupon: 'coupon',
  manual: 'manual',
  rebate: 'rebate',
} as const;

export type PublicEstimateResponseActiveDiscountsItemDiscountType =
  (typeof PublicEstimateResponseActiveDiscountsItemDiscountType)[keyof typeof PublicEstimateResponseActiveDiscountsItemDiscountType];

export const PublicEstimateResponseActiveDiscountsItemDiscountType = {
  percent: 'percent',
  fixed: 'fixed',
} as const;

export type PublicEstimateResponseActiveDiscountsItem = {
  id: string;
  source: PublicEstimateResponseActiveDiscountsItemSource;
  discountType: PublicEstimateResponseActiveDiscountsItemDiscountType;
  discountValue: number;
  appliedAmount: number;
  allocatedToParts: number;
  allocatedToLabor: number;
  allocatedToTires: number;
  allocatedToSubs: number;
  allocatedToFees: number;
  reason: string | null;
};

export type JobGrossProfit = {
  partsCost: number;
  laborCost: number;
  partsGp: number;
  laborGp: number;
  gpDollars: number;
  gpPercent: number;
  billedHours: number;
  gpPerHour: number | null;
};

export type JobPricing = {
  jobId: string;
  jobName: string;
  partsSubtotal: number;
  tiresSubtotal: number;
  laborSubtotal: number;
  subletSubtotal: number;
  feeSubtotal: number;
  taxableFeesSubtotal: number;
  taxableSubletsSubtotal: number;
  subtotal: number;
  discountTotal: number;
  taxAmount: number;
  total: number;
  grossProfit: JobGrossProfit;
};

export type PricingBreakdownSubtotal = {
  parts: number;
  tires: number;
  labor: number;
  sublets: number;
  fees: number;
};

export type PricingBreakdown = {
  subtotal: PricingBreakdownSubtotal;
  taxableFeesSubtotal: number;
  discountTotal: number;
  salesTax: number;
  grandTotal: number;
  jobBreakdowns: JobPricing[];
};

/**
 * Interpolation values for the localized block message (e.g. offending job names).
 */
export type WorkflowTransitionEntryVars = { [key: string]: string };

export type WorkflowTransitionEntry = {
  allowed: boolean;
  /** Machine-readable block reason when `allowed` is false. Mapped to localized copy on the client. */
  code?: string;
  /** Interpolation values for the localized block message (e.g. offending job names). */
  vars?: WorkflowTransitionEntryVars;
};

export type PublicEstimateAvailableActions = {
  customer_approve: WorkflowTransitionEntry;
  customer_decline: WorkflowTransitionEntry;
};

export type PublicEstimateResponse = {
  estimate: PublicEstimateResponseEstimate;
  jobs: PublicEstimateResponseJobsItem[];
  sublets: PublicEstimateResponseSubletsItem[];
  settings: PublicEstimateResponseSettings;
  disclosures: PublicEstimateResponseDisclosuresItem[];
  linkedDvi: PublicEstimateResponseLinkedDvi;
  sendOptions: PublicEstimateResponseSendOptions;
  activeDiscounts: PublicEstimateResponseActiveDiscountsItem[];
  pricing: PricingBreakdown;
  availableActions: PublicEstimateAvailableActions;
};

export type PublicEstimatePricingRequestOptionPicksItem = {
  groupId: string;
  partId: string;
};

export type PublicEstimatePricingRequest = {
  /** Customer's current option-group picks. Empty array prices the persisted selection. */
  optionPicks: PublicEstimatePricingRequestOptionPicksItem[];
};

export type PublicInspectionDetailStatus =
  (typeof PublicInspectionDetailStatus)[keyof typeof PublicInspectionDetailStatus];

export const PublicInspectionDetailStatus = {
  in_progress: 'in_progress',
  completed: 'completed',
  reviewed: 'reviewed',
} as const;

export type VehicleInfo = {
  id: string;
  year: number;
  make: string;
  model: string;
  lastMileageUpdate: string | null;
  customerName: string;
} | null;

export type ShopInfo = {
  name: string;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
} | null;

export type InspectionResultItem = {
  id: string;
  status: string | null;
  notes: string | null;
} | null;

export type InspectionAttachment = {
  id: string;
  inspectionItemId: string;
  filePath: string;
  fileName: string;
  fileType: string;
  fileSize: number | null;
  createdAt: string;
};

export type InspectionItemDetail = {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
  result: InspectionResultItem | null;
  attachments: InspectionAttachment[];
};

export type InspectionCategoryDetail = {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
  items: InspectionItemDetail[];
};

export type PublicInspectionDetail = {
  id: string;
  vehicleId: string;
  status: PublicInspectionDetailStatus;
  mileage: number | null;
  customerSummary: string | null;
  createdAt: string;
  vehicle: VehicleInfo | null;
  shop: ShopInfo | null;
  categories: InspectionCategoryDetail[];
};

export type RedirectTokenResponse = {
  publicToken: string;
};

export type ServiceRequestResponseRoEstimateApprovalStatus =
  (typeof ServiceRequestResponseRoEstimateApprovalStatus)[keyof typeof ServiceRequestResponseRoEstimateApprovalStatus];

export const ServiceRequestResponseRoEstimateApprovalStatus = {
  pending: 'pending',
  approved: 'approved',
  declined: 'declined',
} as const;

export type ServiceRequestResponseRo = {
  id: string;
  publicToken: string;
  repairOrderNumber: string | null;
  isDropoff: boolean;
  pickedUp: boolean;
  closedAt: string | null;
  customerApprovedAt: string | null;
  estimateToken: string | null;
  estimateSent: boolean;
  estimateTotal: number | null;
  estimateShowProgress: boolean;
  estimateApprovalStatus: ServiceRequestResponseRoEstimateApprovalStatus;
  promisedPickupDate: string | null;
  mileageIn: number | null;
  mileageOut: number | null;
  workComplete: boolean;
  /** True when the underlying RO is the per-shop pre-baked sample created during onboarding. Lets the public Service Request view render a "Demo estimate" banner. */
  isSample: boolean;
};

export type ServiceRequestResponseCustomer = {
  firstName: string | null;
  lastName: string | null;
};

export type ServiceRequestResponseVehicleImageStatus =
  (typeof ServiceRequestResponseVehicleImageStatus)[keyof typeof ServiceRequestResponseVehicleImageStatus];

export const ServiceRequestResponseVehicleImageStatus = {
  pending: 'pending',
  generating: 'generating',
  ready: 'ready',
  failed: 'failed',
} as const;

export type ServiceRequestResponseVehicle = {
  id: string;
  year: number;
  make: string;
  model: string;
  submodel: string | null;
  color: string | null;
  bodyStyle: string | null;
  imageStatus: ServiceRequestResponseVehicleImageStatus;
  imageUrl: string | null;
};

export type ServiceRequestResponseShop = {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
} | null;

export type ServiceRequestResponseAppointment = {
  id: string;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  serviceType: string | null;
  publicToken: string | null;
  status: string;
} | null;

export type ServiceRequestResponseDvi = {
  id: string;
  shareToken: string | null;
  status: string;
  isShared: boolean;
} | null;

export type ServiceRequestResponseInvoice = {
  id: string;
  invoiceNumber: string;
  publicPaymentToken: string;
  status: string;
  amountTotal: number;
  balanceDue: number | null;
} | null;

export type ServiceRequestResponseActiveStepId =
  (typeof ServiceRequestResponseActiveStepId)[keyof typeof ServiceRequestResponseActiveStepId];

export const ServiceRequestResponseActiveStepId = {
  booked: 'booked',
  vehicle_received: 'vehicle_received',
  inspection: 'inspection',
  estimate: 'estimate',
  in_progress: 'in_progress',
  invoice: 'invoice',
  complete: 'complete',
} as const;

export type ServiceRequestStepId = (typeof ServiceRequestStepId)[keyof typeof ServiceRequestStepId];

export const ServiceRequestStepId = {
  booked: 'booked',
  vehicle_received: 'vehicle_received',
  inspection: 'inspection',
  estimate: 'estimate',
  in_progress: 'in_progress',
  invoice: 'invoice',
  complete: 'complete',
} as const;

export type ServiceRequestStep = {
  id: ServiceRequestStepId;
  isAvailable: boolean;
};

export type ServiceRequestResponse = {
  ro: ServiceRequestResponseRo;
  customer: ServiceRequestResponseCustomer;
  vehicle: ServiceRequestResponseVehicle;
  shop: ServiceRequestResponseShop;
  appointment: ServiceRequestResponseAppointment;
  dvi: ServiceRequestResponseDvi;
  invoice: ServiceRequestResponseInvoice;
  steps: ServiceRequestStep[];
  activeStepId: ServiceRequestResponseActiveStepId;
};

export type EmptyResponse = unknown | null;

export type SubmitContactRequest = {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name: string;
  /** @maxLength 254 */
  email: string;
  /** @maxLength 120 */
  shopName?: string;
  /** @maxLength 40 */
  phone?: string;
  /**
   * @minLength 1
   * @maxLength 5000
   */
  message: string;
  /** @minLength 1 */
  recaptchaToken: string;
};

export type VerifyRecaptchaResponse = {
  /** Whether the reCAPTCHA verification passed */
  success: boolean;
};

export type VerifyRecaptchaRequest = {
  /**
   * reCAPTCHA response token from the client
   * @minLength 1
   */
  token: string;
  /** Expected reCAPTCHA action (defaults to LOGIN) */
  action?: string;
};

export type SmsPollResult = {
  processed: number;
};

export type SmsScheduleRemindersResult = {
  enqueued: number;
  skipped: number;
};

export type SendSmsNotificationRequest = {
  notificationId: string;
};

export type PollRegistrationsResult = {
  polled: number;
  advanced: number;
  errors: number;
};

export type QuickBooksPollResult = {
  /** Rows claimed by this run */
  claimed: number;
  completed: number;
  retried: number;
  /** Rows deferred 60s on a QuickBooks 429 (no attempt consumed) */
  deferred: number;
  /** Rows released untouched because the connection needs re-auth */
  parked: number;
  /** Rows parked as blocked on missing org configuration (not retried, not failures) */
  blocked: number;
  /** Rows that reached terminal failed status this run */
  failed: number;
};

/**
 * Type of repair shop
 */
export type CreateShopRequestShopType = (typeof CreateShopRequestShopType)[keyof typeof CreateShopRequestShopType];

export const CreateShopRequestShopType = {
  auto_repair_shop: 'auto_repair_shop',
  tire_shop: 'tire_shop',
  transmission_shop: 'transmission_shop',
  brake_shop: 'brake_shop',
  oil_change_shop: 'oil_change_shop',
  body_shop: 'body_shop',
  specialty_shop: 'specialty_shop',
  automotive_repair: 'automotive_repair',
  motorcycle_repair: 'motorcycle_repair',
  truck_repair: 'truck_repair',
  fleet_maintenance: 'fleet_maintenance',
  exhaust_shop: 'exhaust_shop',
  auto_body: 'auto_body',
  towing_service: 'towing_service',
  mobile_mechanic: 'mobile_mechanic',
  dealership_service: 'dealership_service',
  other: 'other',
} as const;

export type ShopAddressInput = {
  /** @minLength 1 */
  address1: string;
  address2?: string;
  /** @minLength 1 */
  city: string;
  /**
   * @minLength 2
   * @maxLength 2
   */
  state: string;
  /** @minLength 5 */
  zipCode: string;
};

export type CreateShopRequest = {
  /** @minLength 1 */
  name: string;
  /** Type of repair shop */
  shopType: CreateShopRequestShopType;
  /** @minLength 1 */
  phone: string;
  phoneExtension?: string;
  address: ShopAddressInput;
  email?: string;
  website?: string;
  /** Business registration or license number */
  businessRegistrationNumber?: string;
};

export type ActiveTask = {
  laborId: string;
  jobName: string | null;
  repairOrderNumber: string | null;
  customerName: string | null;
} | null;

/**
 * Caller’s effective org-level role. Null for shop-level callers (e.g. technicians) with no organization_members row.
 */
export type OrgRole = (typeof OrgRole)[keyof typeof OrgRole] | null;

export const OrgRole = {
  org_owner: 'org_owner',
  org_admin: 'org_admin',
} as const;

export type OrganizationContextOrNull = {
  id: string;
  name: string;
  plan: string;
  ownerUserId: string;
  orgRole: OrgRole | null;
  /** @minimum 0 */
  shopCount: number;
  /** @minimum 0 */
  memberCount: number;
} | null;

export type ChangePasswordRequest = { [key: string]: unknown };

export type ChangePasswordConfirmRequest = {
  /**
   * Verification code from email
   * @minLength 1
   */
  nonce: string;
  /**
   * New password (min 8 chars, must include at least one uppercase letter, one lowercase letter, one number, and one special character)
   * @minLength 8
   * @maxLength 128
   * @pattern [a-z]
   */
  newPassword: string;
};

export type ImpersonationEndResponse = {
  ok: true;
  /** The jti from the impersonation token that ended the session. */
  jti: string;
  /**
   * How long the impersonation session lasted, in whole seconds.
   * @minimum 0
   */
  durationSeconds: number;
};

/**
 * User role
 */
export type UserRoleProperty = (typeof UserRoleProperty)[keyof typeof UserRoleProperty];

export const UserRoleProperty = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type User = {
  id: string;
  avatarUrl: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** User role */
  role: UserRoleProperty;
  /** Technician color (only for technician role) */
  color?: string | null;
  /** Whether the technician is active (only for technician role) */
  isActive?: boolean | null;
};

export type UserWithPermissions = User & {
  permissions: PermissionSet & unknown;
};

export type UserListResponse = UserWithPermissions[];

export type UserDetailResponse = UserWithPermissions & {
  /** Last login timestamp */
  lastActiveAt: string | null;
};

export type UpdateUserPermissions = {
  permissions: PermissionSet & unknown;
};

/**
 * New role to assign to the user
 */
export type ChangeRoleRole = (typeof ChangeRoleRole)[keyof typeof ChangeRoleRole];

export const ChangeRoleRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type ChangeRole = {
  /** New role to assign to the user */
  role: ChangeRoleRole;
};

export type UpdateTechnicianFields = {
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  color?: string;
  isActive?: boolean;
};

/**
 * Parameters for the message template
 */
export type AuditLogEntryMessageParams = { [key: string]: unknown | null } | null;

export type AuditLogEntry = {
  /** Audit log entry ID */
  id: string;
  /** Type of entity affected */
  entityType: string;
  /** ID of the entity affected */
  entityId: string;
  /** i18n message key for the action */
  messageKey: string;
  /** Parameters for the message template */
  messageParams: AuditLogEntryMessageParams;
  /** Timestamp of the action */
  createdAt: string;
};

export type AuditLogResponse = {
  /** List of items for the current page */
  items: AuditLogEntry[];
  /** Total number of items across all pages */
  total: number;
};

export type LoginHistoryEntry = {
  /** Login history entry ID */
  id: string;
  /** IP address of the login */
  ipAddress: string | null;
  /** User agent of the login */
  userAgent: string | null;
  /** Timestamp of the login */
  createdAt: string;
};

export type LoginHistoryResponse = {
  /** List of items for the current page */
  items: LoginHistoryEntry[];
  /** Total number of items across all pages */
  total: number;
};

export type UpdateEmail = {
  /** New email address for the user */
  email: string;
};

export type ShopMessageResponse = {
  message: string;
};

/**
 * Type of repair shop
 */
export type UpdateShopRequestShopType = (typeof UpdateShopRequestShopType)[keyof typeof UpdateShopRequestShopType];

export const UpdateShopRequestShopType = {
  auto_repair_shop: 'auto_repair_shop',
  tire_shop: 'tire_shop',
  transmission_shop: 'transmission_shop',
  brake_shop: 'brake_shop',
  oil_change_shop: 'oil_change_shop',
  body_shop: 'body_shop',
  specialty_shop: 'specialty_shop',
  automotive_repair: 'automotive_repair',
  motorcycle_repair: 'motorcycle_repair',
  truck_repair: 'truck_repair',
  fleet_maintenance: 'fleet_maintenance',
  exhaust_shop: 'exhaust_shop',
  auto_body: 'auto_body',
  towing_service: 'towing_service',
  mobile_mechanic: 'mobile_mechanic',
  dealership_service: 'dealership_service',
  other: 'other',
} as const;

export type UpdateShopRequest = {
  /** @minLength 1 */
  name?: string;
  /** Type of repair shop */
  shopType?: UpdateShopRequestShopType;
  /** @minLength 1 */
  phone?: string;
  phoneExtension?: string;
  address?: ShopAddressInput;
  email?: string;
  website?: string;
  /** Business registration or license number */
  businessRegistrationNumber?: string;
  /** URL of the shop logo */
  logoUrl?: string | null;
};

export type UploadLogoResponse = {
  logoUrl: string;
};

export type OrganizationContext = {
  id: string;
  name: string;
  plan: string;
  ownerUserId: string;
  orgRole: OrgRole | null;
  /** @minimum 0 */
  shopCount: number;
  /** @minimum 0 */
  memberCount: number;
};

export type UpdateOrganizationBody = {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name: string;
};

export type TransferOwnershipBody = {
  newOwnerUserId: string;
};

export type OrganizationShopsList = Shop[];

export type TierUpgradeRequiredErrorCode =
  (typeof TierUpgradeRequiredErrorCode)[keyof typeof TierUpgradeRequiredErrorCode];

export const TierUpgradeRequiredErrorCode = {
  tier_upgrade_required: 'tier_upgrade_required',
} as const;

export type TierUpgradeRequiredError = ApiError & {
  code: TierUpgradeRequiredErrorCode;
  requiredTier: string;
};

export type CreateOrganizationShopBody = CreateShopRequest & {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name?: string;
};

export type OrgUserRole = (typeof OrgUserRole)[keyof typeof OrgUserRole] | null;

export const OrgUserRole = {
  org_owner: 'org_owner',
  org_admin: 'org_admin',
} as const;

export type OrgUserListItem = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  orgRole: OrgUserRole | null;
  /** @minimum 0 */
  shopCount: number;
};

export type OrgUserList = OrgUserListItem[];

/**
 * User role
 */
export type ShopUserListItemRole = (typeof ShopUserListItemRole)[keyof typeof ShopUserListItemRole];

export const ShopUserListItemRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type ShopUserListItem = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  /** User role */
  role: ShopUserListItemRole;
  orgRole: OrgUserRole | null;
};

export type ShopUserList = ShopUserListItem[];

export type OrgUserDetailUser = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
};

/**
 * User role
 */
export type OrgUserDetailShopRolesItemRole =
  | (typeof OrgUserDetailShopRolesItemRole)[keyof typeof OrgUserDetailShopRolesItemRole]
  | null;

export const OrgUserDetailShopRolesItemRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type OrgUserDetailShopRolesItem = {
  shopId: string;
  shopName: string;
  /** User role */
  role: OrgUserDetailShopRolesItemRole;
};

export type OrgUserDetail = {
  user: OrgUserDetailUser;
  orgRole: OrgUserRole | null;
  shopRoles: OrgUserDetailShopRolesItem[];
};

export type UpdateOrgUserBodyOrgRole = (typeof UpdateOrgUserBodyOrgRole)[keyof typeof UpdateOrgUserBodyOrgRole] | null;

export const UpdateOrgUserBodyOrgRole = {
  org_admin: 'org_admin',
} as const;

export type UpdateOrgUserBody = {
  orgRole: UpdateOrgUserBodyOrgRole;
};

/**
 * User role
 */
export type UpdateUserShopRoleBodyRole =
  | (typeof UpdateUserShopRoleBodyRole)[keyof typeof UpdateUserShopRoleBodyRole]
  | null;

export const UpdateUserShopRoleBodyRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type UpdateUserShopRoleBody = {
  /** User role */
  role: UpdateUserShopRoleBodyRole;
};

/**
 * Subscription tier
 */
export type SubscriptionPlanTier = (typeof SubscriptionPlanTier)[keyof typeof SubscriptionPlanTier];

export const SubscriptionPlanTier = {
  free: 'free',
  pro: 'pro',
  elite: 'elite',
} as const;

export type SubscriptionPlanMonthly = {
  stripePriceId: string;
  amount: number;
};

export type SubscriptionPlanAnnual = {
  stripePriceId: string;
  amount: number;
};

export type SubscriptionPlanMetered = {
  stripePriceId: string;
  unitAmount: number;
};

export type SubscriptionPlan = {
  /** Subscription tier */
  tier: SubscriptionPlanTier;
  name: string;
  currency: string;
  stripeProductId: string;
  monthly?: SubscriptionPlanMonthly;
  annual?: SubscriptionPlanAnnual;
  metered?: SubscriptionPlanMetered;
};

export type GetPlansResponse = {
  plans: SubscriptionPlan[];
};

export type CreateCheckoutResponse = {
  checkoutUrl: string;
};

/**
 * Tier to subscribe to
 */
export type CreateCheckoutRequestTier = (typeof CreateCheckoutRequestTier)[keyof typeof CreateCheckoutRequestTier];

export const CreateCheckoutRequestTier = {
  pro: 'pro',
  elite: 'elite',
} as const;

/**
 * Billing interval
 */
export type CreateCheckoutRequestInterval =
  (typeof CreateCheckoutRequestInterval)[keyof typeof CreateCheckoutRequestInterval];

export const CreateCheckoutRequestInterval = {
  month: 'month',
  year: 'year',
} as const;

export type CreateCheckoutRequest = {
  /** Tier to subscribe to */
  tier: CreateCheckoutRequestTier;
  /** Billing interval */
  interval: CreateCheckoutRequestInterval;
};

/**
 * Subscription tier
 */
export type VerifyCheckoutResponseTier = (typeof VerifyCheckoutResponseTier)[keyof typeof VerifyCheckoutResponseTier];

export const VerifyCheckoutResponseTier = {
  free: 'free',
  pro: 'pro',
  elite: 'elite',
} as const;

export type VerifyCheckoutResponse = {
  /** Subscription tier */
  tier: VerifyCheckoutResponseTier;
  subscribed: boolean;
};

export type VerifyCheckoutRequest = {
  /** Stripe checkout session ID */
  sessionId: string;
};

export type CreatePortalResponse = {
  portalUrl: string;
};

/**
 * Optional Stripe Portal flow configuration
 */
export type CreatePortalRequestFlow = (typeof CreatePortalRequestFlow)[keyof typeof CreatePortalRequestFlow];

export const CreatePortalRequestFlow = {
  payment_method_update: 'payment_method_update',
  subscription_cancel: 'subscription_cancel',
} as const;

export type CreatePortalRequest = {
  /** Optional Stripe Portal flow configuration */
  flow?: CreatePortalRequestFlow;
};

export type ReconcileFreeResponse = {
  stripeCustomerId: string | null;
};

export type GetFreeUsageResponsePeriodsItem = {
  periodStart: string;
  periodEnd: string;
  roCount: number;
  amountBilledCents: number;
  stripeInvoiceId: string | null;
};

export type GetFreeUsageResponse = {
  allowance: number;
  used: number;
  remaining: number | null;
  unlimited: boolean;
  paymentMethodOnFile: boolean;
  periods: GetFreeUsageResponsePeriodsItem[];
};

export type SubscriptionInvoice = {
  id: string;
  number: string | null;
  createdAt: string;
  description: string;
  amountCents: number;
  currency: string;
  status: string;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
};

export type GetSubscriptionInvoicesResponse = {
  invoices: SubscriptionInvoice[];
};

export type UpgradeSubscriptionResponse = {
  subscriptionId?: string;
  checkoutUrl?: string;
};

/**
 * Target tier to upgrade to
 */
export type UpgradeSubscriptionRequestTargetTier =
  (typeof UpgradeSubscriptionRequestTargetTier)[keyof typeof UpgradeSubscriptionRequestTargetTier];

export const UpgradeSubscriptionRequestTargetTier = {
  pro: 'pro',
  elite: 'elite',
} as const;

/**
 * Target billing interval
 */
export type UpgradeSubscriptionRequestInterval =
  (typeof UpgradeSubscriptionRequestInterval)[keyof typeof UpgradeSubscriptionRequestInterval];

export const UpgradeSubscriptionRequestInterval = {
  month: 'month',
  year: 'year',
} as const;

export type UpgradeSubscriptionRequest = {
  /** Target tier to upgrade to */
  targetTier: UpgradeSubscriptionRequestTargetTier;
  /** Target billing interval */
  interval: UpgradeSubscriptionRequestInterval;
};

export type StripeConnectStatus = {
  hasAccount: boolean;
  onboardingComplete: boolean;
  accountStatus: string | null;
};

export type StripeConnectOnboardingResponse = {
  url: string;
};

export type StartStripeConnectOnboardingRequest = {
  /** URL to redirect after onboarding */
  returnUrl: string;
  /** URL to redirect if link expires */
  refreshUrl: string;
};

export type StripeDashboardLinkResponse = {
  url: string;
};

export type CarfaxExportTriggerRunResponse = {
  runId: string;
  fileName: string;
  s3Key: string;
  /** @minimum 0 */
  roCount: number;
  /** @minimum 0 */
  lineCount: number;
};

export type CarfaxExportTriggerRunBodyFileType =
  (typeof CarfaxExportTriggerRunBodyFileType)[keyof typeof CarfaxExportTriggerRunBodyFileType];

export const CarfaxExportTriggerRunBodyFileType = {
  HIST: 'HIST',
  PROD: 'PROD',
} as const;

export type CarfaxExportTriggerRunBody = {
  fileType?: CarfaxExportTriggerRunBodyFileType;
  /**
   * YYYY-MM-DD; defaults to today UTC
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  asOfDate?: string;
};

export type CarfaxExportRunFileType = (typeof CarfaxExportRunFileType)[keyof typeof CarfaxExportRunFileType];

export const CarfaxExportRunFileType = {
  HIST: 'HIST',
  PROD: 'PROD',
} as const;

export type CarfaxExportRunStatus = (typeof CarfaxExportRunStatus)[keyof typeof CarfaxExportRunStatus];

export const CarfaxExportRunStatus = {
  pending: 'pending',
  succeeded: 'succeeded',
  failed: 'failed',
} as const;

export type CarfaxExportRun = {
  id: string;
  fileType: CarfaxExportRunFileType;
  fileName: string;
  s3Key: string | null;
  status: CarfaxExportRunStatus;
  roCount: number;
  lineCount: number;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
  ftpUploadedAt: string | null;
};

export type CarfaxExportRunListResponse = {
  runs: CarfaxExportRun[];
};

export type SmsNotification = {
  id: string;
  shopId: string;
  customerId: string;
  appointmentId: string | null;
  notificationType: string;
  phoneE164: string;
  messageBody: string;
  sendingPath: string;
  fromNumber: string;
  status: string;
  scheduledFor: string;
  sentAt: string | null;
  twilioMessageSid: string | null;
  failureReason: string | null;
  retryCount: number;
  cancelledAt: string | null;
  cancelledByUserId: string | null;
  createdAt: string;
};

/**
 * Payment terms for invoices
 */
export type CustomerPaymentTerms = (typeof CustomerPaymentTerms)[keyof typeof CustomerPaymentTerms];

export const CustomerPaymentTerms = {
  net_15: 'net_15',
  net_30: 'net_30',
  net_60: 'net_60',
  due_on_receipt: 'due_on_receipt',
} as const;

/**
 * Type of customer
 */
export type CustomerCustomerType = (typeof CustomerCustomerType)[keyof typeof CustomerCustomerType];

export const CustomerCustomerType = {
  individual: 'individual',
  business: 'business',
} as const;

export type Email = {
  id: string;
  email: string;
  isPrimary: boolean;
};

/**
 * Phone number type
 */
export type PhoneType = (typeof PhoneType)[keyof typeof PhoneType];

export const PhoneType = {
  mobile: 'mobile',
  home: 'home',
  work: 'work',
  other: 'other',
} as const;

export type Phone = {
  id: string;
  phoneNumber: string;
  isPrimary: boolean;
  /** Phone number type */
  type?: PhoneType;
};

export type AddressResponse = {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
};

/**
 * Discount type
 */
export type DiscountType = (typeof DiscountType)[keyof typeof DiscountType];

export const DiscountType = {
  percentage: 'percentage',
  amount: 'amount',
} as const;

/**
 * What the discount applies to
 */
export type DiscountScope = (typeof DiscountScope)[keyof typeof DiscountScope];

export const DiscountScope = {
  parts: 'parts',
  labor: 'labor',
  both: 'both',
} as const;

export type Discount = {
  isEnabled?: boolean;
  /** Discount type */
  type?: DiscountType;
  value?: number | null;
  /** What the discount applies to */
  scope?: DiscountScope;
};

/**
 * Latest SMS consent status for service messages
 */
export type CustomerSmsConsentStatus = (typeof CustomerSmsConsentStatus)[keyof typeof CustomerSmsConsentStatus] | null;

export const CustomerSmsConsentStatus = {
  opted_in: 'opted_in',
  opted_out: 'opted_out',
} as const;

export type Customer = {
  id: string;
  firstName: string;
  lastName: string;
  businessName?: string;
  taxId?: string;
  /** Payment terms for invoices */
  paymentTerms: CustomerPaymentTerms;
  /** Type of customer */
  customerType: CustomerCustomerType;
  emails: Email[];
  phones: Phone[];
  notes?: string;
  address: AddressResponse;
  isTaxable: boolean;
  isMarketable: boolean;
  discount: Discount;
  /** Latest SMS consent status for service messages */
  smsConsentStatus?: CustomerSmsConsentStatus;
  /** Timestamp of the latest SMS consent event */
  smsConsentDate?: string | null;
};

export type CustomerListResponse = {
  items: Customer[];
  /** Total number of customers */
  total: number;
};

/**
 * Type of customer
 */
export type CreateCustomerRequestCustomerType =
  (typeof CreateCustomerRequestCustomerType)[keyof typeof CreateCustomerRequestCustomerType];

export const CreateCustomerRequestCustomerType = {
  individual: 'individual',
  business: 'business',
} as const;

/**
 * Payment terms for invoices
 */
export type CreateCustomerRequestPaymentTerms =
  (typeof CreateCustomerRequestPaymentTerms)[keyof typeof CreateCustomerRequestPaymentTerms];

export const CreateCustomerRequestPaymentTerms = {
  net_15: 'net_15',
  net_30: 'net_30',
  net_60: 'net_60',
  due_on_receipt: 'due_on_receipt',
} as const;

/**
 * Phone number type
 */
export type PhoneInputType = (typeof PhoneInputType)[keyof typeof PhoneInputType];

export const PhoneInputType = {
  mobile: 'mobile',
  home: 'home',
  work: 'work',
  other: 'other',
} as const;

export type PhoneInput = {
  /**
   * @minLength 1
   * @pattern ^\([0-9]{3}\)\s[0-9]{3}-[0-9]{4}$
   */
  phone: string;
  isPrimary?: boolean;
  /** Phone number type */
  type?: PhoneInputType;
};

export type EmailInput = {
  email: string;
  isPrimary?: boolean;
};

export type Address = {
  /** @minLength 1 */
  address1: string;
  address2?: string;
  /** @minLength 1 */
  city: string;
  /**
   * 2-letter state code
   * @minLength 2
   * @maxLength 2
   */
  state: string;
  /** @minLength 5 */
  zipCode: string;
  /**
   * @minLength 2
   * @maxLength 2
   */
  country?: string;
};

export type CreateCustomerRequest = {
  /** Type of customer */
  customerType?: CreateCustomerRequestCustomerType;
  /** @minLength 1 */
  firstName: string;
  lastName?: string;
  businessName?: string;
  taxId?: string;
  /** Payment terms for invoices */
  paymentTerms?: CreateCustomerRequestPaymentTerms;
  phones?: PhoneInput[];
  emails?: EmailInput[];
  address?: Address;
  notes?: string;
  isMarketable?: boolean;
  isTaxable?: boolean;
  discount: Discount;
};

export type CreateCustomerWithForceRequest = CreateCustomerRequest & {
  /** Skip duplicate check and force customer creation */
  force?: boolean;
};

export type CreateCustomerFromPhoneRequest = {
  /**
   * @minLength 1
   * @pattern ^\([0-9]{3}\)\s[0-9]{3}-[0-9]{4}$
   */
  phone: string;
};

/**
 * New SMS consent status
 */
export type SmsConsentStatus = (typeof SmsConsentStatus)[keyof typeof SmsConsentStatus];

export const SmsConsentStatus = {
  opted_in: 'opted_in',
  opted_out: 'opted_out',
} as const;

export type UpdateSmsConsentRequest = {
  consentStatus: SmsConsentStatus;
};

/**
 * Consent source
 */
export type RecordVerbalSmsConsentRequestConsentSource =
  (typeof RecordVerbalSmsConsentRequestConsentSource)[keyof typeof RecordVerbalSmsConsentRequestConsentSource];

export const RecordVerbalSmsConsentRequestConsentSource = {
  verbal: 'verbal',
} as const;

export type RecordVerbalSmsConsentRequest = {
  /**
   * Phone number in E.164 format
   * @pattern ^\+[1-9]\d{1,14}$
   */
  phoneE164: string;
  /** Consent source */
  consentSource: RecordVerbalSmsConsentRequestConsentSource;
};

export type CustomerWithStats = Customer & {
  /** Total amount the shop has received from this customer (sum of successful and partially-refunded payments, net of refunds). */
  totalSpent: number;
};

/**
 * Type of customer
 */
export type UpdateCustomerRequestCustomerType =
  (typeof UpdateCustomerRequestCustomerType)[keyof typeof UpdateCustomerRequestCustomerType];

export const UpdateCustomerRequestCustomerType = {
  individual: 'individual',
  business: 'business',
} as const;

/**
 * Payment terms for invoices
 */
export type UpdateCustomerRequestPaymentTerms =
  (typeof UpdateCustomerRequestPaymentTerms)[keyof typeof UpdateCustomerRequestPaymentTerms];

export const UpdateCustomerRequestPaymentTerms = {
  net_15: 'net_15',
  net_30: 'net_30',
  net_60: 'net_60',
  due_on_receipt: 'due_on_receipt',
} as const;

export type UpdateCustomerRequest = {
  /** Type of customer */
  customerType?: UpdateCustomerRequestCustomerType;
  /** @minLength 1 */
  firstName?: string;
  lastName?: string;
  businessName?: string;
  taxId?: string;
  /** Payment terms for invoices */
  paymentTerms?: UpdateCustomerRequestPaymentTerms;
  phones?: PhoneInput[];
  emails?: EmailInput[];
  address?: Address;
  notes?: string;
  isMarketable?: boolean;
  isTaxable?: boolean;
  discount?: Discount;
};

export type ExportCustomersResponse = {
  /** Signed URL to download the CSV file */
  signedUrl: string;
  /** Number of customers exported */
  count: number;
};

export type UploadCustomerFileResponseSampleRowsItem = { [key: string]: string };

export type UploadCustomerFileResponseSuggestedMappingMappings = { [key: string]: string };

export type UploadCustomerFileResponseSuggestedMappingConfidence = { [key: string]: number };

export type UploadCustomerFileResponseSuggestedMappingReasoning = { [key: string]: string };

export type UploadCustomerFileResponseSuggestedMapping = {
  mappings: UploadCustomerFileResponseSuggestedMappingMappings;
  confidence: UploadCustomerFileResponseSuggestedMappingConfidence;
  reasoning: UploadCustomerFileResponseSuggestedMappingReasoning;
};

export type UploadCustomerFileResponse = {
  jobId: string;
  fileName: string;
  totalRows: number;
  headers: string[];
  sampleRows: UploadCustomerFileResponseSampleRowsItem[];
  suggestedMapping: UploadCustomerFileResponseSuggestedMapping;
};

export type ImportCustomersAcceptedResponse = {
  jobId: string;
};

/**
 * Field mapping from CSV columns to customer fields
 */
export type ImportCustomersRequestFieldMapping = { [key: string]: string };

export type ImportCustomersRequest = {
  /** Import job ID */
  jobId: string;
  /** Field mapping from CSV columns to customer fields */
  fieldMapping: ImportCustomersRequestFieldMapping;
};

export type ImportJobStateStatus = (typeof ImportJobStateStatus)[keyof typeof ImportJobStateStatus];

export const ImportJobStateStatus = {
  pending: 'pending',
  parsing: 'parsing',
  importing: 'importing',
  completed: 'completed',
  failed: 'failed',
} as const;

export type ImportJobStateErrorLogItem = {
  row?: number;
  error: string;
  [key: string]: unknown | null;
};

export type ImportJobState = {
  id: string;
  status: ImportJobStateStatus;
  totalContacts: number;
  importedContacts: number;
  skippedContacts: number;
  errorContacts: number;
  errorLog: ImportJobStateErrorLogItem[] | null;
  fileName: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * User role
 */
export type InvitationRole = (typeof InvitationRole)[keyof typeof InvitationRole];

export const InvitationRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type Invitation = {
  id: string;
  email: string;
  /** User role */
  role: InvitationRole;
  status: string;
  createdAt: string;
  expiresAt: string;
  shopId: string;
};

export type InvitationListResponse = Invitation[];

/**
 * User role
 */
export type CreateInvitationRequestRole =
  (typeof CreateInvitationRequestRole)[keyof typeof CreateInvitationRequestRole];

export const CreateInvitationRequestRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type CreateInvitationRequest = {
  /** Email address of the user to invite */
  email: string;
  /** User role */
  role: CreateInvitationRequestRole;
};

export type BulkInviteResponseOrgRole =
  | (typeof BulkInviteResponseOrgRole)[keyof typeof BulkInviteResponseOrgRole]
  | null;

export const BulkInviteResponseOrgRole = {
  org_admin: 'org_admin',
} as const;

export type BulkInviteResponse = {
  id: string;
  email: string;
  organizationId: string;
  orgRole: BulkInviteResponseOrgRole;
  /** @minimum 0 */
  assignmentCount: number;
};

export type BulkInviteBodyOrgRole = (typeof BulkInviteBodyOrgRole)[keyof typeof BulkInviteBodyOrgRole] | null;

export const BulkInviteBodyOrgRole = {
  org_admin: 'org_admin',
} as const;

/**
 * User role
 */
export type BulkInviteAssignmentRole = (typeof BulkInviteAssignmentRole)[keyof typeof BulkInviteAssignmentRole];

export const BulkInviteAssignmentRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type BulkInviteAssignment = {
  shopId: string;
  /** User role */
  role: BulkInviteAssignmentRole;
};

export type BulkInviteBody = {
  organizationId: string;
  email: string;
  orgRole?: BulkInviteBodyOrgRole;
  assignments?: BulkInviteAssignment[];
};

export type VehicleByVinResponse = {
  baseVehicleId: number;
  yearId: number;
  makeId: number;
  makeName: string;
  modelId: number;
  modelName: string;
  subModelId: number;
  subModelName: string;
  engineId: number;
  engineName: string;
  vehicleName: string;
  vehicleId: number;
} | null;

/**
 * What was detected in the image
 */
export type DecodePhotoResponseType = (typeof DecodePhotoResponseType)[keyof typeof DecodePhotoResponseType];

export const DecodePhotoResponseType = {
  vin: 'vin',
  plate: 'plate',
} as const;

export type DecodePhotoResponse = {
  /** What was detected in the image */
  type: DecodePhotoResponseType;
  /** Extracted VIN (if type is vin, or resolved from plate lookup) */
  vin: string | null;
  /** Extracted license plate number (if type is plate) */
  plate: string | null;
  /** Extracted US state code (if type is plate) */
  plateState: string | null;
  /** AI confidence score (0-1) */
  confidence: number;
  vehicle: (VehicleByVinResponse | null) & unknown;
};

export type DecodePhotoRequest = {
  /**
   * Base64-encoded JPEG image of a VIN or license plate
   * @minLength 1
   * @maxLength 14000000
   */
  imageBase64: string;
};

export type TireSizesResponseItem = {
  id?: string;
  name?: string;
};

export type TireSizesResponse = TireSizesResponseItem[];

export type ResolveVehicleResponse = {
  /** PartsTech vehicle ID */
  vehicleId: number;
  /** PartsTech/ACES base vehicle ID */
  baseVehicleId: number;
};

export type VehicleListItem = {
  id: string;
  customerId: string | null;
  year: number;
  make: string;
  model: string;
  submodel: string | null;
  /** Computed engine description, e.g. '2.0L 4-Cyl Turbo' */
  engine: string | null;
  vin: string | null;
  licensePlate: string | null;
  state: string | null;
  color: string | null;
  mileage: number | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
};

export type VehicleSearchItem = VehicleListItem & {
  /** Customer first name */
  customerFirstName: string | null;
  /** Customer last name */
  customerLastName: string | null;
};

export type VehicleListResponse = VehicleSearchItem[];

export type VehicleWithDerivedImageStatus =
  (typeof VehicleWithDerivedImageStatus)[keyof typeof VehicleWithDerivedImageStatus];

export const VehicleWithDerivedImageStatus = {
  pending: 'pending',
  generating: 'generating',
  ready: 'ready',
  failed: 'failed',
} as const;

export type Vehicle = {
  id: string;
  customerId: string | null;
  year: number;
  make: string;
  model: string;
  submodel: string | null;
  vin: string | null;
  licensePlate: string | null;
  state: string | null;
  engineCylinders: number | null;
  engineDisplacementLiters: number | null;
  engineBlockType: string | null;
  aspiration: string | null;
  fuelType: string | null;
  bodyType: string | null;
  driveType: string | null;
  doorCount: number | null;
  manufacturer: string | null;
  color: string | null;
  mileage: number | null;
  lastMileageUpdate: string | null;
  partsTechYearId: string | null;
  partsTechMakeId: string | null;
  partsTechModelId: string | null;
  partsTechSubmodelId: string | null;
  partsTechEngineId: string | null;
  /** PartsTech/ACES vehicle ID */
  acesVehicleId: string | null;
  /** PartsTech/ACES base vehicle ID */
  acesBaseVehicle: string | null;
  tireSize: string | null;
  rearTireSize: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
};

export type VehicleWithDerived = Vehicle & {
  /** Computed engine description, e.g. '2.0L 4-Cyl Turbo' */
  engine: string | null;
  hasReferences: boolean;
  imageId: string | null;
  imageStatus: VehicleWithDerivedImageStatus;
  /** CDN URL of the generated vehicle image, if ready */
  imageUrl: string | null;
};

export type CreateVehicleRequest = {
  /** Customer to attach this vehicle to */
  customerId: string;
  /**
   * @minimum 1900
   * @maximum 2100
   */
  year: number;
  /** @minLength 1 */
  make: string;
  /** @minLength 1 */
  model: string;
  submodel?: string;
  /**
   * @minLength 11
   * @maxLength 17
   */
  vin?: string;
  /** @maxLength 50 */
  vinPattern?: string;
  licensePlate?: string;
  /**
   * @minLength 2
   * @maxLength 2
   */
  state?: string;
  engineCylinders?: number;
  engineDisplacementLiters?: number;
  engineBlockType?: string;
  aspiration?: string;
  fuelType?: string;
  bodyType?: string;
  driveType?: string;
  color?: string;
  /** @minimum 0 */
  mileage?: number;
  partsTechYearId?: string;
  partsTechMakeId?: string;
  partsTechModelId?: string;
  partsTechSubmodelId?: string;
  partsTechEngineId?: string;
  /** PartsTech/ACES vehicle ID */
  acesVehicleId?: string;
  /** PartsTech/ACES base vehicle ID */
  acesBaseVehicle?: string;
};

export type UpdateVehicleRequest = {
  /**
   * @minimum 1900
   * @maximum 2100
   */
  year?: number;
  make?: string;
  model?: string;
  submodel?: string;
  /**
   * @minLength 11
   * @maxLength 17
   */
  vin?: string;
  /** @maxLength 50 */
  vinPattern?: string;
  licensePlate?: string;
  /**
   * @minLength 2
   * @maxLength 2
   */
  state?: string;
  engineCylinders?: number;
  engineDisplacementLiters?: number;
  engineBlockType?: string;
  aspiration?: string;
  fuelType?: string;
  bodyType?: string;
  driveType?: string;
  color?: string;
  /** @minimum 0 */
  mileage?: number;
  partsTechYearId?: string;
  partsTechMakeId?: string;
  partsTechModelId?: string;
  partsTechSubmodelId?: string;
  partsTechEngineId?: string;
  /** PartsTech/ACES vehicle ID */
  acesVehicleId?: string;
  /** PartsTech/ACES base vehicle ID */
  acesBaseVehicle?: string;
  /** Front or only tire size, e.g. 215/55R16 */
  tireSize?: string;
  /** Rear tire size for staggered setups */
  rearTireSize?: string | null;
};

export type PartsTechConnectionResponse = {
  /** Connection record ID */
  id: string;
  /** Shop ID */
  shopId: string;
  /** PartsTech username */
  username: string;
  /** Whether the connection is active */
  isConnected: boolean | null;
  /** Connection status */
  connectionStatus: string | null;
  /** When the connection was created */
  createdAt: string;
  /** When the connection was last updated */
  updatedAt: string;
};

/**
 * When set, marks this connection as having originated from a TorqueTitan in-app sign-up flow. Used for partner attribution.
 */
export type PartsTechConnectRequestSignupSource =
  | (typeof PartsTechConnectRequestSignupSource)[keyof typeof PartsTechConnectRequestSignupSource]
  | null;

export const PartsTechConnectRequestSignupSource = {
  torquetitan_app: 'torquetitan_app',
} as const;

export type PartsTechConnectRequest = {
  /** PartsTech username/ID for the shop */
  username: string;
  /** PartsTech API key for the shop */
  apiKey: string;
  /** When set, marks this connection as having originated from a TorqueTitan in-app sign-up flow. Used for partner attribution. */
  signupSource?: PartsTechConnectRequestSignupSource;
};

export type PartsTechConnectionStatusResponse = {
  /** Whether the shop has an active PartsTech connection */
  isConnected: boolean;
  /** PartsTech username for the shop, or null if not connected */
  username: string | null;
};

export type PartsTechAutoConnectResponse = {
  /** URL to redirect user to for PartsTech registration/connection */
  registrationUrl: string;
  /** Unique identifier for the user in the partner system */
  smsUserId: string;
};

/**
 * Shop type
 */
export type PartsTechAutoConnectShopType =
  | (typeof PartsTechAutoConnectShopType)[keyof typeof PartsTechAutoConnectShopType]
  | null;

export const PartsTechAutoConnectShopType = {
  GENERAL_REPAIR: 'GENERAL_REPAIR',
  TIRE: 'TIRE',
  QUICK_LUBE: 'QUICK_LUBE',
  HEAVY_DUTY: 'HEAVY_DUTY',
  MOBILE_MECHANIC: 'MOBILE_MECHANIC',
  NEW_USED_VEHICLE_DEALER: 'NEW_USED_VEHICLE_DEALER',
  FLEET_REPAIR: 'FLEET_REPAIR',
  PARTS_SUPPLIER: 'PARTS_SUPPLIER',
  OTHER: 'OTHER',
} as const;

export type PartsTechAutoConnectAddress = {
  /** Street address line 1 */
  address1?: string | null;
  /** Street address line 2 */
  address2?: string | null;
  /** City */
  city?: string | null;
  /** State (ISO 3166-2) */
  state?: string | null;
  /** ZIP code */
  zipCode?: string | null;
  /** Country (ISO 3166-1 alpha-2) */
  country?: string | null;
} | null;

export type PartsTechAutoConnectShop = {
  /** Shop name */
  name?: string | null;
  /** Shop type */
  type?: PartsTechAutoConnectShopType;
  /** Shop phone number */
  phone?: string | null;
  /** Shop website URL */
  website?: string | null;
  address?: PartsTechAutoConnectAddress | null;
} | null;

export type PartsTechAutoConnectRequest = {
  /** User email address */
  email: string;
  /** User first name */
  firstName?: string | null;
  /** User last name */
  lastName?: string | null;
  shop?: PartsTechAutoConnectShop | null;
};

export type PartsTechHealthResponse = {
  /** Whether the partner credentials are valid */
  connected: boolean;
  /** Status message */
  message: string;
};

export type AppointmentStatus = (typeof AppointmentStatus)[keyof typeof AppointmentStatus];

export const AppointmentStatus = {
  scheduled: 'scheduled',
  arrived: 'arrived',
  rescheduled: 'rescheduled',
  cancelled: 'cancelled',
  no_show: 'no_show',
} as const;

export type CustomerSummary = {
  firstName: string;
  lastName: string;
};

export type VehicleSummary = {
  year: number;
  make: string;
  model: string;
};

export type Appointment = {
  id: string;
  customerId: string;
  vehicleId: string;
  technicianId: string | null;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  serviceType: string | null;
  notes: string | null;
  status: AppointmentStatus;
  repairOrderId: string | null;
  createdAt: string;
  customer: CustomerSummary;
  vehicle: VehicleSummary;
};

export type ListAppointmentsResponse = Appointment[];

/**
 * Type of service
 */
export type AppointmentInputServiceType =
  (typeof AppointmentInputServiceType)[keyof typeof AppointmentInputServiceType];

export const AppointmentInputServiceType = {
  'drop-off': 'drop-off',
  wait: 'wait',
  'tow-in': 'tow-in',
} as const;

export type AppointmentInput = {
  /**
   * Appointment date
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  date: string;
  /**
   * Appointment start time
   * @pattern ^\d{2}:\d{2}$
   */
  startTime: string;
  /**
   * Appointment end time
   * @pattern ^\d{2}:\d{2}$
   */
  endTime: string;
  /** Assigned technician UUID */
  technicianId?: string | null;
  /** Type of service */
  serviceType?: AppointmentInputServiceType;
  /**
   * Reason for the appointment
   * @minLength 1
   */
  reasonForVisit: string;
  /** Additional notes */
  notes?: string;
};

export type CreateAppointmentRequest = {
  /** Existing customer UUID */
  customerId: string;
  /** Existing vehicle UUID */
  vehicleId: string;
  /** Existing repair order UUID to link to (skips auto-creation of a new repair order) */
  repairOrderId?: string;
  appointment: AppointmentInput;
};

/**
 * Type of service
 */
export type UpdateAppointmentRequestServiceType =
  (typeof UpdateAppointmentRequestServiceType)[keyof typeof UpdateAppointmentRequestServiceType];

export const UpdateAppointmentRequestServiceType = {
  'drop-off': 'drop-off',
  wait: 'wait',
  'tow-in': 'tow-in',
} as const;

/**
 * Appointment status
 */
export type UpdateAppointmentRequestStatus =
  (typeof UpdateAppointmentRequestStatus)[keyof typeof UpdateAppointmentRequestStatus];

export const UpdateAppointmentRequestStatus = {
  scheduled: 'scheduled',
  arrived: 'arrived',
  rescheduled: 'rescheduled',
  cancelled: 'cancelled',
  no_show: 'no_show',
} as const;

export type UpdateAppointmentRequest = {
  /**
   * Appointment date
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  appointmentDate?: string;
  /**
   * Appointment start time
   * @pattern ^\d{2}:\d{2}$
   */
  startTime?: string;
  /**
   * Appointment end time
   * @pattern ^\d{2}:\d{2}$
   */
  endTime?: string;
  /** Assigned technician UUID */
  technicianId?: string | null;
  /** Type of service */
  serviceType?: UpdateAppointmentRequestServiceType;
  /** Additional notes */
  notes?: string | null;
  /** Appointment status */
  status?: UpdateAppointmentRequestStatus;
};

export type AppointmentSettings = {
  /** Settings record UUID */
  id: string;
  /** Shop UUID */
  shopId: string;
  /** Default appointment duration in minutes */
  defaultDurationMinutes: number;
  /** Whether customer address is required for appointments */
  requireCustomerAddress: boolean;
  /** Whether online booking is paused */
  bookingPaused: boolean;
  /** Message shown when booking is paused */
  bookingPausedMessage: string | null;
  /** Phone number shown when booking is paused */
  bookingPausedPhone: string | null;
  /** Whether lunch break is enabled */
  lunchBreakEnabled: boolean;
  /** Lunch break start time (HH:MM) */
  lunchBreakStart: string | null;
  /** Lunch break end time (HH:MM) */
  lunchBreakEnd: string | null;
  /** Minimum lead time in minutes before an appointment can be booked */
  leadTimeMinutes: number;
  /** Maximum number of concurrent appointments */
  maxConcurrentAppointments: number;
  /** Interval between booking time slot start times in minutes */
  slotIntervalMinutes: number;
};

export type UpdateAppointmentSettingsRequest = {
  /**
   * Default appointment duration in minutes
   * @minimum 5
   * @maximum 480
   */
  defaultDurationMinutes?: number;
  /** Whether to require customer address for appointments */
  requireCustomerAddress?: boolean;
  /** Whether online booking is paused */
  bookingPaused?: boolean;
  /** Message shown when booking is paused */
  bookingPausedMessage?: string | null;
  /** Phone number shown when booking is paused */
  bookingPausedPhone?: string | null;
  /** Whether lunch break is enabled */
  lunchBreakEnabled?: boolean;
  /**
   * Lunch break start time (HH:MM)
   * @pattern ^\d{2}:\d{2}$
   */
  lunchBreakStart?: string | null;
  /**
   * Lunch break end time (HH:MM)
   * @pattern ^\d{2}:\d{2}$
   */
  lunchBreakEnd?: string | null;
  /**
   * Minimum lead time in minutes before an appointment can be booked
   * @minimum 0
   */
  leadTimeMinutes?: number;
  /**
   * Maximum number of concurrent appointments
   * @minimum 1
   */
  maxConcurrentAppointments?: number;
  /**
   * Interval between booking time slot start times in minutes
   * @minimum 5
   * @maximum 120
   */
  slotIntervalMinutes?: number;
};

export type OperatingHourInput = {
  /**
   * Day of week (0=Sunday, 6=Saturday)
   * @minimum 0
   * @maximum 6
   */
  dayOfWeek: number;
  /** Whether the shop is open on this day */
  isActive: boolean;
  /**
   * Opening time
   * @pattern ^\d{2}:\d{2}(:\d{2})?$
   */
  startTime: string;
  /**
   * Closing time
   * @pattern ^\d{2}:\d{2}(:\d{2})?$
   */
  endTime: string;
};

export type OperatingHour = OperatingHourInput & {
  /** Operating hour record UUID */
  id: string;
};

export type OperatingHoursResponse = OperatingHour[];

export type ReplaceOperatingHoursRequest = OperatingHourInput[];

export type HolidayInput = {
  /**
   * Holiday date
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  date: string;
  /**
   * Holiday name
   * @minLength 1
   */
  name: string;
  /**
   * Holiday type (e.g. full_day, partial_day)
   * @minLength 1
   */
  type: string;
  /**
   * Start time for partial-day holidays
   * @pattern ^\d{2}:\d{2}(:\d{2})?$
   */
  startTime?: string | null;
  /**
   * End time for partial-day holidays
   * @pattern ^\d{2}:\d{2}(:\d{2})?$
   */
  endTime?: string | null;
};

export type Holiday = HolidayInput & {
  /** Holiday record UUID */
  id: string;
};

export type HolidaysResponse = Holiday[];

export type ReplaceHolidaysRequest = HolidayInput[];

/**
 * How parts are displayed on estimates
 */
export type EstimateSettingsPartDisplayMode =
  (typeof EstimateSettingsPartDisplayMode)[keyof typeof EstimateSettingsPartDisplayMode];

export const EstimateSettingsPartDisplayMode = {
  full_description: 'full_description',
  part_type_only: 'part_type_only',
} as const;

/**
 * Default oil interval preset used when printing oil stickers
 */
export type EstimateSettingsDefaultOilInterval =
  (typeof EstimateSettingsDefaultOilInterval)[keyof typeof EstimateSettingsDefaultOilInterval];

export const EstimateSettingsDefaultOilInterval = {
  conventional: 'conventional',
  blend: 'blend',
  synthetic: 'synthetic',
} as const;

export type EstimateSettings = {
  /** Settings record UUID */
  id: string;
  /** Shop UUID */
  shopId: string;
  /** Number of days before an estimate expires */
  expirationDays: number;
  /** Whether disclosures are shown on estimates */
  disclosuresEnabled: boolean;
  /** Disclosures text shown on estimates */
  disclosuresText: string | null;
  /** How parts are displayed on estimates */
  partDisplayMode: EstimateSettingsPartDisplayMode;
  /** Default oil interval preset used when printing oil stickers */
  defaultOilInterval: EstimateSettingsDefaultOilInterval;
};

/**
 * How parts are displayed on estimates
 */
export type UpdateEstimateSettingsRequestPartDisplayMode =
  (typeof UpdateEstimateSettingsRequestPartDisplayMode)[keyof typeof UpdateEstimateSettingsRequestPartDisplayMode];

export const UpdateEstimateSettingsRequestPartDisplayMode = {
  full_description: 'full_description',
  part_type_only: 'part_type_only',
} as const;

/**
 * Default oil interval preset used when printing oil stickers
 */
export type UpdateEstimateSettingsRequestDefaultOilInterval =
  (typeof UpdateEstimateSettingsRequestDefaultOilInterval)[keyof typeof UpdateEstimateSettingsRequestDefaultOilInterval];

export const UpdateEstimateSettingsRequestDefaultOilInterval = {
  conventional: 'conventional',
  blend: 'blend',
  synthetic: 'synthetic',
} as const;

export type UpdateEstimateSettingsRequest = {
  /**
   * Number of days before an estimate expires
   * @minimum 1
   * @maximum 365
   */
  expirationDays?: number;
  /** Whether disclosures are shown on estimates */
  disclosuresEnabled?: boolean;
  /** Disclosures text shown on estimates */
  disclosuresText?: string | null;
  /** How parts are displayed on estimates */
  partDisplayMode?: UpdateEstimateSettingsRequestPartDisplayMode;
  /** Default oil interval preset used when printing oil stickers */
  defaultOilInterval?: UpdateEstimateSettingsRequestDefaultOilInterval;
};

export type EstimateDisclosure = {
  id: string;
  title: string;
  body: string;
  displayOrder: number;
  isDefault: boolean;
  attachedRoCount: number;
};

export type EstimateDisclosuresResponse = EstimateDisclosure[];

export type EstimateDisclosureInput = {
  /** @minLength 1 */
  title: string;
  body: string;
  isDefault?: boolean;
};

export type ReplaceEstimateDisclosuresRequest = {
  disclosures: EstimateDisclosureInput[];
};

export type InvoiceSettings = {
  /** Settings record UUID */
  id: string;
  /** Shop UUID */
  shopId: string;
  /** Default number of days until an invoice is due */
  defaultDueDays: number;
};

export type UpdateInvoiceSettingsRequest = {
  /**
   * Default number of days until an invoice is due
   * @minimum 1
   * @maximum 365
   */
  defaultDueDays: number;
};

export type TaxSettings = {
  /** Settings record UUID */
  id: string;
  /** Shop UUID */
  shopId: string;
  /** Tax rate percentage */
  taxRate: number;
  /** Whether tax applies to labor charges */
  applyToLabor: boolean;
  /** Whether tax applies to parts charges */
  applyToParts: boolean;
  /** Whether tax applies to tire charges */
  applyToTires: boolean;
};

export type UpdateTaxSettingsRequest = {
  /**
   * Tax rate percentage
   * @minimum 0
   * @maximum 100
   */
  taxRate?: number;
  /** Whether tax applies to labor charges */
  applyToLabor?: boolean;
  /** Whether tax applies to parts charges */
  applyToParts?: boolean;
  /** Whether tax applies to tire charges */
  applyToTires?: boolean;
};

export type BoardRepairOrderStage = (typeof BoardRepairOrderStage)[keyof typeof BoardRepairOrderStage];

export const BoardRepairOrderStage = {
  intake: 'intake',
  in_progress: 'in_progress',
  invoice: 'invoice',
  closed: 'closed',
} as const;

/**
 * Payment terms for invoices
 */
export type CustomerOrNullPaymentTerms = (typeof CustomerOrNullPaymentTerms)[keyof typeof CustomerOrNullPaymentTerms];

export const CustomerOrNullPaymentTerms = {
  net_15: 'net_15',
  net_30: 'net_30',
  net_60: 'net_60',
  due_on_receipt: 'due_on_receipt',
} as const;

/**
 * Type of customer
 */
export type CustomerOrNullCustomerType = (typeof CustomerOrNullCustomerType)[keyof typeof CustomerOrNullCustomerType];

export const CustomerOrNullCustomerType = {
  individual: 'individual',
  business: 'business',
} as const;

/**
 * Latest SMS consent status for service messages
 */
export type CustomerOrNullSmsConsentStatus =
  | (typeof CustomerOrNullSmsConsentStatus)[keyof typeof CustomerOrNullSmsConsentStatus]
  | null;

export const CustomerOrNullSmsConsentStatus = {
  opted_in: 'opted_in',
  opted_out: 'opted_out',
} as const;

export type CustomerOrNull = {
  id: string;
  firstName: string;
  lastName: string;
  businessName?: string;
  taxId?: string;
  /** Payment terms for invoices */
  paymentTerms: CustomerOrNullPaymentTerms;
  /** Type of customer */
  customerType: CustomerOrNullCustomerType;
  emails: Email[];
  phones: Phone[];
  notes?: string;
  address: AddressResponse;
  isTaxable: boolean;
  isMarketable: boolean;
  discount: Discount;
  /** Latest SMS consent status for service messages */
  smsConsentStatus?: CustomerOrNullSmsConsentStatus;
  /** Timestamp of the latest SMS consent event */
  smsConsentDate?: string | null;
} | null;

export type VehicleOrNull = {
  id: string;
  customerId: string | null;
  year: number;
  make: string;
  model: string;
  submodel: string | null;
  vin: string | null;
  licensePlate: string | null;
  state: string | null;
  engineCylinders: number | null;
  engineDisplacementLiters: number | null;
  engineBlockType: string | null;
  aspiration: string | null;
  fuelType: string | null;
  bodyType: string | null;
  driveType: string | null;
  doorCount: number | null;
  manufacturer: string | null;
  color: string | null;
  mileage: number | null;
  lastMileageUpdate: string | null;
  partsTechYearId: string | null;
  partsTechMakeId: string | null;
  partsTechModelId: string | null;
  partsTechSubmodelId: string | null;
  partsTechEngineId: string | null;
  /** PartsTech/ACES vehicle ID */
  acesVehicleId: string | null;
  /** PartsTech/ACES base vehicle ID */
  acesBaseVehicle: string | null;
  tireSize: string | null;
  rearTireSize: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
} | null;

export type WorkflowStage = {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string | null;
};

export type WorkflowSubStageOrNull = {
  id: string;
  stageId: string | null;
  name: string;
  displayName: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  requiresApproval: boolean;
  autoAdvance: boolean;
  createdAt: string | null;
} | null;

/**
 * Legality of moving to each candidate stage, keyed by target stage name.
 */
export type AvailableTransitionsStages = { [key: string]: WorkflowTransitionEntry };

/**
 * Legality of moving to each sub-stage of the RO's current stage, keyed by target sub-stage name.
 */
export type AvailableTransitionsSubStages = { [key: string]: WorkflowTransitionEntry };

/**
 * Per-event legality for UI-dispatched workflow events (ADR-0011), keyed by event name.
 */
export type AvailableTransitionsEvents = {
  estimate_sent?: WorkflowTransitionEntry;
  estimate_resent?: WorkflowTransitionEntry;
  customer_approved?: WorkflowTransitionEntry;
  customer_declined_all?: WorkflowTransitionEntry;
  pricing_unlocked?: WorkflowTransitionEntry;
  start_work?: WorkflowTransitionEntry;
  mark_work_complete?: WorkflowTransitionEntry;
  mark_work_complete_force?: WorkflowTransitionEntry;
  mark_on_hold?: WorkflowTransitionEntry;
  resume_from_hold?: WorkflowTransitionEntry;
  addon_sent?: WorkflowTransitionEntry;
  addon_approved?: WorkflowTransitionEntry;
  addon_declined?: WorkflowTransitionEntry;
  assign_to_vendor?: WorkflowTransitionEntry;
  sublet_completed?: WorkflowTransitionEntry;
  marked_picked_up?: WorkflowTransitionEntry;
  close_out?: WorkflowTransitionEntry;
  reopen?: WorkflowTransitionEntry;
};

export type AvailableTransitions = {
  /** Legality of moving to each candidate stage, keyed by target stage name. */
  stages: AvailableTransitionsStages;
  /** Legality of moving to each sub-stage of the RO's current stage, keyed by target sub-stage name. */
  subStages: AvailableTransitionsSubStages;
  /** Per-event legality for UI-dispatched workflow events (ADR-0011), keyed by event name. */
  events: AvailableTransitionsEvents;
};

export type RepairOrderWorkflow = {
  stage: WorkflowStage;
  subStage: WorkflowSubStageOrNull | null;
  availableTransitions: AvailableTransitions;
};

/**
 * User role
 */
export type UserOrNullRole = (typeof UserOrNullRole)[keyof typeof UserOrNullRole];

export const UserOrNullRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type UserOrNull = {
  id: string;
  avatarUrl: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  /** User role */
  role: UserOrNullRole;
  /** Technician color (only for technician role) */
  color?: string | null;
  /** Whether the technician is active (only for technician role) */
  isActive?: boolean | null;
} | null;

export type Tag = {
  id: string;
  name: string;
  color: string;
  isSystemGenerated: boolean;
  createdAt: string;
  updatedAt: string;
};

export type BoardRepairOrderNextAppointment = {
  appointmentDate: string;
  startTime: string;
  endTime: string;
} | null;

export type BoardRepairOrder = {
  id: string;
  customerId: string;
  vehicleId: string;
  stage: BoardRepairOrderStage;
  isEstimate: boolean;
  closureReason: string | null;
  totalAmount: number;
  laborCost: number;
  partsCost: number;
  createdAt: string;
  updatedAt: string;
  repairOrderNumber: string | null;
  workflowStageId: string | null;
  workflowSubStageId: string | null;
  stageEnteredAt: string | null;
  stageNotes: string | null;
  estimatedCompletionDate: string | null;
  priorityLevel: string;
  promisedPickupDate: string | null;
  nextAppointment: BoardRepairOrderNextAppointment;
  customer: CustomerOrNull | null;
  vehicle: VehicleOrNull | null;
  workflow: RepairOrderWorkflow;
  assignedTechnician: UserOrNull | null;
  assignedServiceAdvisor: UserOrNull | null;
  tags: Tag[];
};

export type BoardRepairOrderList = BoardRepairOrder[];

export type RecentRepairOrderItemRepairOrderStage =
  (typeof RecentRepairOrderItemRepairOrderStage)[keyof typeof RecentRepairOrderItemRepairOrderStage];

export const RecentRepairOrderItemRepairOrderStage = {
  intake: 'intake',
  in_progress: 'in_progress',
  invoice: 'invoice',
  closed: 'closed',
} as const;

export type RecentRepairOrderItemRepairOrderCustomer = {
  firstName: string | null;
  lastName: string | null;
} | null;

export type RecentRepairOrderItemRepairOrderVehicle = {
  year: number | null;
  make: string | null;
  model: string | null;
} | null;

export type RecentRepairOrderItemRepairOrderWorkflowSubStage = {
  id: string;
  name: string | null;
  displayName: string | null;
} | null;

export type RecentRepairOrderItemRepairOrder = {
  id: string;
  repairOrderNumber: string | null;
  stage: RecentRepairOrderItemRepairOrderStage;
  createdAt: string;
  customer: RecentRepairOrderItemRepairOrderCustomer;
  vehicle: RecentRepairOrderItemRepairOrderVehicle;
  workflowSubStage: RecentRepairOrderItemRepairOrderWorkflowSubStage;
} | null;

export type RecentRepairOrderItem = {
  repairOrderId: string;
  lastViewedAt: string;
  repairOrder: RecentRepairOrderItemRepairOrder;
};

export type RecentRepairOrdersResponse = {
  items: RecentRepairOrderItem[];
};

export type RepairOrderListItemStage = (typeof RepairOrderListItemStage)[keyof typeof RepairOrderListItemStage];

export const RepairOrderListItemStage = {
  intake: 'intake',
  in_progress: 'in_progress',
  invoice: 'invoice',
  closed: 'closed',
} as const;

/**
 * Priority level of the repair order
 */
export type RepairOrderListItemPriorityLevel =
  | (typeof RepairOrderListItemPriorityLevel)[keyof typeof RepairOrderListItemPriorityLevel]
  | null;

export const RepairOrderListItemPriorityLevel = {
  low: 'low',
  normal: 'normal',
  high: 'high',
  urgent: 'urgent',
} as const;

/**
 * How the technician is attached to the repair order
 */
export type RepairOrderTechnicianSource =
  (typeof RepairOrderTechnicianSource)[keyof typeof RepairOrderTechnicianSource];

export const RepairOrderTechnicianSource = {
  direct: 'direct',
  job_labor: 'job_labor',
} as const;

export type RepairOrderTechnician = User & {
  /** How the technician is attached to the repair order */
  source: RepairOrderTechnicianSource;
  /** When the technician was directly assigned; null for job-labor-derived technicians */
  assignedAt: string | null;
};

export type RepairOrderListItem = {
  id: string;
  repairOrderNumber: string | null;
  stage: RepairOrderListItemStage;
  isEstimate: boolean;
  closureReason: string | null;
  createdAt: string;
  updatedAt: string;
  totalAmount: number | null;
  /** Priority level of the repair order */
  priorityLevel: RepairOrderListItemPriorityLevel;
  promisedPickupDate: string | null;
  isArchived: boolean;
  customer: CustomerOrNull | null;
  vehicle: VehicleOrNull | null;
  workflow: RepairOrderWorkflow;
  serviceAdvisor: UserOrNull | null;
  technicians: RepairOrderTechnician[];
  tags: Tag[];
};

export type RepairOrderListResponse = {
  items: RepairOrderListItem[];
  /** Total number of repair orders matching filters */
  total: number;
};

export type RepairOrderDetailStage = (typeof RepairOrderDetailStage)[keyof typeof RepairOrderDetailStage];

export const RepairOrderDetailStage = {
  intake: 'intake',
  in_progress: 'in_progress',
  invoice: 'invoice',
  closed: 'closed',
} as const;

/**
 * Reason for closing the repair order
 */
export type RepairOrderDetailClosureReason =
  | (typeof RepairOrderDetailClosureReason)[keyof typeof RepairOrderDetailClosureReason]
  | null;

export const RepairOrderDetailClosureReason = {
  completed: 'completed',
  no_show: 'no_show',
  referred_out: 'referred_out',
  unable_to_diagnose: 'unable_to_diagnose',
  cancelled_by_customer: 'cancelled_by_customer',
  shop_unable_to_perform: 'shop_unable_to_perform',
  customer_declined_all: 'customer_declined_all',
  duplicate_or_error: 'duplicate_or_error',
  customer_unreachable: 'customer_unreachable',
  written_off: 'written_off',
  other: 'other',
} as const;

/**
 * Priority level of the repair order
 */
export type RepairOrderDetailPriorityLevel =
  | (typeof RepairOrderDetailPriorityLevel)[keyof typeof RepairOrderDetailPriorityLevel]
  | null;

export const RepairOrderDetailPriorityLevel = {
  low: 'low',
  normal: 'normal',
  high: 'high',
  urgent: 'urgent',
} as const;

/**
 * The send-options snapshot persisted by the most recent estimate share. Null when the estimate has never been shared. Used by the FE to mirror estimateSent and to pre-populate the share dialog.
 */
export type RepairOrderDetailEstimateSendOptions = {
  requireSignature: boolean;
  includeAiSummary: boolean;
  showInspection: boolean;
  showProgress: boolean;
} | null;

export type JobPartPricingInfoMatchedRowMarkupType =
  (typeof JobPartPricingInfoMatchedRowMarkupType)[keyof typeof JobPartPricingInfoMatchedRowMarkupType];

export const JobPartPricingInfoMatchedRowMarkupType = {
  percentage: 'percentage',
  fixed: 'fixed',
} as const;

export type JobPartPricingInfoMatchedRow = {
  minPrice: number;
  maxPrice: number;
  markupType: JobPartPricingInfoMatchedRowMarkupType;
  markupValue: number;
  multiplier: number | null;
  grossProfitMargin: number | null;
} | null;

export type JobPartPricingInfo = {
  matrixId: string;
  matrixName: string;
  matchedRow: JobPartPricingInfoMatchedRow;
} | null;

export type JobPart = {
  id: string;
  partName: string;
  partNumber: string | null;
  partTypeName: string | null;
  quantity: number;
  unitPrice: number;
  costPrice: number | null;
  brandDisplayName: string | null;
  originalData?: unknown | null;
  coreCharge: number | null;
  pricingMatrixId: string | null;
  overrideOriginalRetail: number | null;
  position: string | null;
  isTire: boolean;
  fet: number;
  brand: string | null;
  model: string | null;
  size: string | null;
  optionGroupId: string | null;
  isSelectedOption: boolean;
  pricingInfo: JobPartPricingInfo;
};

/**
 * Status of a labor task
 */
export type JobLaborStatus = (typeof JobLaborStatus)[keyof typeof JobLaborStatus];

export const JobLaborStatus = {
  not_started: 'not_started',
  in_progress: 'in_progress',
  paused: 'paused',
  on_hold: 'on_hold',
  complete: 'complete',
} as const;

export type JobLabor = {
  id: string;
  description: string;
  hours: number;
  hourlyRate: number;
  userId: string | null;
  technicianId: string | null;
  /** Status of a labor task */
  status: JobLaborStatus;
  skillLevel: string | null;
  shopLaborRateId: string | null;
};

export type JobFee = {
  id: string;
  name: string;
  amount: number;
  amountType: string;
  isTaxable: boolean;
  displayOrder: number;
  catalogId: string | null;
  perUnitBasis: string | null;
  defaultAmount: number | null;
};

export type OptionGroupPreset = (typeof OptionGroupPreset)[keyof typeof OptionGroupPreset] | null;

export const OptionGroupPreset = {
  gbb: 'gbb',
  numbered: 'numbered',
} as const;

export type OptionGroup = {
  id: string;
  jobId: string;
  label: string;
  preset: OptionGroupPreset;
  displayOrder: number;
};

export type RepairOrderJobDotCodesItemMethod =
  (typeof RepairOrderJobDotCodesItemMethod)[keyof typeof RepairOrderJobDotCodesItemMethod];

export const RepairOrderJobDotCodesItemMethod = {
  photo: 'photo',
  manual: 'manual',
} as const;

export type RepairOrderJobDotCodesItem = {
  /**
   * 1-based tire position within the selected tire option
   * @minimum 0
   * @exclusiveMinimum true
   */
  tireIndex: number;
  /** @minLength 1 */
  code: string;
  method: RepairOrderJobDotCodesItemMethod;
  capturedAt: string;
};

export type RepairOrderJob = {
  id: string;
  name: string;
  displayOrder: number;
  customerApprovalStatus: string | null;
  customerApprovalNotes: string | null;
  customerApprovedAt: string | null;
  workStatus: string | null;
  workStartedAt: string | null;
  workCompletedAt: string | null;
  assignedTechnicianId: string | null;
  isLockedByApproval: boolean | null;
  lockedAt: string | null;
  lockedByUserId: string | null;
  parts: JobPart[];
  labor: JobLabor[];
  fees: JobFee[];
  optionGroups?: OptionGroup[];
  dotCodes: RepairOrderJobDotCodesItem[];
};

export type RepairOrderDetail = {
  id: string;
  repairOrderNumber: string | null;
  /** RO public token used for /s/:token customer link */
  publicToken: string;
  stage: RepairOrderDetailStage;
  isEstimate: boolean;
  createdAt: string;
  updatedAt: string;
  totalAmount: number | null;
  laborCost: number | null;
  partsCost: number | null;
  taxAmount: number | null;
  suppliesAmount: number | null;
  discountAmount: number | null;
  pricing: PricingBreakdown;
  customerApprovedAt: string | null;
  isDropoff: boolean | null;
  dropoffDate: string | null;
  dropoffNotes: string | null;
  promisedPickupDate: string | null;
  estimatedCompletionDate: string | null;
  actualPickupDate: string | null;
  mileageIn: number | null;
  mileageOut: number | null;
  workflowStageId: string | null;
  workflowSubStageId: string | null;
  stageEnteredAt: string | null;
  stageNotes: string | null;
  /** Reason for closing the repair order */
  closureReason: RepairOrderDetailClosureReason;
  pickedUp: boolean;
  closedAt: string | null;
  /** Priority level of the repair order */
  priorityLevel: RepairOrderDetailPriorityLevel;
  isArchived: boolean;
  estimateToken: string | null;
  estimateExpiresAt: string | null;
  /** True when the estimate has been shared with the customer at least once. Derived server-side from estimateSendOptions, which sendEstimate populates on first share. */
  estimateSent: boolean;
  /** The send-options snapshot persisted by the most recent estimate share. Null when the estimate has never been shared. Used by the FE to mirror estimateSent and to pre-populate the share dialog. */
  estimateSendOptions: RepairOrderDetailEstimateSendOptions;
  /** True for the per-shop pre-baked sample RO created during onboarding. Sample ROs are excluded from list/billing/analytics queries. */
  isSample: boolean;
  customer: CustomerOrNull | null;
  vehicle: VehicleOrNull | null;
  workflow: RepairOrderWorkflow;
  serviceAdvisor: UserOrNull | null;
  technicians: RepairOrderTechnician[];
  tags: Tag[];
  jobs: RepairOrderJob[];
  partDisplayMode?: string;
  /** List of customer concern strings */
  customerConcerns: string[];
  hasDvi: boolean;
  hasInspectionSummary: boolean;
  createdByUserId: string | null;
  pricingLocked: boolean;
  approvedPricingSnapshot?: unknown | null;
  shopId: string;
  customerId: string;
  vehicleId: string;
  serviceAdvisorId: string | null;
  assignedTechnicianId: string | null;
  /** Ordered list of disclosure ids attached to this RO. Empty array if none. */
  disclosureIds: string[];
};

/**
 * Priority level of the repair order
 */
export type CreateRepairOrderRequestPriorityLevel =
  (typeof CreateRepairOrderRequestPriorityLevel)[keyof typeof CreateRepairOrderRequestPriorityLevel];

export const CreateRepairOrderRequestPriorityLevel = {
  low: 'low',
  normal: 'normal',
  high: 'high',
  urgent: 'urgent',
} as const;

export type CreateJobPart = {
  partName?: string;
  partNumber?: string;
  /** @minimum 0 */
  quantity?: number;
  /** @minimum 0 */
  unitPrice?: number;
  /** @minimum 0 */
  costPrice?: number;
  /** @minimum 0 */
  coreCharge?: number;
  brandDisplayName?: string;
  originalData?: unknown | null;
  pricingMatrixId?: string | null;
  /** @minimum 0 */
  overrideOriginalRetail?: number | null;
  recalculateRetail?: boolean;
  position?: string | null;
  isTire?: boolean;
  /** @minimum 0 */
  fet?: number;
  brand?: string | null;
  model?: string | null;
  size?: string | null;
  optionGroupId?: string | null;
  isSelectedOption?: boolean;
};

export type CreateJobLabor = {
  description?: string;
  /** @minimum 0 */
  hours?: number;
  /** @minimum 0 */
  hourlyRate?: number;
  technicianId?: string;
  userId?: string;
  skillLevel?: string;
  shopLaborRateId?: string | null;
};

export type CreateJob = {
  /** @minLength 1 */
  name: string;
  sortOrder?: number;
  parts?: CreateJobPart[];
  labor?: CreateJobLabor[];
};

export type CreateRepairOrderRequest = {
  /** Customer UUID */
  customerId: string;
  /** Vehicle UUID */
  vehicleId: string;
  /**
   * List of customer concern strings
   * @items.minLength 1
   */
  customerConcerns?: string[];
  workflowStageId?: string;
  /** Priority level of the repair order */
  priorityLevel?: CreateRepairOrderRequestPriorityLevel;
  serviceAdvisorId?: string;
  isDropoff?: boolean;
  dropoffDate?: string;
  promisedPickupDate?: string;
  estimatedCompletionDate?: string;
  /** @minimum 0 */
  mileageIn?: number;
  jobs?: CreateJob[];
};

/**
 * Priority level of the repair order
 */
export type UpdateRepairOrderRequestPriorityLevel =
  | (typeof UpdateRepairOrderRequestPriorityLevel)[keyof typeof UpdateRepairOrderRequestPriorityLevel]
  | null;

export const UpdateRepairOrderRequestPriorityLevel = {
  low: 'low',
  normal: 'normal',
  high: 'high',
  urgent: 'urgent',
} as const;

export type UpdateRepairOrderRequest = {
  /**
   * Replace all customer concerns with this list
   * @items.minLength 1
   */
  customerConcerns?: string[];
  workflowStageId?: string;
  workflowSubStageId?: string | null;
  stageNotes?: string | null;
  serviceAdvisorId?: string | null;
  /** Replace the vehicle on this repair order */
  vehicleId?: string | null;
  /** Priority level of the repair order */
  priorityLevel?: UpdateRepairOrderRequestPriorityLevel;
  promisedPickupDate?: string | null;
  estimatedCompletionDate?: string | null;
  dropoffDate?: string | null;
  isDropoff?: boolean;
  /** @minimum 0 */
  mileageIn?: number | null;
  /** @minimum 0 */
  mileageOut?: number | null;
  /** Bypass the labor-complete gate when transitioning into Invoice. Requires the `repairOrders.forceComplete` permission; inert on any non-into-Invoice transition. */
  force?: boolean;
};

export type ReplaceTechniciansRequest = {
  technicianIds: string[];
};

export type ReplaceTagsRequest = {
  tagIds: string[];
};

export type ActivityLogEntryMessageParams = { [key: string]: unknown | null } | null;

export type ActivityLogEntry = {
  id: string;
  messageKey: string;
  messageParams: ActivityLogEntryMessageParams;
  actorName: string | null;
  createdAt: string;
};

export type ActivityLogResponse = {
  items: ActivityLogEntry[];
};

export type RepairOrderPart = {
  id: string;
  repairOrderId: string;
  partName: string;
  partNumber: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  inventoryItemId: string | null;
  brandDisplayName: string | null;
  createdAt: string;
};

export type AddRepairOrderPartRequestOriginalData = { [key: string]: unknown | null } | null;

export type AddRepairOrderPartRequest = {
  /** @minLength 1 */
  partName: string;
  partNumber?: string | null;
  /** @minimum 1 */
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  inventoryItemId?: string | null;
  brandDisplayName?: string | null;
  originalData?: AddRepairOrderPartRequestOriginalData;
};

/**
 * Invoice lifecycle status
 */
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

export const InvoiceStatus = {
  draft: 'draft',
  open: 'open',
  partially_paid: 'partially_paid',
  paid: 'paid',
  voided: 'voided',
  uncollectible: 'uncollectible',
} as const;

export type Invoice = {
  id: string;
  invoiceNumber: string;
  /** Invoice lifecycle status */
  status: InvoiceStatus;
  customerName: string | null;
  customerEmail: string | null;
  customerId: string | null;
  repairOrderId: string | null;
  amountSubtotal: number;
  amountTax: number;
  amountFet: number;
  amountTotal: number;
  currency: string;
  notes: string | null;
  dueDate: string | null;
  sentAt: string | null;
  postedAt: string | null;
  voidedAt: string | null;
  publicPaymentToken: string;
  /** Whether the sent invoice carries a Stripe pay link. Null when the invoice has not yet been sent, or when the caller did not override the shop default at send time. */
  includePaymentLink: boolean | null;
  customerAddressLine1: string | null;
  customerAddressLine2: string | null;
  customerCity: string | null;
  customerState: string | null;
  customerZipCode: string | null;
  /** Tax rate (percent) snapshotted at issuance — e.g. 8.25 for 8.25%. */
  taxRate: number | null;
  /** Free-form jurisdiction hint (typically the shop state) for sales tax reporting. */
  taxJurisdiction: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ConvertToInvoiceRequest = {
  /** Bypass the labor-complete gate when converting into Invoice. Requires the `repairOrders.forceComplete` permission. The no-approved-jobs and no-billable-content checks still apply. */
  force?: boolean;
};

export type WorkflowTransitionResult = {
  stageChanged: boolean;
  subStageChanged: boolean;
  newStageName: string | null;
  newSubStageName: string | null;
  closed: boolean;
  closureReason?: string;
};

export type WorkflowEvent =
  | {
      type: 'ro_created';
      hasAppointment: boolean;
    }
  | {
      type: 'appointment_set';
    }
  | {
      type: 'vehicle_checked_in';
    }
  | {
      type: 'dvi_started';
    }
  | {
      type: 'dvi_completed';
    }
  | {
      type: 'estimate_sent';
    }
  | {
      type: 'customer_approved';
      /** @minimum 0 */
      approvedCount: number;
      /** @minimum 0 */
      declinedCount: number;
      /** @minimum 1 */
      totalCount: number;
    }
  | {
      type: 'customer_declined_all';
    }
  | {
      type: 'start_work';
    }
  | {
      type: 'parts_needed';
    }
  | {
      type: 'parts_arrived';
    }
  | {
      type: 'addon_sent';
    }
  | {
      type: 'addon_approved';
    }
  | {
      type: 'addon_declined';
    }
  | {
      type: 'mark_on_hold';
    }
  | {
      type: 'resume_from_hold';
    }
  | {
      type: 'assign_to_vendor';
    }
  | {
      type: 'sublet_completed';
    }
  | {
      type: 'mark_work_complete';
      force?: boolean;
    }
  | {
      type: 'invoice_created';
    }
  | {
      type: 'invoice_sent';
    }
  | {
      type: 'payment_recorded';
      isPaid: boolean;
    }
  | {
      type: 'marked_picked_up';
    }
  | {
      type: 'close_out';
      /** Reason for closing the repair order */
      reason:
        | 'completed'
        | 'no_show'
        | 'referred_out'
        | 'unable_to_diagnose'
        | 'cancelled_by_customer'
        | 'shop_unable_to_perform'
        | 'customer_declined_all'
        | 'duplicate_or_error'
        | 'customer_unreachable'
        | 'written_off'
        | 'other';
      notes?: string;
    };

export type AdvanceWorkflowRequest = {
  event: WorkflowEvent;
};

export type MoveSubStageRequest = {
  /** Target sub-stage UUID */
  subStageId: string;
};

export type CloseRepairOrderResponse = {
  stageChanged: boolean;
  subStageChanged: boolean;
  newStageName: string | null;
  newSubStageName: string | null;
  closed: boolean;
  closureReason?: string;
};

/**
 * Reason for closing the repair order
 */
export type CloseRepairOrderRequestClosureReason =
  (typeof CloseRepairOrderRequestClosureReason)[keyof typeof CloseRepairOrderRequestClosureReason];

export const CloseRepairOrderRequestClosureReason = {
  completed: 'completed',
  no_show: 'no_show',
  referred_out: 'referred_out',
  unable_to_diagnose: 'unable_to_diagnose',
  cancelled_by_customer: 'cancelled_by_customer',
  shop_unable_to_perform: 'shop_unable_to_perform',
  customer_declined_all: 'customer_declined_all',
  duplicate_or_error: 'duplicate_or_error',
  customer_unreachable: 'customer_unreachable',
  written_off: 'written_off',
  other: 'other',
} as const;

export type CloseRepairOrderRequest = {
  /** Reason for closing the repair order */
  closureReason: CloseRepairOrderRequestClosureReason;
  /** Additional notes (required when reason is other) */
  notes?: string;
};

export type MarkPickedUpResponse = {
  stageChanged: boolean;
  subStageChanged: boolean;
  newStageName: string | null;
  newSubStageName: string | null;
  closed: boolean;
  closureReason?: string;
};

export type ReopenRepairOrderResponse = {
  stageChanged: boolean;
  subStageChanged: boolean;
  newStageName: string | null;
  newSubStageName: string | null;
  closed: false;
};

/**
 * Stage to reopen the RO into. Subject to the closure-reason → target matrix.
 */
export type ReopenRepairOrderRequestTargetStage =
  (typeof ReopenRepairOrderRequestTargetStage)[keyof typeof ReopenRepairOrderRequestTargetStage];

export const ReopenRepairOrderRequestTargetStage = {
  intake: 'intake',
  in_progress: 'in_progress',
} as const;

export type ReopenRepairOrderRequest = {
  /** Stage to reopen the RO into. Subject to the closure-reason → target matrix. */
  targetStage: ReopenRepairOrderRequestTargetStage;
  /**
   * Required free-text rationale for the reopen — written to the activity log.
   * @minLength 1
   */
  notes: string;
};

export type CreateJobRequest = {
  /** @minLength 1 */
  name: string;
  sortOrder?: number;
};

export type CreatedJobResponse = {
  id: string;
};

export type SmartBuildResponse = {
  created: string[];
  skippedDuplicates: string[];
};

export type SmartBuildSuggestionUrgency =
  (typeof SmartBuildSuggestionUrgency)[keyof typeof SmartBuildSuggestionUrgency];

export const SmartBuildSuggestionUrgency = {
  immediate: 'immediate',
  recommended: 'recommended',
} as const;

export type SmartBuildSuggestionCategory =
  (typeof SmartBuildSuggestionCategory)[keyof typeof SmartBuildSuggestionCategory];

export const SmartBuildSuggestionCategory = {
  safety: 'safety',
  reliability: 'reliability',
  maintenance: 'maintenance',
  comfort: 'comfort',
} as const;

export type SmartBuildSuggestion = {
  /** @minLength 1 */
  name: string;
  cannedJobId?: string;
  sourceItems?: string[];
  accepted: boolean;
  urgency?: SmartBuildSuggestionUrgency;
  category?: SmartBuildSuggestionCategory;
  alreadyOnRepairOrder?: boolean;
};

export type SmartBuildRequest = {
  /**
   * @minItems 1
   * @maxItems 20
   */
  suggestions: SmartBuildSuggestion[];
  requestLogId?: string;
};

export type UpdateJobRequest = {
  /** @minLength 1 */
  name?: string;
  customerApprovalStatus?: string | null;
};

export type CreateJobFeeAmountType = (typeof CreateJobFeeAmountType)[keyof typeof CreateJobFeeAmountType];

export const CreateJobFeeAmountType = {
  fixed: 'fixed',
  percentage: 'percentage',
} as const;

export type CreateJobFeePerUnitBasis = (typeof CreateJobFeePerUnitBasis)[keyof typeof CreateJobFeePerUnitBasis] | null;

export const CreateJobFeePerUnitBasis = {
  tire_count: 'tire_count',
} as const;

export type CreateJobFee = {
  /** @minLength 1 */
  name: string;
  /** @minimum 0 */
  amount: number;
  amountType?: CreateJobFeeAmountType;
  isTaxable?: boolean;
  displayOrder?: number;
  catalogId?: string | null;
  perUnitBasis?: CreateJobFeePerUnitBasis;
  /** @minimum 0 */
  defaultAmount?: number | null;
};

export type CreateJobSublet = {
  /** @minLength 1 */
  vendorName: string;
  description?: string | null;
  /** @minimum 0 */
  cost?: number;
};

export type ReplaceOptionGroupPreset = (typeof ReplaceOptionGroupPreset)[keyof typeof ReplaceOptionGroupPreset] | null;

export const ReplaceOptionGroupPreset = {
  gbb: 'gbb',
  numbered: 'numbered',
} as const;

export type ReplaceOptionGroup = {
  id?: string;
  /** @minLength 1 */
  label: string;
  preset: ReplaceOptionGroupPreset;
  displayOrder?: number;
};

export type ReplaceJobLinesRequest = {
  parts?: CreateJobPart[];
  labor?: CreateJobLabor[];
  fees?: CreateJobFee[];
  sublets?: CreateJobSublet[];
  optionGroups?: ReplaceOptionGroup[];
};

export type AddJobFeeRequestAmountType = (typeof AddJobFeeRequestAmountType)[keyof typeof AddJobFeeRequestAmountType];

export const AddJobFeeRequestAmountType = {
  fixed: 'fixed',
  percentage: 'percentage',
} as const;

export type AddJobFeeRequestPerUnitBasis =
  | (typeof AddJobFeeRequestPerUnitBasis)[keyof typeof AddJobFeeRequestPerUnitBasis]
  | null;

export const AddJobFeeRequestPerUnitBasis = {
  tire_count: 'tire_count',
} as const;

export type AddJobFeeRequest = {
  /** @minLength 1 */
  name: string;
  /** @minimum 0 */
  amount: number;
  amountType?: AddJobFeeRequestAmountType;
  isTaxable?: boolean;
  catalogId?: string | null;
  perUnitBasis?: AddJobFeeRequestPerUnitBasis;
  /** @minimum 0 */
  defaultAmount?: number | null;
};

export type UpdateJobFeeRequestAmountType =
  (typeof UpdateJobFeeRequestAmountType)[keyof typeof UpdateJobFeeRequestAmountType];

export const UpdateJobFeeRequestAmountType = {
  fixed: 'fixed',
  percentage: 'percentage',
} as const;

export type UpdateJobFeeRequestPerUnitBasis =
  | (typeof UpdateJobFeeRequestPerUnitBasis)[keyof typeof UpdateJobFeeRequestPerUnitBasis]
  | null;

export const UpdateJobFeeRequestPerUnitBasis = {
  tire_count: 'tire_count',
} as const;

export type UpdateJobFeeRequest = {
  /** @minLength 1 */
  name?: string;
  /** @minimum 0 */
  amount?: number;
  amountType?: UpdateJobFeeRequestAmountType;
  isTaxable?: boolean;
  perUnitBasis?: UpdateJobFeeRequestPerUnitBasis;
  /** @minimum 0 */
  defaultAmount?: number | null;
};

export type SaveDotCodesResponseDotCodesItemMethod =
  (typeof SaveDotCodesResponseDotCodesItemMethod)[keyof typeof SaveDotCodesResponseDotCodesItemMethod];

export const SaveDotCodesResponseDotCodesItemMethod = {
  photo: 'photo',
  manual: 'manual',
} as const;

export type SaveDotCodesResponseDotCodesItem = {
  /**
   * 1-based tire position within the selected tire option
   * @minimum 0
   * @exclusiveMinimum true
   */
  tireIndex: number;
  /** @minLength 1 */
  code: string;
  method: SaveDotCodesResponseDotCodesItemMethod;
  capturedAt: string;
};

export type SaveDotCodesResponse = {
  dotCodes: SaveDotCodesResponseDotCodesItem[];
};

export type SaveDotCodesRequestDotCodesItemMethod =
  (typeof SaveDotCodesRequestDotCodesItemMethod)[keyof typeof SaveDotCodesRequestDotCodesItemMethod];

export const SaveDotCodesRequestDotCodesItemMethod = {
  photo: 'photo',
  manual: 'manual',
} as const;

export type SaveDotCodesRequestDotCodesItem = {
  /**
   * 1-based tire position within the selected tire option
   * @minimum 0
   * @exclusiveMinimum true
   */
  tireIndex: number;
  /** @minLength 1 */
  code: string;
  method: SaveDotCodesRequestDotCodesItemMethod;
  capturedAt: string;
};

export type SaveDotCodesRequest = {
  dotCodes: SaveDotCodesRequestDotCodesItem[];
};

export type DecodeDotPhotoResponse = {
  dotCode: string | null;
  confidence: number;
};

export type DecodeDotPhotoRequest = {
  /**
   * @minLength 1
   * @maxLength 14000000
   */
  imageBase64: string;
};

export type UnlockJobRequest = {
  /** @minLength 1 */
  reason: string;
};

export type AddJobPartRequest = {
  partName?: string;
  partNumber?: string;
  /** @minimum 0 */
  quantity?: number;
  /** @minimum 0 */
  unitPrice?: number;
  /** @minimum 0 */
  costPrice?: number;
  /** @minimum 0 */
  coreCharge?: number;
  brandDisplayName?: string;
  originalData?: unknown | null;
  pricingMatrixId?: string | null;
  /** @minimum 0 */
  overrideOriginalRetail?: number | null;
  recalculateRetail?: boolean;
  position?: string | null;
  isTire?: boolean;
  /** @minimum 0 */
  fet?: number;
  brand?: string | null;
  model?: string | null;
  size?: string | null;
  optionGroupId?: string | null;
  isSelectedOption?: boolean;
};

export type UpdateJobPartRequest = {
  /** @minLength 1 */
  partName?: string;
  partNumber?: string | null;
  /** @minimum 0 */
  quantity?: number;
  /** @minimum 0 */
  unitPrice?: number;
  /** @minimum 0 */
  costPrice?: number | null;
  /** @minimum 0 */
  coreCharge?: number | null;
  brandDisplayName?: string | null;
  originalData?: unknown | null;
  pricingMatrixId?: string | null;
  /** @minimum 0 */
  overrideOriginalRetail?: number | null;
  recalculateRetail?: boolean;
  position?: string | null;
  isTire?: boolean;
  /** @minimum 0 */
  fet?: number;
  brand?: string | null;
  model?: string | null;
  size?: string | null;
  optionGroupId?: string | null;
  isSelectedOption?: boolean;
};

export type AddJobTireRequest = {
  partName?: string;
  partNumber?: string;
  /** @minimum 0 */
  quantity?: number;
  /** @minimum 0 */
  unitPrice?: number;
  /** @minimum 0 */
  costPrice?: number;
  /** @minimum 0 */
  coreCharge?: number;
  brandDisplayName?: string;
  originalData?: unknown | null;
  pricingMatrixId?: string | null;
  /** @minimum 0 */
  overrideOriginalRetail?: number | null;
  recalculateRetail?: boolean;
  position?: string | null;
  /** @minimum 0 */
  fet?: number;
  brand?: string | null;
  model?: string | null;
  size?: string | null;
  optionGroupId?: string | null;
  isSelectedOption?: boolean;
};

export type UpdateJobTireRequest = {
  /** @minLength 1 */
  partName?: string;
  partNumber?: string | null;
  /** @minimum 0 */
  quantity?: number;
  /** @minimum 0 */
  unitPrice?: number;
  /** @minimum 0 */
  costPrice?: number | null;
  /** @minimum 0 */
  coreCharge?: number | null;
  brandDisplayName?: string | null;
  originalData?: unknown | null;
  pricingMatrixId?: string | null;
  /** @minimum 0 */
  overrideOriginalRetail?: number | null;
  recalculateRetail?: boolean;
  position?: string | null;
  /** @minimum 0 */
  fet?: number;
  brand?: string | null;
  model?: string | null;
  size?: string | null;
  optionGroupId?: string | null;
  isSelectedOption?: boolean;
};

export type CreateOptionGroupRequestPreset =
  | (typeof CreateOptionGroupRequestPreset)[keyof typeof CreateOptionGroupRequestPreset]
  | null;

export const CreateOptionGroupRequestPreset = {
  gbb: 'gbb',
  numbered: 'numbered',
} as const;

export type CreateOptionGroupRequest = {
  /** @minLength 1 */
  label: string;
  preset: CreateOptionGroupRequestPreset;
  /** @minItems 2 */
  partIds: string[];
};

export type SetOptionGroupSelectedOptionRequest = {
  partId: string;
};

export type UpdateOptionGroupRequestPreset =
  | (typeof UpdateOptionGroupRequestPreset)[keyof typeof UpdateOptionGroupRequestPreset]
  | null;

export const UpdateOptionGroupRequestPreset = {
  gbb: 'gbb',
  numbered: 'numbered',
} as const;

export type UpdateOptionGroupRequest = {
  /** @minLength 1 */
  label?: string;
  preset?: UpdateOptionGroupRequestPreset;
};

export type ReorderOptionGroupPartsRequest = {
  /** @minItems 2 */
  partIds: string[];
};

export type GenerateEstimateLinkRequest = {
  /**
   * @minimum 1
   * @maximum 365
   */
  expirationDays?: number;
};

/**
 * Indicates the send was queued, not delivered immediately
 */
export type SendEstimateScheduledResponseStatus =
  (typeof SendEstimateScheduledResponseStatus)[keyof typeof SendEstimateScheduledResponseStatus];

export const SendEstimateScheduledResponseStatus = {
  scheduled: 'scheduled',
} as const;

export type SendEstimateScheduledResponse = {
  /** Indicates the send was queued, not delivered immediately */
  status: SendEstimateScheduledResponseStatus;
  /** ISO-8601 timestamp at which the SMS will be delivered (next allowable send time per quiet-hours rules) */
  scheduledFor: string;
};

/**
 * Stable discriminator for the frontend
 */
export type SendEstimateNeedsOptInResponseCode =
  (typeof SendEstimateNeedsOptInResponseCode)[keyof typeof SendEstimateNeedsOptInResponseCode];

export const SendEstimateNeedsOptInResponseCode = {
  needs_optin: 'needs_optin',
} as const;

/**
 * `never` when no consent row exists; `opted_out` when the most recent row is opted_out
 */
export type SendEstimateNeedsOptInResponseLastConsentStatus =
  (typeof SendEstimateNeedsOptInResponseLastConsentStatus)[keyof typeof SendEstimateNeedsOptInResponseLastConsentStatus];

export const SendEstimateNeedsOptInResponseLastConsentStatus = {
  never: 'never',
  opted_out: 'opted_out',
} as const;

export type SendEstimateNeedsOptInResponse = {
  /** Human-readable explanation */
  error: string;
  /** Stable discriminator for the frontend */
  code: SendEstimateNeedsOptInResponseCode;
  /** `never` when no consent row exists; `opted_out` when the most recent row is opted_out */
  lastConsentStatus: SendEstimateNeedsOptInResponseLastConsentStatus;
};

/**
 * Delivery channel
 */
export type SendEstimateRequestChannel = (typeof SendEstimateRequestChannel)[keyof typeof SendEstimateRequestChannel];

export const SendEstimateRequestChannel = {
  email: 'email',
  sms: 'sms',
  copy_link: 'copy_link',
} as const;

export type SendEstimateOptions = {
  requireSignature: boolean;
  includeAiSummary: boolean;
  showInspection: boolean;
  showProgress: boolean;
  /** Ordered list of disclosure ids to attach. Replace semantics. Omit to leave unchanged. */
  disclosureIds?: string[];
};

export type SendEstimateRequest = {
  /** Delivery channel */
  channel: SendEstimateRequestChannel;
  /** Recipient email (required for email channel) */
  to?: string;
  /** Email subject line */
  subject?: string;
  /** Custom message body for email */
  message?: string;
  /** Recipient phone in E.164 (required for sms channel) */
  phone?: string;
  /** Custom SMS body text */
  smsBody?: string;
  options: SendEstimateOptions;
};

export type EstimateApproval = {
  id: string;
  approvalStatus: string;
  signerName: string | null;
  signerEmail: string | null;
  hasPdf: boolean;
  createdAt: string;
  approvedByUserId: string | null;
  approvedByUserName: string | null;
  hasDigitalSignature: boolean;
};

export type EstimateApprovalListResponse = {
  items: EstimateApproval[];
};

export type ApprovalPdfUrlResponse = {
  url: string;
};

export type RecalculatePricingResponse = {
  pricing: PricingBreakdown;
  repairOrder: RepairOrderDetail;
};

export type UnlockPricingRequest = {
  /** @minLength 1 */
  reason: string;
};

export type AddOnRequestStatus = (typeof AddOnRequestStatus)[keyof typeof AddOnRequestStatus];

export const AddOnRequestStatus = {
  pending: 'pending',
  sent: 'sent',
  approved: 'approved',
  declined: 'declined',
} as const;

export type AddOnRequest = {
  id: string;
  repairOrderId: string;
  status: AddOnRequestStatus;
  description: string;
  estimatedCost: number | null;
  cannedJobId: string | null;
  customerResponseAt: string | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateAddOnRequest = {
  /**
   * Description of the add-on work
   * @minLength 1
   */
  description: string;
  /**
   * Estimated cost
   * @minimum 0
   */
  estimatedCost?: number;
  /** Optional canned job ID from the jobs catalog */
  cannedJobId?: string;
};

export type AddOnRequestList = AddOnRequest[];

export type SubletResponse = {
  id: string;
  repairOrderId: string;
  jobId: string | null;
  vendorId: string | null;
  vendorName: string;
  vendorContact: string | null;
  description: string | null;
  estimatedCost: number | null;
  actualCost: number | null;
  customerPrice: number | null;
  taxable: boolean;
  status: string;
  assignedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateSubletRequest = {
  /** Sublet vendor UUID */
  vendorId: string;
  /** Optional parent job UUID. When set, the sublet attributes to that job in pricing/UI; when omitted/null it is RO-scoped (legacy). */
  jobId?: string | null;
  /**
   * Description of work being sublet
   * @maxLength 2000
   */
  description?: string;
  /**
   * Estimated cost (shop pays vendor)
   * @minimum 0
   */
  estimatedCost?: number;
  /**
   * Price charged to the customer (independent of cost)
   * @minimum 0
   */
  customerPrice?: number;
  /** Whether this sublet line is taxable (defaults to vendor.defaultTaxable) */
  taxable?: boolean;
};

export type SubletListResponse = {
  items: SubletResponse[];
};

export type UpdateSubletRequest = {
  /** New sublet vendor UUID (re-snapshots name/contact) */
  vendorId?: string;
  /** Re-attach the sublet to a job UUID, or set to null to detach. */
  jobId?: string | null;
  /**
   * Description of work being sublet
   * @maxLength 2000
   */
  description?: string | null;
  /**
   * Estimated cost (shop pays vendor)
   * @minimum 0
   */
  estimatedCost?: number | null;
  /**
   * Price charged to the customer (independent of cost)
   * @minimum 0
   */
  customerPrice?: number | null;
  /** Whether this sublet line is taxable */
  taxable?: boolean;
};

export type CreateJobLaborRequest = {
  description?: string;
  /** @minimum 0 */
  hours?: number;
  /** @minimum 0 */
  hourlyRate?: number;
  technicianId?: string;
  userId?: string;
  skillLevel?: string;
  shopLaborRateId?: string | null;
};

export type UpdateJobLaborRequest = {
  description?: string;
  /** @minimum 0 */
  hours?: number;
  /** @minimum 0 */
  hourlyRate?: number;
  technicianId?: string | null;
  userId?: string | null;
  skillLevel?: string | null;
  shopLaborRateId?: string | null;
};

export type DiscountResponseScope = (typeof DiscountResponseScope)[keyof typeof DiscountResponseScope];

export const DiscountResponseScope = {
  order: 'order',
  job: 'job',
  line: 'line',
} as const;

export type DiscountResponseSource = (typeof DiscountResponseSource)[keyof typeof DiscountResponseSource];

export const DiscountResponseSource = {
  customer: 'customer',
  coupon: 'coupon',
  manual: 'manual',
  rebate: 'rebate',
} as const;

export type DiscountResponseDiscountType =
  (typeof DiscountResponseDiscountType)[keyof typeof DiscountResponseDiscountType];

export const DiscountResponseDiscountType = {
  percent: 'percent',
  fixed: 'fixed',
} as const;

/**
 * Per-job allocation map for order-scope discounts. Keys are repair_order_jobs.id. Used by the frontend to attribute the discount inside each job card.
 */
export type DiscountResponseAllocationsPerJob = {
  [key: string]: {
    parts: number;
    labor: number;
    tires: number;
    subs: number;
    fees: number;
  };
} | null;

export type DiscountResponseStatus = (typeof DiscountResponseStatus)[keyof typeof DiscountResponseStatus];

export const DiscountResponseStatus = {
  active: 'active',
  pending_approval: 'pending_approval',
  rejected: 'rejected',
} as const;

export type DiscountResponse = {
  id: string;
  scope: DiscountResponseScope;
  targetId: string | null;
  source: DiscountResponseSource;
  sourceId: string | null;
  discountType: DiscountResponseDiscountType;
  discountValue: number;
  appliedAmount: number;
  allocatedToParts: number;
  allocatedToLabor: number;
  allocatedToTires: number;
  allocatedToSubs: number;
  allocatedToFees: number;
  /** Per-job allocation map for order-scope discounts. Keys are repair_order_jobs.id. Used by the frontend to attribute the discount inside each job card. */
  allocationsPerJob: DiscountResponseAllocationsPerJob;
  reason: string | null;
  status: DiscountResponseStatus;
  createdAt: string;
  /** Coupon display name when source=coupon (joined). Null otherwise. */
  couponName: string | null;
  /** Coupon code when source=coupon (joined). Null otherwise. */
  couponCode: string | null;
};

/**
 * Discount scope
 */
export type ApplyDiscountRequestScope = (typeof ApplyDiscountRequestScope)[keyof typeof ApplyDiscountRequestScope];

export const ApplyDiscountRequestScope = {
  order: 'order',
  job: 'job',
} as const;

/**
 * Discount source — manual for advisor-entered discounts, coupon for code-based
 */
export type ApplyDiscountRequestSource = (typeof ApplyDiscountRequestSource)[keyof typeof ApplyDiscountRequestSource];

export const ApplyDiscountRequestSource = {
  manual: 'manual',
  coupon: 'coupon',
} as const;

/**
 * Discount type
 */
export type ApplyDiscountRequestDiscountType =
  (typeof ApplyDiscountRequestDiscountType)[keyof typeof ApplyDiscountRequestDiscountType];

export const ApplyDiscountRequestDiscountType = {
  percent: 'percent',
  fixed: 'fixed',
} as const;

/**
 * Which line-item categories the discount applies to
 */
export type ApplyDiscountRequestScopeEligibility = {
  parts: boolean;
  labor: boolean;
  tires: boolean;
  subs: boolean;
  fees: boolean;
};

export type ApplyDiscountRequest = {
  /** Discount scope */
  scope: ApplyDiscountRequestScope;
  /** Job UUID when scope is job, null for order scope */
  targetId: string | null;
  /** Discount source — manual for advisor-entered discounts, coupon for code-based */
  source: ApplyDiscountRequestSource;
  /** Coupon UUID when source is coupon */
  sourceId?: string | null;
  /** Discount type */
  discountType: ApplyDiscountRequestDiscountType;
  /**
   * Discount value (percent or fixed dollar amount)
   * @minimum 0
   * @maximum 100000
   */
  discountValue: number;
  /** Which line-item categories the discount applies to */
  scopeEligibility: ApplyDiscountRequestScopeEligibility;
  /**
   * Reason for the discount (required when source=manual)
   * @minLength 1
   * @maxLength 500
   */
  reason?: string;
};

export type RemoveDiscountRequest = {
  /**
   * Optional reason for removing the discount
   * @maxLength 500
   */
  reason?: string;
};

export type CouponResponseDiscountType = (typeof CouponResponseDiscountType)[keyof typeof CouponResponseDiscountType];

export const CouponResponseDiscountType = {
  percent: 'percent',
  fixed: 'fixed',
} as const;

export type CouponResponseAppliesTo = (typeof CouponResponseAppliesTo)[keyof typeof CouponResponseAppliesTo];

export const CouponResponseAppliesTo = {
  order: 'order',
  job: 'job',
} as const;

export type CouponResponseEligibilitySegment =
  (typeof CouponResponseEligibilitySegment)[keyof typeof CouponResponseEligibilitySegment];

export const CouponResponseEligibilitySegment = {
  all: 'all',
  first_time: 'first_time',
  returning: 'returning',
  customer_tag: 'customer_tag',
  specific_customer: 'specific_customer',
} as const;

export type CouponStatus = (typeof CouponStatus)[keyof typeof CouponStatus];

export const CouponStatus = {
  active: 'active',
  scheduled: 'scheduled',
  expired: 'expired',
  disabled: 'disabled',
  archived: 'archived',
} as const;

export type CouponResponseRedemptionStats = {
  total: number;
  uniqueCustomers: number;
};

export type CouponResponse = {
  id: string;
  shopId: string;
  name: string;
  code: string;
  description: string | null;
  discountType: CouponResponseDiscountType;
  discountValue: number;
  appliesTo: CouponResponseAppliesTo;
  appliesToCannedJobIds: string[] | null;
  eligibilitySegment: CouponResponseEligibilitySegment;
  eligibleCustomerTag: string | null;
  eligibleCustomerIds: string[] | null;
  startDate: string | null;
  endDate: string | null;
  minSpend: number | null;
  maxDiscount: number | null;
  maxRedemptionsTotal: number | null;
  maxRedemptionsPerCustomer: number | null;
  allowStacking: boolean;
  additionalTerms: string | null;
  isActive: boolean;
  archivedAt: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  status: CouponStatus;
  redemptionStats: CouponResponseRedemptionStats;
};

export type ListCouponsResponse = {
  items: CouponResponse[];
  total: number;
};

/**
 * Whether the discount is a percentage or a fixed dollar amount
 */
export type CreateCouponRequestDiscountType =
  (typeof CreateCouponRequestDiscountType)[keyof typeof CreateCouponRequestDiscountType];

export const CreateCouponRequestDiscountType = {
  percent: 'percent',
  fixed: 'fixed',
} as const;

/**
 * Whether the coupon applies to the whole order or specific jobs
 */
export type CreateCouponRequestAppliesTo =
  (typeof CreateCouponRequestAppliesTo)[keyof typeof CreateCouponRequestAppliesTo];

export const CreateCouponRequestAppliesTo = {
  order: 'order',
  job: 'job',
} as const;

/**
 * Which customers are eligible to use this coupon
 */
export type CreateCouponRequestEligibilitySegment =
  (typeof CreateCouponRequestEligibilitySegment)[keyof typeof CreateCouponRequestEligibilitySegment];

export const CreateCouponRequestEligibilitySegment = {
  all: 'all',
  first_time: 'first_time',
  returning: 'returning',
  customer_tag: 'customer_tag',
  specific_customer: 'specific_customer',
} as const;

export type CreateCouponRequest = {
  /**
   * Display name for the coupon
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * Coupon code (auto-generated if omitted or null)
   * @maxLength 20
   */
  code?: string | null;
  /**
   * Optional internal description
   * @maxLength 2000
   */
  description?: string | null;
  /** Whether the discount is a percentage or a fixed dollar amount */
  discountType: CreateCouponRequestDiscountType;
  /**
   * Discount amount — percentage (0–100) or fixed dollar value
   * @minimum 0
   * @exclusiveMinimum true
   */
  discountValue: number;
  /** Whether the coupon applies to the whole order or specific jobs */
  appliesTo: CreateCouponRequestAppliesTo;
  /** Canned job UUIDs this coupon applies to when appliesTo is "job". Null means any job; non-empty array restricts to specific jobs. */
  appliesToCannedJobIds?: string[] | null;
  /** Which customers are eligible to use this coupon */
  eligibilitySegment: CreateCouponRequestEligibilitySegment;
  /**
   * Required when eligibilitySegment is "customer_tag". Tag name to match against customer tags.
   * @maxLength 50
   */
  eligibleCustomerTag?: string | null;
  /** Required when eligibilitySegment is "specific_customer". UUIDs of eligible customers. */
  eligibleCustomerIds?: string[] | null;
  /**
   * ISO date (YYYY-MM-DD) when the coupon becomes active
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  startDate?: string | null;
  /**
   * ISO date (YYYY-MM-DD) when the coupon expires (inclusive)
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  endDate?: string | null;
  /**
   * Minimum repair order subtotal required to apply the coupon
   * @minimum 0
   */
  minSpend?: number | null;
  /**
   * Maximum dollar amount that can be discounted (caps percent discounts)
   * @minimum 0
   */
  maxDiscount?: number | null;
  /**
   * Total number of times this coupon may be redeemed across all customers
   * @minimum 0
   * @exclusiveMinimum true
   */
  maxRedemptionsTotal?: number | null;
  /**
   * Maximum number of times a single customer may redeem this coupon
   * @minimum 0
   * @exclusiveMinimum true
   */
  maxRedemptionsPerCustomer?: number | null;
  /** Whether this coupon can be combined with other discounts. Defaults to false. */
  allowStacking?: boolean;
  /**
   * Customer-facing fine print or terms
   * @maxLength 2000
   */
  additionalTerms?: string | null;
  /** Whether the coupon is active. Defaults to true. */
  isActive?: boolean;
};

export type ValidateCouponCodeSuccess = {
  valid: true;
  couponId: string;
  appliedAmount: number;
};

export type ValidateCouponCodeFailureReason =
  (typeof ValidateCouponCodeFailureReason)[keyof typeof ValidateCouponCodeFailureReason];

export const ValidateCouponCodeFailureReason = {
  inactive: 'inactive',
  archived: 'archived',
  not_yet_active: 'not_yet_active',
  expired: 'expired',
  min_spend_not_met: 'min_spend_not_met',
  max_redemptions_reached: 'max_redemptions_reached',
  max_redemptions_per_customer_reached: 'max_redemptions_per_customer_reached',
  customer_not_eligible: 'customer_not_eligible',
  coupon_not_found: 'coupon_not_found',
} as const;

export type ValidateCouponCodeFailure = {
  valid: false;
  reason: ValidateCouponCodeFailureReason;
};

export type ValidateCouponCodeResponse = ValidateCouponCodeSuccess | ValidateCouponCodeFailure;

export type ValidateCouponCodeRequest = {
  /**
   * Coupon code to validate
   * @minLength 1
   * @maxLength 20
   */
  code: string;
  /** UUID of the customer applying the coupon */
  customerId: string;
  /** UUID of the repair order the coupon is being applied to */
  repairOrderId: string;
};

export type ListEligibleCouponsResponse = {
  items: CouponResponse[];
};

/**
 * Whether the discount is a percentage or a fixed dollar amount
 */
export type UpdateCouponRequestDiscountType =
  (typeof UpdateCouponRequestDiscountType)[keyof typeof UpdateCouponRequestDiscountType];

export const UpdateCouponRequestDiscountType = {
  percent: 'percent',
  fixed: 'fixed',
} as const;

/**
 * Whether the coupon applies to the whole order or specific jobs
 */
export type UpdateCouponRequestAppliesTo =
  (typeof UpdateCouponRequestAppliesTo)[keyof typeof UpdateCouponRequestAppliesTo];

export const UpdateCouponRequestAppliesTo = {
  order: 'order',
  job: 'job',
} as const;

/**
 * Which customers are eligible to use this coupon
 */
export type UpdateCouponRequestEligibilitySegment =
  (typeof UpdateCouponRequestEligibilitySegment)[keyof typeof UpdateCouponRequestEligibilitySegment];

export const UpdateCouponRequestEligibilitySegment = {
  all: 'all',
  first_time: 'first_time',
  returning: 'returning',
  customer_tag: 'customer_tag',
  specific_customer: 'specific_customer',
} as const;

export type UpdateCouponRequest = {
  /**
   * Display name for the coupon
   * @minLength 1
   * @maxLength 100
   */
  name?: string;
  /**
   * Optional internal description
   * @maxLength 2000
   */
  description?: string | null;
  /** Whether the discount is a percentage or a fixed dollar amount */
  discountType?: UpdateCouponRequestDiscountType;
  /**
   * Discount amount — percentage (0–100) or fixed dollar value
   * @minimum 0
   * @exclusiveMinimum true
   */
  discountValue?: number;
  /** Whether the coupon applies to the whole order or specific jobs */
  appliesTo?: UpdateCouponRequestAppliesTo;
  /** Canned job UUIDs this coupon applies to when appliesTo is "job". Null means any job; non-empty array restricts to specific jobs. */
  appliesToCannedJobIds?: string[] | null;
  /** Which customers are eligible to use this coupon */
  eligibilitySegment?: UpdateCouponRequestEligibilitySegment;
  /**
   * Required when eligibilitySegment is "customer_tag". Tag name to match against customer tags.
   * @maxLength 50
   */
  eligibleCustomerTag?: string | null;
  /** Required when eligibilitySegment is "specific_customer". UUIDs of eligible customers. */
  eligibleCustomerIds?: string[] | null;
  /**
   * ISO date (YYYY-MM-DD) when the coupon becomes active
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  startDate?: string | null;
  /**
   * ISO date (YYYY-MM-DD) when the coupon expires (inclusive)
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  endDate?: string | null;
  /**
   * Minimum repair order subtotal required to apply the coupon
   * @minimum 0
   */
  minSpend?: number | null;
  /**
   * Maximum dollar amount that can be discounted (caps percent discounts)
   * @minimum 0
   */
  maxDiscount?: number | null;
  /**
   * Total number of times this coupon may be redeemed across all customers
   * @minimum 0
   * @exclusiveMinimum true
   */
  maxRedemptionsTotal?: number | null;
  /**
   * Maximum number of times a single customer may redeem this coupon
   * @minimum 0
   * @exclusiveMinimum true
   */
  maxRedemptionsPerCustomer?: number | null;
  /** Whether this coupon can be combined with other discounts. Defaults to false. */
  allowStacking?: boolean;
  /**
   * Customer-facing fine print or terms
   * @maxLength 2000
   */
  additionalTerms?: string | null;
  /** Whether the coupon is active. Defaults to true. */
  isActive?: boolean;
};

export type RedemptionResponse = {
  id: string;
  customerId: string;
  /** Full name of the customer */
  customerName: string;
  repairOrderId: string;
  /** Human-readable RO number */
  repairOrderNumber: string | null;
  /** Dollar amount discounted at redemption time */
  redeemedAmount: number;
  /** ISO datetime when the coupon was redeemed */
  redeemedAt: string;
  /** ISO datetime when the redemption was reversed, if applicable */
  reversedAt: string | null;
};

export type ListRedemptionsResponse = {
  items: RedemptionResponse[];
};

/**
 * Type of inventory item
 */
export type InventoryItemItemType = (typeof InventoryItemItemType)[keyof typeof InventoryItemItemType];

export const InventoryItemItemType = {
  part: 'part',
  tire: 'tire',
  fluid: 'fluid',
  supply: 'supply',
} as const;

export type InventoryItem = {
  id: string;
  userId: string;
  shopId: string;
  /** Type of inventory item */
  itemType: InventoryItemItemType;
  name: string;
  partNumber: string | null;
  brand: string | null;
  description: string | null;
  category: string | null;
  currentQuantity: number;
  minimumQuantity: number;
  reorderQuantity: number;
  unitCost: number;
  sellingPrice: number;
  location: string | null;
  supplier: string | null;
  supplierId: string | null;
  autoReorder: boolean;
  tireSize: string | null;
  tireType: string | null;
  loadRating: string | null;
  speedRating: string | null;
  treadDepth: number | null;
  viscosity: string | null;
  volumePerUnit: number | null;
  containerType: string | null;
  supplyType: string | null;
  dimensions: string | null;
  material: string | null;
  partTypeName: string | null;
  externalSource: string | null;
  externalId: string | null;
  externalPayload?: unknown | null;
  imageUrl: string | null;
  importId: string | null;
  createdAt: string;
  updatedAt: string;
  activeReservationsCount: number;
  onOrderCount?: number;
};

export type InventoryListResponse = {
  items: InventoryItem[];
  /** Total number of inventory items */
  total: number;
};

/**
 * Type of inventory item
 */
export type CreateInventoryItemRequestItemType =
  (typeof CreateInventoryItemRequestItemType)[keyof typeof CreateInventoryItemRequestItemType];

export const CreateInventoryItemRequestItemType = {
  part: 'part',
  tire: 'tire',
  fluid: 'fluid',
  supply: 'supply',
} as const;

export type CreateInventoryItemRequest = {
  /** @minLength 1 */
  name: string;
  /** Type of inventory item */
  itemType: CreateInventoryItemRequestItemType;
  partNumber?: string;
  brand?: string;
  description?: string;
  category?: string;
  /** @minimum 0 */
  currentQuantity?: number;
  /** @minimum 0 */
  minimumQuantity?: number;
  /** @minimum 0 */
  reorderQuantity?: number;
  /** @minimum 0 */
  unitCost?: number;
  /** @minimum 0 */
  sellingPrice?: number;
  location?: string;
  supplier?: string;
  supplierId?: string;
  autoReorder?: boolean;
  tireSize?: string;
  tireType?: string;
  loadRating?: string;
  speedRating?: string;
  treadDepth?: number;
  viscosity?: string;
  volumePerUnit?: number;
  containerType?: string;
  supplyType?: string;
  dimensions?: string;
  material?: string;
  partTypeName?: string;
  externalSource?: string;
  externalId?: string;
  externalPayload?: unknown | null;
  imageUrl?: string;
  importId?: string;
};

export type InventoryStats = {
  /** Total number of inventory items */
  totalItems: number;
  /** Total inventory value (sum of unitCost * currentQuantity) */
  totalValue: number;
  /** Number of items below minimum quantity */
  lowStockCount: number;
  /** Number of items with zero quantity */
  outOfStockCount: number;
};

export type BulkDeleteInventoryItemsResponseErrorsItem = {
  id: string;
  reason: string;
};

export type BulkDeleteInventoryItemsResponse = {
  /** Number of items successfully deleted */
  deletedCount: number;
  /** Items that could not be deleted */
  errors: BulkDeleteInventoryItemsResponseErrorsItem[];
};

export type BulkDeleteInventoryItemsRequest = {
  /**
   * Array of inventory item UUIDs to delete
   * @minItems 1
   * @maxItems 100
   */
  ids: string[];
};

export type LatestImportResponseImport = {
  id: string;
  cartData?: unknown | null;
  createdAt: string;
} | null;

export type LatestImportResponse = {
  import: LatestImportResponseImport;
};

export type ReservationAggregate = {
  inventoryItemId: string;
  totalReserved: number;
};

export type ReservationListResponse = {
  reservations: ReservationAggregate[];
};

export type Reservation = {
  id: string;
  userId: string;
  inventoryItemId: string;
  repairOrderId: string;
  quantity: number;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateReservationRequest = {
  /** Inventory item UUID */
  inventoryItemId: string;
  /** Repair order UUID */
  repairOrderId: string;
  /**
   * Quantity to reserve
   * @minimum 1
   */
  quantity: number;
};

/**
 * Type of inventory item
 */
export type UpdateInventoryItemRequestItemType =
  (typeof UpdateInventoryItemRequestItemType)[keyof typeof UpdateInventoryItemRequestItemType];

export const UpdateInventoryItemRequestItemType = {
  part: 'part',
  tire: 'tire',
  fluid: 'fluid',
  supply: 'supply',
} as const;

export type UpdateInventoryItemRequest = {
  /** @minLength 1 */
  name?: string;
  /** Type of inventory item */
  itemType?: UpdateInventoryItemRequestItemType;
  partNumber?: string;
  brand?: string;
  description?: string;
  category?: string;
  /** @minimum 0 */
  currentQuantity?: number;
  /** @minimum 0 */
  minimumQuantity?: number;
  /** @minimum 0 */
  reorderQuantity?: number;
  /** @minimum 0 */
  unitCost?: number;
  /** @minimum 0 */
  sellingPrice?: number;
  location?: string;
  supplier?: string;
  supplierId?: string;
  autoReorder?: boolean;
  tireSize?: string;
  tireType?: string;
  loadRating?: string;
  speedRating?: string;
  treadDepth?: number;
  viscosity?: string;
  volumePerUnit?: number;
  containerType?: string;
  supplyType?: string;
  dimensions?: string;
  material?: string;
  partTypeName?: string;
  externalSource?: string;
  externalId?: string;
  externalPayload?: unknown | null;
  imageUrl?: string;
  importId?: string;
};

export type LinkInventoryItemRequestExternalPayload = { [key: string]: unknown | null };

export type LinkInventoryItemRequest = {
  name: string;
  partNumber: string;
  brand: string | null;
  category: string | null;
  /** @pattern ^\d+(\.\d+)?$ */
  unitCost: string;
  supplier: string | null;
  externalId: string;
  externalPayload: LinkInventoryItemRequestExternalPayload;
  imageUrl: string | null;
};

/**
 * Type of supplier
 */
export type SupplierSupplierType = (typeof SupplierSupplierType)[keyof typeof SupplierSupplierType];

export const SupplierSupplierType = {
  aftermarket: 'aftermarket',
  tire: 'tire',
} as const;

export type Supplier = {
  id: string;
  shopId: string;
  name: string;
  contactEmail: string | null;
  contactPhone: string | null;
  address: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  /** Type of supplier */
  supplierType: SupplierSupplierType;
  isActive: boolean;
  partsTechSupplierId: string | null;
  partsTechStoreId: number | null;
  partsTechStoreName: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SupplierListResponse = {
  items: Supplier[];
  /** Total number of suppliers */
  total: number;
};

/**
 * Type of supplier
 */
export type CreateSupplierRequestSupplierType =
  (typeof CreateSupplierRequestSupplierType)[keyof typeof CreateSupplierRequestSupplierType];

export const CreateSupplierRequestSupplierType = {
  aftermarket: 'aftermarket',
  tire: 'tire',
} as const;

export type CreateSupplierRequest = {
  /** @minLength 1 */
  name: string;
  /** Type of supplier */
  supplierType: CreateSupplierRequestSupplierType;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  isActive?: boolean;
  partsTechSupplierId?: string;
};

export type SyncPartsTechSuppliersResponse = {
  /** Number of suppliers synced */
  synced: number;
  /** Sync result message */
  message: string;
};

/**
 * Type of supplier
 */
export type UpdateSupplierRequestSupplierType =
  (typeof UpdateSupplierRequestSupplierType)[keyof typeof UpdateSupplierRequestSupplierType];

export const UpdateSupplierRequestSupplierType = {
  aftermarket: 'aftermarket',
  tire: 'tire',
} as const;

export type UpdateSupplierRequest = {
  /** @minLength 1 */
  name?: string;
  /** Type of supplier */
  supplierType?: UpdateSupplierRequestSupplierType;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  isActive?: boolean;
  partsTechSupplierId?: string;
};

export type TagListResponse = Tag[];

export type CreateTagRequest = {
  /**
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  color?: string;
};

export type UpdateTagRequest = {
  /**
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  color?: string;
};

export type ShopLaborRate = {
  id: string;
  title: string;
  rate: number;
  isDefault: boolean;
};

export type ShopLaborRateListResponse = ShopLaborRate[];

export type CreateShopLaborRateRequest = {
  /** @minLength 1 */
  title: string;
  /** @minimum 0 */
  rate: number;
  isDefault?: boolean;
};

export type UpdateShopLaborRateRequest = {
  /** @minLength 1 */
  title?: string;
  /** @minimum 0 */
  rate?: number;
  isDefault?: boolean;
};

export type ShopGoals = {
  grossProfitEnabled: boolean;
  gpPerHourGoalEnabled: boolean;
  /** @minimum 0 */
  gpPerHourGoal: number;
};

export type UpdateShopGoals = {
  grossProfitEnabled?: boolean;
  gpPerHourGoalEnabled?: boolean;
  /** @minimum 0 */
  gpPerHourGoal?: number;
};

export type LaborOperationItemLaborTimeInterval =
  (typeof LaborOperationItemLaborTimeInterval)[keyof typeof LaborOperationItemLaborTimeInterval];

export const LaborOperationItemLaborTimeInterval = {
  Hours: 'Hours',
  Minutes: 'Minutes',
} as const;

export type LaborOperationItemRequiredSkill = {
  code: string;
  name: string;
  description: string;
} | null;

export type LaborOperationItemQualifiersItem = {
  description: string;
  qualifierId: string;
};

export type LaborOperationItem = {
  id: string;
  name: string;
  hours: number;
  taxonomyId: string | null;
  group: string;
  subGroup: string;
  description: string | null;
  baseLaborTime: number | null;
  allLaborTime: number | null;
  additionalLaborTime: number | null;
  laborTimeInterval: LaborOperationItemLaborTimeInterval;
  requiredSkill: LaborOperationItemRequiredSkill;
  qualifiers: LaborOperationItemQualifiersItem[];
};

export type LaborOperationGroupSubGroupsItem = {
  name: string;
  items: LaborOperationItem[];
};

export type LaborOperationGroup = {
  name: string;
  subGroups: LaborOperationGroupSubGroupsItem[];
};

export type LaborOperationsResponse = {
  groups: LaborOperationGroup[];
  items: LaborOperationItem[];
};

export type LaborDetailApplicationItemsItemLaborTimeInterval =
  (typeof LaborDetailApplicationItemsItemLaborTimeInterval)[keyof typeof LaborDetailApplicationItemsItemLaborTimeInterval];

export const LaborDetailApplicationItemsItemLaborTimeInterval = {
  Hours: 'Hours',
  Minutes: 'Minutes',
} as const;

export type LaborDetailApplicationQualifiersItem = {
  description: string;
  qualifierId: string;
};

export type LaborDetailApplicationItemsItemRequiredSkill = {
  code: string;
  name: string;
  description: string;
} | null;

export type LaborDetailApplicationItemsItemNotesItem = {
  text: string;
};

export type LaborDetailApplicationItemsItem = {
  baseLaborTime: number;
  allLaborTime: number | null;
  additionalLaborTime: number | null;
  laborTimeInterval: LaborDetailApplicationItemsItemLaborTimeInterval;
  requiredSkill: LaborDetailApplicationItemsItemRequiredSkill;
  serviceType: string | null;
  notes: LaborDetailApplicationItemsItemNotesItem[];
};

export type LaborDetailApplication = {
  applicationId: string;
  displayName: string;
  engineDescription: string | null;
  qualifiers: LaborDetailApplicationQualifiersItem[];
  items: LaborDetailApplicationItemsItem[];
};

export type LaborDetailsResponse = {
  applications: LaborDetailApplication[];
};

export type InvoiceListStats = {
  /** Total number of invoices across all statuses */
  totalCount: number;
  /** Sum of amount_total for open and partially_paid invoices */
  outstandingAmount: number;
  /** Sum of amount_total for paid invoices */
  collectedAmount: number;
  /** Number of draft invoices */
  draftCount: number;
};

export type InvoiceListResponse = {
  /** List of items for the current page */
  items: Invoice[];
  /** Total number of items across all pages */
  total: number;
};

/**
 * Payment method type
 */
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const PaymentMethod = {
  cash: 'cash',
  check: 'check',
  external_terminal: 'external_terminal',
  stripe_checkout: 'stripe_checkout',
  other: 'other',
} as const;

/**
 * Payment status
 */
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const PaymentStatus = {
  succeeded: 'succeeded',
  refunded: 'refunded',
  partially_refunded: 'partially_refunded',
  disputed: 'disputed',
} as const;

/**
 * Stripe dispute status
 */
export type PaymentDisputeStatus = (typeof PaymentDisputeStatus)[keyof typeof PaymentDisputeStatus] | null;

export const PaymentDisputeStatus = {
  needs_response: 'needs_response',
  under_review: 'under_review',
  won: 'won',
  lost: 'lost',
} as const;

export type Payment = {
  id: string;
  amount: number;
  /** Payment method type */
  method: PaymentMethod;
  /** Payment status */
  status: PaymentStatus;
  notes: string | null;
  stripeChargeId: string | null;
  stripePaymentIntentId: string | null;
  stripeDisputeId: string | null;
  disputeReason: string | null;
  /** Stripe dispute status */
  disputeStatus: PaymentDisputeStatus;
  refundedAmount: number;
  createdAt: string;
};

/**
 * Type of ledger entry
 */
export type LedgerEntryEntryType = (typeof LedgerEntryEntryType)[keyof typeof LedgerEntryEntryType];

export const LedgerEntryEntryType = {
  invoice_posted: 'invoice_posted',
  payment_captured: 'payment_captured',
  refund_issued: 'refund_issued',
  void_reversal: 'void_reversal',
  write_off: 'write_off',
  chargeback: 'chargeback',
  adjustment: 'adjustment',
} as const;

export type LedgerEntry = {
  id: string;
  /** Type of ledger entry */
  entryType: LedgerEntryEntryType;
  debitAmount: number;
  creditAmount: number;
  description: string | null;
  createdAt: string;
};

export type InvoiceShopBlock = {
  name: string | null;
  address: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  phone: string | null;
  email: string | null;
  businessRegistrationNumber: string | null;
} | null;

export type InvoiceDetail = Invoice & {
  balanceDue: number;
  lineItems: LineItem[];
  payments: Payment[];
  ledgerEntries: LedgerEntry[];
  shop: InvoiceShopBlock | null;
  /** Repair order number (e.g. "000007") of the linked RO, when one exists. */
  repairOrderNumber: string | null;
};

export type LineItemInputType = (typeof LineItemInputType)[keyof typeof LineItemInputType] | null;

export const LineItemInputType = {
  labor: 'labor',
  parts: 'parts',
  tires: 'tires',
  fee: 'fee',
  sublet: 'sublet',
} as const;

export type LineItemInput = {
  /** @minLength 1 */
  description: string;
  /** @minimum 0 */
  quantity?: number;
  /** @minimum 0 */
  unitPrice: number;
  /** @minimum 0 */
  amount: number;
  type?: LineItemInputType;
};

export type CreateInvoiceRequest = {
  /** Customer UUID */
  customerId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null | '' | unknown;
  /** Repair order UUID */
  repairOrderId?: string | null;
  notes?: string | null;
  /** ISO date */
  dueDate?: string | null;
  /** @minimum 0 */
  amountTax?: number;
  /** @minimum 0 */
  amountFet?: number;
  /** @minItems 1 */
  lineItems: LineItemInput[];
};

export type UpdateInvoiceRequest = {
  customerName?: string | null;
  customerEmail?: string | null | '' | unknown;
  notes?: string | null;
  dueDate?: string | null;
  /** @minimum 0 */
  amountTax?: number;
  /** @minimum 0 */
  amountFet?: number;
  lineItems?: LineItemInput[];
};

/**
 * Indicates the send was queued, not delivered immediately
 */
export type SendInvoiceScheduledResponseStatus =
  (typeof SendInvoiceScheduledResponseStatus)[keyof typeof SendInvoiceScheduledResponseStatus];

export const SendInvoiceScheduledResponseStatus = {
  scheduled: 'scheduled',
} as const;

export type SendInvoiceScheduledResponse = {
  /** Indicates the send was queued, not delivered immediately */
  status: SendInvoiceScheduledResponseStatus;
  /** ISO-8601 timestamp at which the SMS will be delivered (next allowable send time per quiet-hours rules) */
  scheduledFor: string;
};

/**
 * Stable discriminator for the frontend
 */
export type SendInvoiceNeedsOptInResponseCode =
  (typeof SendInvoiceNeedsOptInResponseCode)[keyof typeof SendInvoiceNeedsOptInResponseCode];

export const SendInvoiceNeedsOptInResponseCode = {
  needs_optin: 'needs_optin',
} as const;

/**
 * `never` when no consent row exists; `opted_out` when the most recent row is opted_out
 */
export type SendInvoiceNeedsOptInResponseLastConsentStatus =
  (typeof SendInvoiceNeedsOptInResponseLastConsentStatus)[keyof typeof SendInvoiceNeedsOptInResponseLastConsentStatus];

export const SendInvoiceNeedsOptInResponseLastConsentStatus = {
  never: 'never',
  opted_out: 'opted_out',
} as const;

export type SendInvoiceNeedsOptInResponse = {
  /** Human-readable explanation */
  error: string;
  /** Stable discriminator for the frontend */
  code: SendInvoiceNeedsOptInResponseCode;
  /** `never` when no consent row exists; `opted_out` when the most recent row is opted_out */
  lastConsentStatus: SendInvoiceNeedsOptInResponseLastConsentStatus;
};

/**
 * Delivery channel. Defaults to 'email' when omitted.
 */
export type SendInvoiceBodyChannel = (typeof SendInvoiceBodyChannel)[keyof typeof SendInvoiceBodyChannel];

export const SendInvoiceBodyChannel = {
  email: 'email',
  sms: 'sms',
} as const;

export type SendInvoiceBody = {
  /** Whether the sent invoice should carry a Stripe pay link. Silently ignored for shops without Stripe connected. Defaults to the shop behavior (pay link included when Stripe is connected). */
  includePaymentLink?: boolean;
  /** Delivery channel. Defaults to 'email' when omitted. */
  channel?: SendInvoiceBodyChannel;
  /** Recipient phone number. Required when channel='sms'. */
  phone?: string;
  /**
   * Optional shop-edited message body. Used as the SMS body or appended to the email.
   * @maxLength 1000
   */
  message?: string;
};

/**
 * Payment method type
 */
export type RecordPaymentRequestMethod = (typeof RecordPaymentRequestMethod)[keyof typeof RecordPaymentRequestMethod];

export const RecordPaymentRequestMethod = {
  cash: 'cash',
  check: 'check',
  external_terminal: 'external_terminal',
  stripe_checkout: 'stripe_checkout',
  other: 'other',
} as const;

export type RecordPaymentRequest = {
  /**
   * @minimum 0
   * @exclusiveMinimum true
   */
  amount: number;
  /** Payment method type */
  method: RecordPaymentRequestMethod;
  notes?: string | null;
};

export type RefundPaymentRequest = {
  /**
   * @minimum 0
   * @exclusiveMinimum true
   */
  amount: number;
  reason?: string;
};

/**
 * Type of provisioned phone number, or null if none
 */
export type MessagingConfigResponseNumberType =
  | (typeof MessagingConfigResponseNumberType)[keyof typeof MessagingConfigResponseNumberType]
  | null;

export const MessagingConfigResponseNumberType = {
  toll_free: 'toll_free',
  two_way: 'two_way',
} as const;

export type MessagingConfigResponse = {
  hasPhoneNumber: boolean;
  /** Type of provisioned phone number, or null if none */
  numberType: MessagingConfigResponseNumberType;
  phoneNumber: string | null;
};

/**
 * Current Twilio messaging setup status
 */
export type TwilioSetupStatusResponseStatus =
  (typeof TwilioSetupStatusResponseStatus)[keyof typeof TwilioSetupStatusResponseStatus];

export const TwilioSetupStatusResponseStatus = {
  not_started: 'not_started',
  subaccount_created: 'subaccount_created',
  customer_profile_submitted: 'customer_profile_submitted',
  customer_profile_approved: 'customer_profile_approved',
  trust_product_submitted: 'trust_product_submitted',
  trust_product_approved: 'trust_product_approved',
  brand_submitted: 'brand_submitted',
  brand_approved: 'brand_approved',
  messaging_service_created: 'messaging_service_created',
  campaign_submitted: 'campaign_submitted',
  campaign_verified: 'campaign_verified',
  phone_purchased: 'phone_purchased',
  phone_a2p_pending: 'phone_a2p_pending',
  ready: 'ready',
  failed: 'failed',
} as const;

export type TwilioSetupStatusResponse = {
  /** Current Twilio messaging setup status */
  status: TwilioSetupStatusResponseStatus;
  hasPhoneNumber: boolean;
  phoneNumber: string | null;
  brandStatus: string | null;
  brandFailureReason: string | null;
  messagingServiceSid: string | null;
  customerProfileStatus: string | null;
  customerProfileFailureReason: string | null;
  customerProfileSubmittedAt: string | null;
  trustProductStatus: string | null;
  trustProductFailureReason: string | null;
  campaignStatus: string | null;
  campaignFailureReason: string | null;
  campaignSubmittedAt: string | null;
  phoneA2pStatus: string | null;
  phoneA2pFailureReason: string | null;
  errorMessage: string | null;
};

/**
 * Business entity type for A2P brand registration
 */
export type InitializeTwilioSetupRequestBusinessEntityType =
  (typeof InitializeTwilioSetupRequestBusinessEntityType)[keyof typeof InitializeTwilioSetupRequestBusinessEntityType];

export const InitializeTwilioSetupRequestBusinessEntityType = {
  PARTNERSHIP: 'PARTNERSHIP',
  LLC: 'LLC',
  CORPORATION: 'CORPORATION',
  NON_PROFIT: 'NON_PROFIT',
} as const;

/**
 * Authorized representative position level for Twilio Trust Hub
 */
export type InitializeTwilioSetupRequestAuthorizedRepJobPosition =
  (typeof InitializeTwilioSetupRequestAuthorizedRepJobPosition)[keyof typeof InitializeTwilioSetupRequestAuthorizedRepJobPosition];

export const InitializeTwilioSetupRequestAuthorizedRepJobPosition = {
  CEO: 'CEO',
  CFO: 'CFO',
  VP: 'VP',
  Director: 'Director',
  GM: 'GM',
  General_Counsel: 'General Counsel',
  Other: 'Other',
} as const;

export type InitializeTwilioSetupRequest = {
  /**
   * Federal Employer Identification Number
   * @pattern ^\d{2}-?\d{7}$
   */
  ein: string;
  /** Business entity type for A2P brand registration */
  businessEntityType: InitializeTwilioSetupRequestBusinessEntityType;
  /**
   * Authorized representative first name
   * @minLength 1
   */
  authorizedRepFirstName: string;
  /**
   * Authorized representative last name
   * @minLength 1
   */
  authorizedRepLastName: string;
  /** Authorized representative email */
  authorizedRepEmail: string;
  /**
   * Authorized representative phone
   * @minLength 10
   */
  authorizedRepPhone: string;
  /**
   * Authorized representative job title
   * @minLength 1
   */
  authorizedRepTitle: string;
  /**
   * Legal business name as registered with the IRS (falls back to shop name if not provided)
   * @minLength 1
   */
  legalBusinessName?: string;
  /**
   * Business street address
   * @minLength 1
   */
  address?: string;
  /**
   * Business city
   * @minLength 1
   */
  city?: string;
  /**
   * Two-letter state code
   * @minLength 2
   * @maxLength 2
   */
  state?: string;
  /**
   * ZIP code
   * @minLength 5
   */
  zipCode?: string;
  /** Business website URL */
  website?: string;
  /** Authorized representative position level for Twilio Trust Hub */
  authorizedRepJobPosition?: InitializeTwilioSetupRequestAuthorizedRepJobPosition;
};

export type AvailablePhoneNumber = {
  phoneNumber: string;
  friendlyName: string;
  capabilities: string[];
  monthlyPrice: string;
};

export type SearchPhoneNumbersResponse = {
  numbers: AvailablePhoneNumber[];
};

export type SearchPhoneNumbersRequest = {
  /**
   * US area code to search for available numbers
   * @minLength 3
   * @maxLength 3
   * @pattern ^\d{3}$
   */
  areaCode?: string;
  /**
   * ZIP code to find local numbers
   * @minLength 5
   * @maxLength 10
   */
  postalCode?: string;
  /**
   * Pattern to match in phone number (digits or letters)
   * @minLength 2
   * @maxLength 16
   */
  contains?: string;
};

export type ProvisionPhoneNumberRequest = {
  /**
   * E.164 formatted phone number to provision
   * @minLength 10
   */
  phoneNumber: string;
};

export type SendSmsMessageResponse = {
  messageSid: string;
};

export type SendSmsMessageRequest = {
  conversationId: string;
  /**
   * @minLength 1
   * @maxLength 1600
   */
  message: string;
};

export type SmsRegistrationStatus = {
  /** A2P brand registration status, or null when the shop has no subaccount */
  brandRegistrationStatus: string | null;
  /** Phone A2P registration status, or null when the shop has no subaccount */
  phoneA2pRegistrationStatus: string | null;
  /** True when the shop has started subaccount provisioning at all */
  hasProvisionedNumber: boolean;
  /** True when both registration statuses are approved AND an active phone number exists */
  isReady: boolean;
  /** True when provisioned but not yet ready — the banner condition */
  isPending: boolean;
};

export type GetQuickBooksAuthUrlResponse = {
  /** QuickBooks OAuth authorization URL */
  authUrl: string;
};

/**
 * Lifecycle status of the organization-level QuickBooks connection
 */
export type NullableQuickBooksConnectionStatus =
  (typeof NullableQuickBooksConnectionStatus)[keyof typeof NullableQuickBooksConnectionStatus];

export const NullableQuickBooksConnectionStatus = {
  connected: 'connected',
  disconnected: 'disconnected',
  needs_reauth: 'needs_reauth',
} as const;

export type QuickBooksSyncLog = {
  id: string;
  entityType: string;
  entityId: string;
  operation: string;
  /** QuickBooks id of the pushed entity */
  qbEntityId: string | null;
  syncedAt: string;
};

export type NullableQuickBooksConnection = {
  id: string;
  /** Lifecycle status of the organization-level QuickBooks connection */
  status: NullableQuickBooksConnectionStatus;
  realmId: string;
  companyName: string | null;
  setupCompleted: boolean;
  /** True when setup completed without the Bank step and a shop has since connected Stripe — the integrations card surfaces a finish-the-bank-mapping prompt; Stripe-payment syncs hold in blocked until the mapping is committed */
  stripeBankMappingNeeded: boolean;
  lastSyncAt: string | null;
  /**
   * Count of terminally failed sync rows for the organization — the integrations card's health summary. Blocked rows (Stripe work parked on a missing bank mapping) are not failures and are excluded.
   * @minimum 0
   */
  failedSyncCount: number;
  disconnectedAt: string | null;
  createdAt: string;
  syncLogs: QuickBooksSyncLog[];
} | null;

export type QuickBooksFailedSync = {
  /** The sync-queue row id — stable key for the drill-in list */
  id: string;
  /** invoice | payment | refund */
  entityType: string;
  operation: string;
  /** Plain-English failure reason, translated from the QuickBooks fault when the row failed — never a raw error code */
  reason: string;
  /** The invoice this failure links to (where the per-invoice retry lives); null when it can no longer be resolved */
  invoiceId: string | null;
  /** Invoice number for the drill-in link label */
  invoiceNumber: string | null;
  /** When the row reached its terminal failed state */
  failedAt: string;
};

export type QuickBooksFailedSyncListResponse = {
  failures: QuickBooksFailedSync[];
};

export type QuickBooksPlan = {
  /** Detected QuickBooks plan name */
  name: string | null;
  /** False below QuickBooks Online Plus — setup is hard-blocked */
  supported: boolean;
};

/**
 * TorqueTitan revenue/liability bucket a QuickBooks account or item maps to
 */
export type QuickBooksAccountProposalBucket =
  (typeof QuickBooksAccountProposalBucket)[keyof typeof QuickBooksAccountProposalBucket];

export const QuickBooksAccountProposalBucket = {
  parts: 'parts',
  labor: 'labor',
  tires: 'tires',
  shop_supplies: 'shop_supplies',
  sublet: 'sublet',
  discount: 'discount',
  sales_tax: 'sales_tax',
  fet: 'fet',
  bad_debt: 'bad_debt',
} as const;

export type QuickBooksAccountProposalAction =
  (typeof QuickBooksAccountProposalAction)[keyof typeof QuickBooksAccountProposalAction];

export const QuickBooksAccountProposalAction = {
  reuse: 'reuse',
  create: 'create',
} as const;

export type QuickBooksAccountProposal = {
  /** TorqueTitan revenue/liability bucket a QuickBooks account or item maps to */
  bucket: QuickBooksAccountProposalBucket;
  /** Tax jurisdiction for per-state rows; null for the default row */
  jurisdiction: string | null;
  action: QuickBooksAccountProposalAction;
  /** Matched QuickBooks account id when action is reuse */
  accountId: string | null;
  /** Matched account name, or the conventional name that will be created */
  accountName: string;
  /** QuickBooks AccountType required for this bucket */
  accountType: string;
};

/**
 * TorqueTitan revenue/liability bucket a QuickBooks account or item maps to
 */
export type QuickBooksItemProposalBucket =
  (typeof QuickBooksItemProposalBucket)[keyof typeof QuickBooksItemProposalBucket];

export const QuickBooksItemProposalBucket = {
  parts: 'parts',
  labor: 'labor',
  tires: 'tires',
  shop_supplies: 'shop_supplies',
  sublet: 'sublet',
  discount: 'discount',
  sales_tax: 'sales_tax',
  fet: 'fet',
  bad_debt: 'bad_debt',
} as const;

export type QuickBooksItemProposalAction =
  (typeof QuickBooksItemProposalAction)[keyof typeof QuickBooksItemProposalAction];

export const QuickBooksItemProposalAction = {
  reuse: 'reuse',
  create: 'create',
} as const;

export type QuickBooksItemProposal = {
  /** TorqueTitan revenue/liability bucket a QuickBooks account or item maps to */
  bucket: QuickBooksItemProposalBucket;
  action: QuickBooksItemProposalAction;
  itemId: string | null;
  itemName: string;
};

export type QuickBooksChartAccount = {
  id: string;
  name: string;
  accountType: string;
};

export type QuickBooksShopClassProposalAction =
  (typeof QuickBooksShopClassProposalAction)[keyof typeof QuickBooksShopClassProposalAction];

export const QuickBooksShopClassProposalAction = {
  reuse: 'reuse',
  create: 'create',
} as const;

export type QuickBooksShopClassProposal = {
  shopId: string;
  shopName: string;
  action: QuickBooksShopClassProposalAction;
  /** Matched QuickBooks Class id when action is reuse; null when one will be created */
  classId: string | null;
  /** Matched class name, or the shop name a new class will be created with */
  className: string;
};

export type QuickBooksClass = {
  id: string;
  name: string;
};

export type QuickBooksClassPreflight = {
  /** True for multi-shop organizations (more than one shop) — the wizard renders the Classes step only then */
  applicable: boolean;
  /** Per-shop class match proposals; empty for single-shop organizations */
  proposals: QuickBooksShopClassProposal[];
  /** The company's existing Classes, for the Classes step's per-shop override; empty when not applicable */
  availableClasses: QuickBooksClass[];
};

export type QuickBooksBankAccount = {
  id: string;
  name: string;
};

export type QuickBooksStripePreflight = {
  /** True when the organization has at least one shop with completed Stripe Connect onboarding — the wizard renders the Bank step only then */
  applicable: boolean;
  /** The QuickBooks file's real Bank-type accounts. The Bank step is choose-only from this list — a payout bank account is never auto-created */
  bankAccounts: QuickBooksBankAccount[];
  /** QuickBooks account id of the currently mapped payout bank, for reconfigure prefill; null when unmapped */
  payoutBankAccountId: string | null;
};

export type QuickBooksSetupPreflightResponse = {
  plan: QuickBooksPlan;
  /** Plain-English explanation when the QuickBooks plan blocks setup; null otherwise */
  blockedReason: string | null;
  accounts: QuickBooksAccountProposal[];
  items: QuickBooksItemProposal[];
  /** The pulled chart of accounts, for the wizard's Customize overrides */
  chartOfAccounts: QuickBooksChartAccount[];
  classes: QuickBooksClassPreflight;
  stripe: QuickBooksStripePreflight;
};

export type QuickBooksAccountMapping = {
  bucket: string;
  jurisdiction: string | null;
  accountId: string;
  accountName: string;
};

export type QuickBooksItemMapping = {
  bucket: string;
  itemId: string;
  itemName: string;
  accountId: string;
};

export type QuickBooksShopClassMapping = {
  shopId: string;
  classId: string;
  className: string;
};

export type QuickBooksSetupMappingsResponse = {
  accounts: QuickBooksAccountMapping[];
  items: QuickBooksItemMapping[];
  classes: QuickBooksShopClassMapping[];
};

/**
 * TorqueTitan revenue/liability bucket a QuickBooks account or item maps to
 */
export type QuickBooksAccountSelectionBucket =
  (typeof QuickBooksAccountSelectionBucket)[keyof typeof QuickBooksAccountSelectionBucket];

export const QuickBooksAccountSelectionBucket = {
  parts: 'parts',
  labor: 'labor',
  tires: 'tires',
  shop_supplies: 'shop_supplies',
  sublet: 'sublet',
  discount: 'discount',
  sales_tax: 'sales_tax',
  fet: 'fet',
  bad_debt: 'bad_debt',
} as const;

export type QuickBooksAccountSelection = {
  /** TorqueTitan revenue/liability bucket a QuickBooks account or item maps to */
  bucket: QuickBooksAccountSelectionBucket;
  /**
   * Tax jurisdiction for per-state rows; omit or null for the default row
   * @minLength 1
   * @maxLength 100
   */
  jurisdiction?: string | null;
  /** Existing QuickBooks account to reuse; null to create one named accountName */
  accountId: string | null;
  /**
   * @minLength 1
   * @maxLength 100
   */
  accountName: string;
};

/**
 * TorqueTitan revenue/liability bucket a QuickBooks account or item maps to
 */
export type QuickBooksItemSelectionBucket =
  (typeof QuickBooksItemSelectionBucket)[keyof typeof QuickBooksItemSelectionBucket];

export const QuickBooksItemSelectionBucket = {
  parts: 'parts',
  labor: 'labor',
  tires: 'tires',
  shop_supplies: 'shop_supplies',
  sublet: 'sublet',
  discount: 'discount',
  sales_tax: 'sales_tax',
  fet: 'fet',
  bad_debt: 'bad_debt',
} as const;

export type QuickBooksItemSelection = {
  /** TorqueTitan revenue/liability bucket a QuickBooks account or item maps to */
  bucket: QuickBooksItemSelectionBucket;
  /** Existing QuickBooks item to reuse; null to create one named itemName */
  itemId: string | null;
  /**
   * @minLength 1
   * @maxLength 100
   */
  itemName: string;
};

export type QuickBooksClassSelection = {
  shopId: string;
  /** Existing QuickBooks Class to reuse; null to create one named className */
  classId: string | null;
  /**
   * @minLength 1
   * @maxLength 100
   */
  className: string;
};

export type CommitQuickBooksSetupRequest = {
  /** @minItems 1 */
  accounts: QuickBooksAccountSelection[];
  /** @minItems 1 */
  items: QuickBooksItemSelection[];
  /** Per-shop class selections (multi-shop orgs); omit or empty for single-shop organizations */
  classes?: QuickBooksClassSelection[];
};

export type CommitQuickBooksBankMappingRequest = {
  /**
   * QuickBooks account id of the real bank account where Stripe payouts land — choose-only from the file's existing Bank-type accounts, never created
   * @minLength 1
   */
  bankAccountId: string;
};

/**
 * Derived from the sync queue and the cached QuickBooks id: syncing while work is queued or in flight, failed when the last push failed terminally, synced once the invoice exists in QuickBooks, not_synced when it never reached QuickBooks (e.g. posted while disconnected)
 */
export type QuickBooksInvoiceSyncStatusStatus =
  (typeof QuickBooksInvoiceSyncStatusStatus)[keyof typeof QuickBooksInvoiceSyncStatusStatus];

export const QuickBooksInvoiceSyncStatusStatus = {
  synced: 'synced',
  syncing: 'syncing',
  failed: 'failed',
  not_synced: 'not_synced',
} as const;

export type QuickBooksInvoiceSyncStatus = {
  /** Derived from the sync queue and the cached QuickBooks id: syncing while work is queued or in flight, failed when the last push failed terminally, synced once the invoice exists in QuickBooks, not_synced when it never reached QuickBooks (e.g. posted while disconnected) */
  status: QuickBooksInvoiceSyncStatusStatus;
  /** Plain-English explanation of the terminal failure — only populated when status is failed */
  lastError: string | null;
};

export type QuickBooksCustomerSearchResult = {
  /** QuickBooks customer id */
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
};

export type SearchQuickBooksCustomersResponse = {
  customers: QuickBooksCustomerSearchResult[];
};

export type RelinkQuickBooksCustomerResponse = {
  customerId: string;
  quickbooksCustomerId: string;
};

export type RelinkQuickBooksCustomerBody = {
  /**
   * Chosen QuickBooks customer id
   * @minLength 1
   */
  quickbooksCustomerId: string;
};

export type InspectionStatus = (typeof InspectionStatus)[keyof typeof InspectionStatus];

export const InspectionStatus = {
  in_progress: 'in_progress',
  completed: 'completed',
  reviewed: 'reviewed',
} as const;

export type InspectionOverallCondition =
  | (typeof InspectionOverallCondition)[keyof typeof InspectionOverallCondition]
  | null;

export const InspectionOverallCondition = {
  good: 'good',
  attention: 'attention',
  critical: 'critical',
} as const;

export type Inspection = {
  id: string;
  inspectionDate: string;
  status: InspectionStatus;
  mileage: number | null;
  overallCondition: InspectionOverallCondition;
  repairOrderId: string | null;
};

export type InspectionListResponse = Inspection[];

export type InspectionDetailStatus = (typeof InspectionDetailStatus)[keyof typeof InspectionDetailStatus];

export const InspectionDetailStatus = {
  in_progress: 'in_progress',
  completed: 'completed',
  reviewed: 'reviewed',
} as const;

export type InspectionDetail = {
  id: string;
  vehicleId: string;
  repairOrderId: string | null;
  inspectorUserId: string;
  templateId: string | null;
  status: InspectionDetailStatus;
  mileage: number | null;
  notes: string | null;
  customerSummary: string | null;
  isShared: boolean | null;
  shareToken: string | null;
  shareExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  categories: InspectionCategoryDetail[];
};

export type CreateInspectionRequest = {
  vehicleId: string;
  repairOrderId?: string | null;
  templateId?: string | null;
  mileage?: number | null;
  notes?: string | null;
};

export type InspectionResult = {
  id: string;
  inspectionId: string;
  inspectionItemId: string;
  status: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UpsertInspectionResultRequestStatus =
  | (typeof UpsertInspectionResultRequestStatus)[keyof typeof UpsertInspectionResultRequestStatus]
  | null;

export const UpsertInspectionResultRequestStatus = {
  good: 'good',
  attention: 'attention',
  replace: 'replace',
} as const;

export type UpsertInspectionResultRequest = {
  inspectionItemId: string;
  status?: UpsertInspectionResultRequestStatus;
  notes?: string | null;
};

export type InspectionItem = {
  id: string;
  categoryId: string;
  name: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  templateId: string | null;
};

export type CreateInspectionItemRequest = {
  categoryId: string;
  /** @minLength 1 */
  name: string;
  description?: string | null;
  displayOrder?: number;
  templateId?: string | null;
};

export type CreateAttachmentRequestFileType =
  (typeof CreateAttachmentRequestFileType)[keyof typeof CreateAttachmentRequestFileType];

export const CreateAttachmentRequestFileType = {
  image: 'image',
  video: 'video',
} as const;

export type CreateAttachmentRequest = {
  inspectionItemId: string;
  /** @minLength 1 */
  filePath: string;
  /** @minLength 1 */
  fileName: string;
  fileType: CreateAttachmentRequestFileType;
  /**
   * @minimum 0
   * @exclusiveMinimum true
   */
  fileSize: number;
};

export type SharingResponse = {
  id: string;
  isShared: boolean | null;
  shareToken: string | null;
  shareExpiresAt: string | null;
};

export type UpdateSharingRequest = {
  isShared: false;
};

export type ShareInspectionResponse = {
  shareToken: string;
};

export type ShareInspectionRequest = {
  /**
   * @minimum 1
   * @maximum 365
   */
  expirationDays?: number;
};

export type SeedDefaultInspectionItemsResponse = {
  categoriesCreated: number;
  itemsCreated: number;
};

export type SeedInspectionItem = {
  /** @minLength 1 */
  name: string;
  description?: string | null;
  displayOrder?: number;
};

export type SeedCategory = {
  /** @minLength 1 */
  name: string;
  description?: string | null;
  displayOrder?: number;
  items: SeedInspectionItem[];
};

export type SeedDefaultInspectionItemsRequest = {
  categories: SeedCategory[];
};

export type GenerateCustomerSummaryResponse = {
  /** AI-generated customer-friendly summary in markdown format */
  summary: string;
};

export type GenerateCustomerSummaryRequest = {
  /** Inspection ID to fetch vehicle info for context */
  inspectionId?: string;
  /**
   * Formatted inspection results, e.g. - Attention - Brakes - Pads - 4mm
   * @minItems 1
   */
  inspectionResults: string[];
};

export type DviStatus = (typeof DviStatus)[keyof typeof DviStatus];

export const DviStatus = {
  in_progress: 'in_progress',
  completed: 'completed',
  reviewed: 'reviewed',
} as const;

export type UpdateDviStatusResponse = {
  id: string;
  status: DviStatus;
  /** Whether the linked repair order workflow was advanced */
  workflowAdvanced: boolean;
};

export type UpdateDviStatusRequest = {
  status: DviStatus & unknown;
};

export type UpdateInspectionRequestStatus =
  (typeof UpdateInspectionRequestStatus)[keyof typeof UpdateInspectionRequestStatus];

export const UpdateInspectionRequestStatus = {
  in_progress: 'in_progress',
  completed: 'completed',
  reviewed: 'reviewed',
} as const;

export type UpdateInspectionRequestOverallCondition =
  | (typeof UpdateInspectionRequestOverallCondition)[keyof typeof UpdateInspectionRequestOverallCondition]
  | null;

export const UpdateInspectionRequestOverallCondition = {
  good: 'good',
  attention: 'attention',
  critical: 'critical',
} as const;

export type UpdateInspectionRequest = {
  status?: UpdateInspectionRequestStatus;
  overallCondition?: UpdateInspectionRequestOverallCondition;
  notes?: string | null;
  customerSummary?: string | null;
  mileage?: number | null;
};

export type InspectionTemplateListItem = {
  id: string;
  name: string;
  description: string | null;
  isSystemDefault: boolean;
  isDefaultForShop: boolean;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type InspectionTemplateListResponse = InspectionTemplateListItem[];

export type TemplateItem = {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
};

export type TemplateCategory = {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  items: TemplateItem[];
};

export type InspectionTemplateDetail = InspectionTemplateListItem & {
  categories: TemplateCategory[];
};

export type CreateInspectionTemplateCategoriesItemItemsItem = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string;
  /** @maxLength 1000 */
  description?: string | null;
  /** @minimum 0 */
  displayOrder: number;
};

export type CreateInspectionTemplateCategoriesItem = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string;
  /** @maxLength 1000 */
  description?: string | null;
  /** @minimum 0 */
  displayOrder: number;
  items: CreateInspectionTemplateCategoriesItemItemsItem[];
};

export type CreateInspectionTemplate = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string;
  /** @maxLength 1000 */
  description?: string | null;
  categories: CreateInspectionTemplateCategoriesItem[];
};

export type UpdateInspectionTemplateCategoriesItemItemsItem = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string;
  /** @maxLength 1000 */
  description?: string | null;
  /** @minimum 0 */
  displayOrder: number;
};

export type UpdateInspectionTemplateCategoriesItem = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string;
  /** @maxLength 1000 */
  description?: string | null;
  /** @minimum 0 */
  displayOrder: number;
  items: UpdateInspectionTemplateCategoriesItemItemsItem[];
};

export type UpdateInspectionTemplate = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name?: string;
  /** @maxLength 1000 */
  description?: string | null;
  categories?: UpdateInspectionTemplateCategoriesItem[];
};

/**
 * Whether the matrix applies to general parts or tires
 */
export type PricingMatrixType = (typeof PricingMatrixType)[keyof typeof PricingMatrixType];

export const PricingMatrixType = {
  parts: 'parts',
  tires: 'tires',
} as const;

export type PricingMatrix = {
  /** Pricing matrix UUID */
  id: string;
  /** Pricing matrix name */
  name: string;
  /** Optional description */
  description: string | null;
  /** Whether this is the default matrix for the shop */
  isDefault: boolean;
  /** Whether this matrix is active */
  isEnabled: boolean;
  /** Whether this matrix is a system-managed default */
  isSystemDefault: boolean;
  type: PricingMatrixType;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
};

/**
 * Markup type: percentage or fixed amount
 */
export type PricingMatrixRowMarkupType = (typeof PricingMatrixRowMarkupType)[keyof typeof PricingMatrixRowMarkupType];

export const PricingMatrixRowMarkupType = {
  percentage: 'percentage',
  fixed: 'fixed',
} as const;

export type PricingMatrixRow = {
  /** Row UUID */
  id: string;
  /** Parent pricing matrix UUID */
  matrixId: string;
  /** Minimum price for this range */
  minPrice: number;
  /** Maximum price for this range */
  maxPrice: number;
  /** Markup type: percentage or fixed amount */
  markupType: PricingMatrixRowMarkupType;
  /** Markup value (percentage or fixed amount) */
  markupValue: number;
  /** Multiplier applied to the cost */
  multiplier: number | null;
  /** Gross profit margin as a decimal */
  grossProfitMargin: number | null;
  /** Display order of this row */
  displayOrder: number;
};

export type PricingMatrixCategory = {
  /** Category UUID */
  id: string;
  /** Parent pricing matrix UUID */
  matrixId: string;
  /** PartsTech category ID (null for a part-type-level association) */
  partstechCategoryId: string | null;
  /** PartsTech category name (null for a part-type-level association) */
  partstechCategoryName: string | null;
  /** PartsTech subcategory ID */
  partstechSubcategoryId: string | null;
  /** PartsTech subcategory name */
  partstechSubcategoryName: string | null;
  /** PartsTech part type ID */
  partstechPartTypeId: string | null;
  /** PartsTech part type name */
  partstechPartTypeName: string | null;
  /** Whether this entry applies to the entire category */
  appliesToEntireCategory: boolean;
};

export type PricingMatrixDetail = PricingMatrix & {
  /** Pricing rows for this matrix */
  rows: PricingMatrixRow[];
  /** Category overrides for this matrix */
  categories: PricingMatrixCategory[];
};

export type CreatePricingMatrixRequest = {
  /**
   * Pricing matrix name
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * Optional description
   * @maxLength 500
   */
  description?: string | null;
  /** Whether to set this as the default matrix */
  isDefault?: boolean;
  type: PricingMatrixType;
};

export type CalculateRetailPriceResponse = {
  /** Calculated retail price after applying markup */
  retailPrice: number;
  /** ID of the pricing matrix that was applied, or null if none matched */
  matrixId: string | null;
};

export type CalculateRetailPriceRequest = {
  /** Cost price of the part */
  costPrice: number;
  /** PartsTech part type ID (7636 = Tires) */
  partTypeId?: number;
};

export type UpdatePricingMatrixRequest = {
  /**
   * Pricing matrix name
   * @minLength 1
   * @maxLength 100
   */
  name?: string;
  /**
   * Optional description
   * @maxLength 500
   */
  description?: string | null;
  /** Whether to set this as the default matrix */
  isDefault?: boolean;
  /** Whether this matrix is active */
  isEnabled?: boolean;
};

/**
 * Markup type: percentage or fixed amount
 */
export type SavePricingMatrixRequestRowsItemMarkupType =
  (typeof SavePricingMatrixRequestRowsItemMarkupType)[keyof typeof SavePricingMatrixRequestRowsItemMarkupType];

export const SavePricingMatrixRequestRowsItemMarkupType = {
  percentage: 'percentage',
  fixed: 'fixed',
} as const;

export type SavePricingMatrixRequestRowsItem = {
  /**
   * Minimum price for this range
   * @minimum 0
   */
  minPrice: number;
  /**
   * Maximum price for this range
   * @minimum 0
   */
  maxPrice: number;
  /** Markup type: percentage or fixed amount */
  markupType: SavePricingMatrixRequestRowsItemMarkupType;
  /**
   * Markup value (percentage or fixed amount)
   * @minimum 0
   */
  markupValue: number;
  /** Multiplier applied to the cost */
  multiplier?: number | null;
  /** Gross profit margin as a decimal */
  grossProfitMargin?: number | null;
  /**
   * Display order of this row
   * @minimum 0
   */
  displayOrder: number;
};

export type SavePricingMatrixRequestCategoriesItem = {
  /** PartsTech category ID (omit/null for a part-type-level association) */
  partstechCategoryId?: string | null;
  /** PartsTech category name (omit/null for a part-type-level association) */
  partstechCategoryName?: string | null;
  /** PartsTech subcategory ID */
  partstechSubcategoryId?: string | null;
  /** PartsTech subcategory name */
  partstechSubcategoryName?: string | null;
  /** PartsTech part type ID */
  partstechPartTypeId?: string | null;
  /** PartsTech part type name */
  partstechPartTypeName?: string | null;
  /** @deprecated Server-derived from column presence. Field accepted for backward compatibility but ignored. */
  appliesToEntireCategory?: boolean;
};

export type SavePricingMatrixRequest = {
  /**
   * Pricing matrix name
   * @minLength 1
   * @maxLength 100
   */
  name?: string;
  /**
   * Optional description
   * @maxLength 500
   */
  description?: string | null;
  /**
   * Pricing rows (at least one required)
   * @minItems 1
   */
  rows: SavePricingMatrixRequestRowsItem[];
  /** Category overrides for this matrix */
  categories?: SavePricingMatrixRequestCategoriesItem[];
};

export type FeeAmountType = (typeof FeeAmountType)[keyof typeof FeeAmountType];

export const FeeAmountType = {
  fixed: 'fixed',
  percentage: 'percentage',
} as const;

export type FeeScope = (typeof FeeScope)[keyof typeof FeeScope] | null;

export const FeeScope = {
  parts: 'parts',
  labor: 'labor',
  tires: 'tires',
  parts_and_labor: 'parts_and_labor',
  subtotal: 'subtotal',
} as const;

export type FeePerUnitBasis = (typeof FeePerUnitBasis)[keyof typeof FeePerUnitBasis] | null;

export const FeePerUnitBasis = {
  tire_count: 'tire_count',
} as const;

export type Fee = {
  id: string;
  name: string;
  amountType: FeeAmountType;
  defaultAmount: number;
  scope: FeeScope;
  perUnitBasis: FeePerUnitBasis;
  taxable: boolean;
  autoApply: boolean;
  displayOrder: number;
  isActive: boolean;
};

export type CreateFeeRequestAmountType = (typeof CreateFeeRequestAmountType)[keyof typeof CreateFeeRequestAmountType];

export const CreateFeeRequestAmountType = {
  fixed: 'fixed',
  percentage: 'percentage',
} as const;

export type CreateFeeRequestScope = (typeof CreateFeeRequestScope)[keyof typeof CreateFeeRequestScope] | null;

export const CreateFeeRequestScope = {
  parts: 'parts',
  labor: 'labor',
  tires: 'tires',
  parts_and_labor: 'parts_and_labor',
  subtotal: 'subtotal',
} as const;

export type CreateFeeRequestPerUnitBasis =
  | (typeof CreateFeeRequestPerUnitBasis)[keyof typeof CreateFeeRequestPerUnitBasis]
  | null;

export const CreateFeeRequestPerUnitBasis = {
  tire_count: 'tire_count',
} as const;

export type CreateFeeRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string;
  amountType: CreateFeeRequestAmountType;
  /** @minimum 0 */
  defaultAmount: number;
  scope?: CreateFeeRequestScope;
  perUnitBasis?: CreateFeeRequestPerUnitBasis;
  taxable: boolean;
  autoApply: boolean;
  /** @minimum 0 */
  displayOrder?: number;
};

export type ReorderFeesRequestItemsItem = {
  id: string;
  /** @minimum 0 */
  displayOrder: number;
};

export type ReorderFeesRequest = {
  /** @minItems 1 */
  items: ReorderFeesRequestItemsItem[];
};

export type UpdateFeeRequestAmountType = (typeof UpdateFeeRequestAmountType)[keyof typeof UpdateFeeRequestAmountType];

export const UpdateFeeRequestAmountType = {
  fixed: 'fixed',
  percentage: 'percentage',
} as const;

export type UpdateFeeRequestScope = (typeof UpdateFeeRequestScope)[keyof typeof UpdateFeeRequestScope] | null;

export const UpdateFeeRequestScope = {
  parts: 'parts',
  labor: 'labor',
  tires: 'tires',
  parts_and_labor: 'parts_and_labor',
  subtotal: 'subtotal',
} as const;

export type UpdateFeeRequestPerUnitBasis =
  | (typeof UpdateFeeRequestPerUnitBasis)[keyof typeof UpdateFeeRequestPerUnitBasis]
  | null;

export const UpdateFeeRequestPerUnitBasis = {
  tire_count: 'tire_count',
} as const;

export type UpdateFeeRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name?: string;
  amountType?: UpdateFeeRequestAmountType;
  /** @minimum 0 */
  defaultAmount?: number;
  scope?: UpdateFeeRequestScope;
  perUnitBasis?: UpdateFeeRequestPerUnitBasis;
  taxable?: boolean;
  autoApply?: boolean;
  /** @minimum 0 */
  displayOrder?: number;
  isActive?: boolean;
};

export type SubletVendor = {
  id: string;
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  defaultTaxable: boolean;
  notes: string | null;
  isActive: boolean;
};

export type SubletVendorList = SubletVendor[];

export type CreateSubletVendorRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name: string;
  /** @maxLength 255 */
  contactName?: string | null;
  /** @maxLength 50 */
  phone?: string | null;
  /** @maxLength 255 */
  email?: string | null;
  /** @maxLength 255 */
  addressLine1?: string | null;
  /** @maxLength 255 */
  addressLine2?: string | null;
  /** @maxLength 255 */
  city?: string | null;
  /** @maxLength 50 */
  state?: string | null;
  /** @maxLength 20 */
  zip?: string | null;
  defaultTaxable?: boolean;
  notes?: string | null;
};

export type UpdateSubletVendorRequest = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  name?: string;
  /** @maxLength 255 */
  contactName?: string | null;
  /** @maxLength 50 */
  phone?: string | null;
  /** @maxLength 255 */
  email?: string | null;
  /** @maxLength 255 */
  addressLine1?: string | null;
  /** @maxLength 255 */
  addressLine2?: string | null;
  /** @maxLength 255 */
  city?: string | null;
  /** @maxLength 50 */
  state?: string | null;
  /** @maxLength 20 */
  zip?: string | null;
  defaultTaxable?: boolean;
  notes?: string | null;
  isActive?: boolean;
};

export type PayModel = (typeof PayModel)[keyof typeof PayModel];

export const PayModel = {
  hourly: 'hourly',
  flat_rate: 'flat_rate',
  track_both: 'track_both',
} as const;

export type NullableTimeTrackingSettings = {
  id: string;
  shopId: string;
  allowConcurrentJobClocks: boolean;
  payModel: PayModel;
  warnOnNoTimeClock: boolean;
  requireTimeClockForJobClock: boolean;
  createdAt: string;
  updatedAt: string;
} | null;

export type TimeTrackingSettings = {
  id: string;
  shopId: string;
  allowConcurrentJobClocks: boolean;
  payModel: PayModel;
  warnOnNoTimeClock: boolean;
  requireTimeClockForJobClock: boolean;
  createdAt: string;
  updatedAt: string;
};

export type UpdateTimeTrackingSettingsRequest = {
  allowConcurrentJobClocks?: boolean;
  payModel?: PayModel;
  warnOnNoTimeClock?: boolean;
  requireTimeClockForJobClock?: boolean;
};

export type EntryType = (typeof EntryType)[keyof typeof EntryType];

export const EntryType = {
  attendance: 'attendance',
  job: 'job',
} as const;

export type EntryStatus = (typeof EntryStatus)[keyof typeof EntryStatus];

export const EntryStatus = {
  active: 'active',
  pending_approval: 'pending_approval',
  approved: 'approved',
} as const;

export type TimeEntry = {
  id: string;
  userId: string;
  shopId: string | null;
  entryType: EntryType;
  isBreak: boolean;
  repairOrderId: string | null;
  laborTaskId: string | null;
  clockInTime: string;
  clockOutTime: string | null;
  breakDurationMinutes: number;
  status: EntryStatus;
  payModelSnapshot: PayModel & (unknown | null);
  billedHoursSnapshot: string | null;
  hourlyRateSnapshot: string | null;
  totalHours: string | null;
  notes: string | null;
  pauseReason: string | null;
  pausedAt: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** Human-readable RO number, joined from repair_orders */
  repairOrderNumber: string | null;
  /** Labor task description, joined from repair_order_job_labor */
  laborTaskName: string | null;
  /** Parent job ID (repair_order_jobs.id), joined from repair_order_job_labor */
  repairOrderJobId: string | null;
};

export type JobTimerEntry = TimeEntry;

export type ClockOutAttendanceResponse = {
  entry: TimeEntry;
  pausedJobEntries: JobTimerEntry[];
};

export type NullableTimeEntry = {
  id: string;
  userId: string;
  shopId: string | null;
  entryType: EntryType;
  isBreak: boolean;
  repairOrderId: string | null;
  laborTaskId: string | null;
  clockInTime: string;
  clockOutTime: string | null;
  breakDurationMinutes: number;
  status: EntryStatus;
  payModelSnapshot: PayModel & (unknown | null);
  billedHoursSnapshot: string | null;
  hourlyRateSnapshot: string | null;
  totalHours: string | null;
  notes: string | null;
  pauseReason: string | null;
  pausedAt: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** Human-readable RO number, joined from repair_orders */
  repairOrderNumber: string | null;
  /** Labor task description, joined from repair_order_job_labor */
  laborTaskName: string | null;
  /** Parent job ID (repair_order_jobs.id), joined from repair_order_job_labor */
  repairOrderJobId: string | null;
} | null;

export type ClockInJobResponseWarning = (typeof ClockInJobResponseWarning)[keyof typeof ClockInJobResponseWarning];

export const ClockInJobResponseWarning = {
  no_attendance_clock: 'no_attendance_clock',
} as const;

export type ClockInJobResponse = {
  entry: TimeEntry;
  warning?: ClockInJobResponseWarning;
  taskStatusChanged: boolean;
  roStageChanged: boolean;
};

export type ClockInJobRequest = {
  laborTaskId: string;
  onBehalfOfUserId?: string;
};

export type ClockOutJobResponse = {
  entry: TimeEntry;
};

export type CompletionAction = (typeof CompletionAction)[keyof typeof CompletionAction];

export const CompletionAction = {
  pause: 'pause',
  on_hold: 'on_hold',
  complete: 'complete',
  switch: 'switch',
} as const;

export type ClockOutJobRequest = {
  entryId: string;
  completionAction: CompletionAction;
  notes?: string | null;
};

export type CurrentJobsResponse = TimeEntry[];

export type StartJobTimerResponse = {
  entry: JobTimerEntry;
  autoClockIn: boolean;
};

export type JobTimerResponse = {
  entry: JobTimerEntry;
};

export type ResumeJobTimerResponse = {
  entry: JobTimerEntry;
  autoClockIn: boolean;
};

export type JobTimerStatusEntry = JobTimerEntry & {
  userName: string | null;
  userAvatarUrl: string | null;
};

export type JobTimerStatusResponse = {
  entries: JobTimerStatusEntry[];
};

export type CreateManualEntryRequest = {
  userId: string;
  entryType: EntryType;
  clockInTime: string;
  clockOutTime: string;
  /** @minimum 0 */
  breakDurationMinutes?: number;
  laborTaskId?: string | null;
  repairOrderId?: string | null;
  notes?: string | null;
  /** @minLength 1 */
  reason: string;
};

export type ListTimeEntriesResponse = {
  rows: TimeEntry[];
  total: number;
};

export type UpdateTimeEntryRequest = {
  clockInTime?: string;
  clockOutTime?: string | null;
  /** @minimum 0 */
  breakDurationMinutes?: number;
  notes?: string | null;
};

export type ListPendingApprovalsResponse = {
  rows: TimeEntry[];
};

export type ApproveEntriesResponse = {
  approvedCount: number;
};

export type ApproveEntriesRequest = {
  /** @minItems 1 */
  entryIds: string[];
};

export type UnapproveEntryRequest = {
  /** @minLength 1 */
  reason: string;
};

export type CannedJobJobType = (typeof CannedJobJobType)[keyof typeof CannedJobJobType];

export const CannedJobJobType = {
  repair: 'repair',
  tire: 'tire',
} as const;

export type DefaultPart =
  | {
      partTypeId: number;
      partTypeName: string;
      /** @minimum 1 */
      quantity: number;
    }
  | {
      partType: string;
      /** @minimum 1 */
      quantity: number;
    };

export type DefaultLabor = {
  description: string;
  /** @minimum 0 */
  hours: number;
  skillLevel: string;
};

export type TireConfigDisplayMode = (typeof TireConfigDisplayMode)[keyof typeof TireConfigDisplayMode];

export const TireConfigDisplayMode = {
  option: 'option',
  single: 'single',
} as const;

export type TireConfig = {
  displayMode?: TireConfigDisplayMode;
  services?: string[];
} | null;

export type CannedJobOptionGroupPreset =
  | (typeof CannedJobOptionGroupPreset)[keyof typeof CannedJobOptionGroupPreset]
  | null;

export const CannedJobOptionGroupPreset = {
  gbb: 'gbb',
  numbered: 'numbered',
} as const;

export type CannedJobOptionGroup = {
  id: string;
  label: string;
  preset: CannedJobOptionGroupPreset;
  displayOrder: number;
};

export type CannedJobPartLine = {
  id: string;
  name: string;
  partNumber: string | null;
  brand: string | null;
  model: string | null;
  size: string | null;
  quantity: number;
  unitPrice: number;
  costPrice: number;
  coreCharge: number;
  fet: number;
  isTire: boolean;
  optionGroupId: string | null;
  isSelectedOption: boolean;
  displayOrder: number;
};

export type CannedJobLaborLine = {
  id: string;
  description: string;
  hours: number;
  hourlyRate: number;
  skillLevel: string | null;
  displayOrder: number;
};

export type CannedJobFeeLineAmountType = (typeof CannedJobFeeLineAmountType)[keyof typeof CannedJobFeeLineAmountType];

export const CannedJobFeeLineAmountType = {
  fixed: 'fixed',
  percentage: 'percentage',
} as const;

export type CannedJobFeeLineScope = (typeof CannedJobFeeLineScope)[keyof typeof CannedJobFeeLineScope] | null;

export const CannedJobFeeLineScope = {
  parts: 'parts',
  labor: 'labor',
  tires: 'tires',
  parts_and_labor: 'parts_and_labor',
  subtotal: 'subtotal',
} as const;

export type CannedJobFeeLinePerUnitBasis =
  | (typeof CannedJobFeeLinePerUnitBasis)[keyof typeof CannedJobFeeLinePerUnitBasis]
  | null;

export const CannedJobFeeLinePerUnitBasis = {
  tire_count: 'tire_count',
} as const;

export type CannedJobFeeLine = {
  id: string;
  catalogId: string | null;
  name: string;
  amountType: CannedJobFeeLineAmountType;
  defaultAmount: number;
  scope: CannedJobFeeLineScope;
  perUnitBasis: CannedJobFeeLinePerUnitBasis;
  taxable: boolean;
  displayOrder: number;
};

export type CannedJob = {
  id: string;
  name: string;
  jobType: CannedJobJobType;
  description: string | null;
  category: string | null;
  isActive: boolean;
  defaultParts: DefaultPart[];
  defaultLabor: DefaultLabor[];
  tireConfig: TireConfig | null;
  optionGroups: CannedJobOptionGroup[];
  parts: CannedJobPartLine[];
  labor: CannedJobLaborLine[];
  fees: CannedJobFeeLine[];
  createdAt: string;
  updatedAt: string;
};

export type CannedJobListResponse = CannedJob[];

export type CreateCannedJobRequestJobType =
  (typeof CreateCannedJobRequestJobType)[keyof typeof CreateCannedJobRequestJobType];

export const CreateCannedJobRequestJobType = {
  repair: 'repair',
  tire: 'tire',
} as const;

export type CreateCannedJobRequestDefaultPartsItem = {
  partTypeId: number;
  partTypeName: string;
  /** @minimum 1 */
  quantity: number;
};

export type CreateCannedJobRequest = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  jobType: CreateCannedJobRequestJobType;
  /** @maxLength 1000 */
  description?: string | null;
  /** @maxLength 100 */
  category?: string | null;
  isActive?: boolean;
  defaultParts?: CreateCannedJobRequestDefaultPartsItem[];
  defaultLabor?: DefaultLabor[];
  tireConfig?: TireConfig | null;
};

export type UpdateCannedJobRequestJobType =
  (typeof UpdateCannedJobRequestJobType)[keyof typeof UpdateCannedJobRequestJobType];

export const UpdateCannedJobRequestJobType = {
  repair: 'repair',
  tire: 'tire',
} as const;

export type UpdateCannedJobRequestDefaultPartsItem = {
  partTypeId: number;
  partTypeName: string;
  /** @minimum 1 */
  quantity: number;
};

export type UpdateCannedJobRequest = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
  jobType?: UpdateCannedJobRequestJobType;
  /** @maxLength 1000 */
  description?: string | null;
  /** @maxLength 100 */
  category?: string | null;
  isActive?: boolean;
  defaultParts?: UpdateCannedJobRequestDefaultPartsItem[];
  defaultLabor?: DefaultLabor[];
  tireConfig?: TireConfig | null;
};

/**
 * Job type
 */
export type SuggestedJobType = (typeof SuggestedJobType)[keyof typeof SuggestedJobType];

export const SuggestedJobType = {
  repair: 'repair',
  tire: 'tire',
} as const;

/**
 * Urgency level: immediate (replace items) or recommended (attention items)
 */
export type SuggestedJobUrgency = (typeof SuggestedJobUrgency)[keyof typeof SuggestedJobUrgency];

export const SuggestedJobUrgency = {
  immediate: 'immediate',
  recommended: 'recommended',
} as const;

/**
 * Priority category for sorting
 */
export type SuggestedJobCategory = (typeof SuggestedJobCategory)[keyof typeof SuggestedJobCategory];

export const SuggestedJobCategory = {
  safety: 'safety',
  reliability: 'reliability',
  maintenance: 'maintenance',
  comfort: 'comfort',
} as const;

export type SuggestedJob = {
  /** Job title, e.g. Front Brake Service */
  title: string;
  /** Job type */
  type: SuggestedJobType;
  /** Urgency level: immediate (replace items) or recommended (attention items) */
  urgency: SuggestedJobUrgency;
  /** DVI item names that contributed to this job suggestion */
  sourceItems: string[];
  /** Priority category for sorting */
  category: SuggestedJobCategory;
  /** Matched canned job UUID from the shop's catalog (omitted when no match) */
  cannedJobId?: string;
  /** True when a job with this cannedJobId already exists on the repair order */
  alreadyOnRepairOrder?: boolean;
};

export type GenerateSuggestedJobsResponse = {
  /** Array of AI-generated job suggestions */
  suggestedJobs: SuggestedJob[];
  /** Summary message about the generation result */
  message: string;
  /** Whether these suggestions were retrieved from cache */
  isPreGenerated: boolean;
  /** ID of the openai_request_logs row that produced this set of suggestions. Pass back on bulk-create so decision rows correlate with the prompt run. */
  requestLogId?: string;
};

export type GenerateSuggestedJobsRequest = {
  /** Repair order ID to generate job suggestions for */
  repairOrderId: string;
  /** If true, store results and skip cache check (used for pre-generation on DVI completion) */
  store_results?: boolean;
  /** Specific DVI inspection ID (optional, auto-detected from repair order) */
  dvi_inspection_id?: string;
};

export type PartTypeItem = {
  partTypeId: number;
  partTypeName: string;
  application?: string;
};

export type PartTypeSearchResponse = PartTypeItem[];

/**
 * Connection status
 */
export type SupplierPreferenceStatus = (typeof SupplierPreferenceStatus)[keyof typeof SupplierPreferenceStatus];

export const SupplierPreferenceStatus = {
  Processing: 'Processing',
  Approved: 'Approved',
  Not_Approved: 'Not Approved',
  Help_Requested: 'Help Requested',
  Pending: 'Pending',
  Ready_For_Review: 'Ready For Review',
  Supplier_Approval_Needed: 'Supplier Approval Needed',
} as const;

/**
 * Supplier information
 */
export type SupplierPreferenceSupplier = {
  /** Supplier ID */
  id: number;
  /** Supplier name */
  name: string;
};

/**
 * Store information
 */
export type SupplierPreferenceStore = {
  /** Store ID */
  id: number;
  /** Store name */
  name: string | number;
  /** Seller ID */
  sellerId?: string | null;
};

export type SupplierPreferenceCredentialsItem = {
  /** Credential label */
  label: string;
  /** Internal credential name */
  internalName: string;
  /** Credential value */
  value: string;
  /** Credential type */
  type: string;
};

export type SupplierPreference = {
  /** Supplier preference ID */
  id: number;
  /** Supplier information */
  supplier: SupplierPreferenceSupplier;
  /** Store information */
  store: SupplierPreferenceStore;
  /** Supplier credentials */
  credentials: SupplierPreferenceCredentialsItem[];
  /** Connection status */
  status: SupplierPreferenceStatus;
  /** Human-readable status description */
  statusDescription: string;
  /** Priority order */
  priority: number;
};

export type SuppliersListResponse = SupplierPreference[];

export type PartsCategory = {
  /** Category ID */
  categoryId?: number;
  /** Category name */
  categoryName?: string;
};

export type PartsCategoriesResponse = PartsCategory[];

export type PartsSubcategory = {
  /** Subcategory ID */
  subcategoryId?: number;
  /** Subcategory name */
  subcategoryName?: string;
  /** Parent category ID */
  categoryId?: number;
  /** Parent category name */
  categoryName?: string;
};

export type PartsSubcategoriesResponse = PartsSubcategory[];

export type PartsPartType = {
  /** Part type ID */
  partTypeId?: number;
  /** Part type name */
  partTypeName?: string;
  /** Part type description */
  description?: string | null;
};

export type PartsPartTypesResponse = PartsPartType[];

export type PartsOrderItemWithSession = {
  id: string;
  sessionId: string;
  itemId: string;
  partName: string;
  partNumber: string | null;
  brand: string | null;
  brandDisplayName: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  description: string | null;
  category: string | null;
  imageUrl: string | null;
  partTypeId: number | null;
  taxonomy?: unknown | null;
  originalData?: unknown | null;
  status: string;
  returnedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sessionContextType: string;
  sessionContextRepairOrderId: string | null;
  repairOrderNumber: string | null;
  sessionOrderedAt: string;
  sessionStatus: string;
};

export type AllPartsOrderItemsListResponse = {
  items: PartsOrderItemWithSession[];
  total: number;
  page: number;
  limit: number;
};

export type UpdatePartsOrderItemStatusRequestStatus =
  (typeof UpdatePartsOrderItemStatusRequestStatus)[keyof typeof UpdatePartsOrderItemStatusRequestStatus];

export const UpdatePartsOrderItemStatusRequestStatus = {
  ordered: 'ordered',
  arrived: 'arrived',
  returned: 'returned',
} as const;

export type UpdatePartsOrderItemStatusRequest = {
  status: UpdatePartsOrderItemStatusRequestStatus;
};

export type CreateStockOrderResponseOrdersItemSupplier = {
  name?: string;
  id?: number;
  [key: string]: unknown | null;
};

export type CreateStockOrderResponseOrdersItemStore = {
  name?: string;
  id?: number;
  [key: string]: unknown | null;
};

export type CreateStockOrderResponseOrdersItemDelivery = {
  name?: string | null;
  price?: number;
};

export type CreateStockOrderResponseOrdersItemPaymentType =
  (typeof CreateStockOrderResponseOrdersItemPaymentType)[keyof typeof CreateStockOrderResponseOrdersItemPaymentType];

export const CreateStockOrderResponseOrdersItemPaymentType = {
  CreditCard: 'CreditCard',
  Wholesale: 'Wholesale',
} as const;

export type CreateStockOrderResponseOrdersItemPartsItemPrice = {
  price?: number;
  cost?: number;
  list?: number;
  coreCharge?: number;
  [key: string]: unknown | null;
};

export type CreateStockOrderResponseOrdersItemPartsItemBrand = {
  brandName?: string;
  brandId?: number;
  [key: string]: unknown | null;
};

export type CreateStockOrderResponseOrdersItemPartsItem = {
  orderItemId: string;
  partId: string;
  lineCardId?: string | null;
  quantity: number;
  partName?: string;
  partNumber?: string;
  price?: CreateStockOrderResponseOrdersItemPartsItemPrice;
  brand?: CreateStockOrderResponseOrdersItemPartsItemBrand;
  imageUrl?: string | null;
  availability?: unknown | null;
  storesAvailability?: unknown | null;
  [key: string]: unknown | null;
};

export type CreateStockOrderResponseOrdersItem = {
  supplier?: CreateStockOrderResponseOrdersItemSupplier;
  store?: CreateStockOrderResponseOrdersItemStore;
  totalPrice?: number;
  tax?: number;
  totalDiscount?: number;
  coreCharge?: number;
  fet?: number;
  shippingPrice?: number;
  delivery?: CreateStockOrderResponseOrdersItemDelivery;
  paymentType?: CreateStockOrderResponseOrdersItemPaymentType;
  parts?: CreateStockOrderResponseOrdersItemPartsItem[];
  poNumber?: string | null;
  notes?: string | null;
  [key: string]: unknown | null;
};

export type CreateStockOrderResponse = {
  sessionId: string;
  orders: CreateStockOrderResponseOrdersItem[];
};

export type CreateStockOrderRequestItemsItem = {
  inventoryItemId: string;
  /** @minimum 1 */
  quantity: number;
};

export type CreateStockOrderRequest = {
  /** @minItems 1 */
  items: CreateStockOrderRequestItemsItem[];
};

export type ConfirmStockOrderResponseOrdersItemSupplier = {
  name?: string;
  id?: number;
  [key: string]: unknown | null;
};

export type ConfirmStockOrderResponseOrdersItemStore = {
  name?: string;
  id?: number;
  [key: string]: unknown | null;
};

export type ConfirmStockOrderResponseOrdersItemDelivery = {
  name?: string | null;
  price?: number;
};

export type ConfirmStockOrderResponseOrdersItemPaymentType =
  (typeof ConfirmStockOrderResponseOrdersItemPaymentType)[keyof typeof ConfirmStockOrderResponseOrdersItemPaymentType];

export const ConfirmStockOrderResponseOrdersItemPaymentType = {
  CreditCard: 'CreditCard',
  Wholesale: 'Wholesale',
} as const;

export type ConfirmStockOrderResponseOrdersItemPartsItemPrice = {
  price?: number;
  cost?: number;
  list?: number;
  coreCharge?: number;
  [key: string]: unknown | null;
};

export type ConfirmStockOrderResponseOrdersItemPartsItemBrand = {
  brandName?: string;
  brandId?: number;
  [key: string]: unknown | null;
};

export type ConfirmStockOrderResponseOrdersItemPartsItem = {
  orderItemId: string;
  partId: string;
  lineCardId?: string | null;
  quantity: number;
  partName?: string;
  partNumber?: string;
  price?: ConfirmStockOrderResponseOrdersItemPartsItemPrice;
  brand?: ConfirmStockOrderResponseOrdersItemPartsItemBrand;
  imageUrl?: string | null;
  availability?: unknown | null;
  storesAvailability?: unknown | null;
  [key: string]: unknown | null;
};

export type ConfirmStockOrderResponseOrdersItem = {
  supplier?: ConfirmStockOrderResponseOrdersItemSupplier;
  store?: ConfirmStockOrderResponseOrdersItemStore;
  totalPrice?: number;
  tax?: number;
  totalDiscount?: number;
  coreCharge?: number;
  fet?: number;
  shippingPrice?: number;
  delivery?: ConfirmStockOrderResponseOrdersItemDelivery;
  paymentType?: ConfirmStockOrderResponseOrdersItemPaymentType;
  parts?: ConfirmStockOrderResponseOrdersItemPartsItem[];
  poNumber?: string | null;
  notes?: string | null;
  id?: number;
  orderUrl?: string;
  [key: string]: unknown | null;
};

export type ConfirmStockOrderResponse = {
  sessionId: string;
  orders: ConfirmStockOrderResponseOrdersItem[];
};

export type ConfirmStockOrderRequestOrdersItemOrderItemsItem = {
  /** @minLength 1 */
  orderItemId: string;
  /** @minimum 1 */
  quantity: number;
};

export type ConfirmStockOrderRequestOrdersItem = {
  storeId: number;
  poNumber?: string | null;
  notes?: string | null;
  /** @minItems 1 */
  orderItems: ConfirmStockOrderRequestOrdersItemOrderItemsItem[];
};

export type ConfirmStockOrderRequest = {
  /** @minLength 1 */
  sessionId: string;
  /** @minItems 1 */
  orders: ConfirmStockOrderRequestOrdersItem[];
};

export type PartsOrderSession = {
  id: string;
  shopId: string;
  status: string;
  contextType: string;
  contextRepairOrderId: string | null;
  vendorSessionId: string | null;
  redirectUrl?: string | null;
  vehicleVin: string | null;
  vehicleYear: number | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
};

export type CreatePartsOrderSessionRequestContext =
  | {
      type: 'repair_order';
      repairOrderId: string;
    }
  | {
      type: 'inventory';
    };

export type CreatePartsOrderSessionRequestSearchParamsVehicleParamsEngineParams = {
  engineVinId?: string;
  engineDesignationId?: number;
  engineVersionId?: number;
  fuelTypeId?: number;
  cylinderHeadTypeId?: number;
} | null;

export type CreatePartsOrderSessionRequestSearchParamsVehicleParams = {
  yearId?: number | null;
  makeId?: number | null;
  modelId?: number | null;
  subModelId?: number | null;
  engineId?: number | null;
  engineConfigId?: number | null;
  engineParams?: CreatePartsOrderSessionRequestSearchParamsVehicleParamsEngineParams;
};

export type CreatePartsOrderSessionRequestSearchParams = {
  vehicleParams?: CreatePartsOrderSessionRequestSearchParamsVehicleParams;
  partNumber?: string[];
};

export type CreatePartsOrderSessionRequestStockOrderPartsItem = {
  partNumber: string;
  partId?: string | null;
  quantity?: number | null;
  storeId?: number | null;
  lineCardId?: number | null;
};

export type CreatePartsOrderSessionRequestUrls = {
  callbackUrl?: string | null;
  callbackOrderUrl?: string | null;
  returnUrl?: string | null;
};

export type CreatePartsOrderSessionRequestSettings = {
  poNumber?: string | null;
} | null;

export type CreatePartsOrderSessionRequestAdditionalInformation = {
  mileage?: number | null;
} | null;

export type CreatePartsOrderSessionRequest = {
  context: CreatePartsOrderSessionRequestContext;
  vehicleVin?: string | null;
  vehicleYear?: number | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  searchParams?: CreatePartsOrderSessionRequestSearchParams;
  stockOrderParts?: CreatePartsOrderSessionRequestStockOrderPartsItem[];
  urls?: CreatePartsOrderSessionRequestUrls;
  settings?: CreatePartsOrderSessionRequestSettings;
  additionalInformation?: CreatePartsOrderSessionRequestAdditionalInformation;
};

export type PartsOrderSessionList = {
  sessions: PartsOrderSession[];
};

export type PartsOrderItem = {
  id: string;
  sessionId: string;
  itemId: string;
  partName: string;
  partNumber: string | null;
  brand: string | null;
  brandDisplayName: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  description: string | null;
  category: string | null;
  imageUrl: string | null;
  partTypeId: number | null;
  taxonomy?: unknown | null;
  originalData?: unknown | null;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export type PartsOrderSubmission = {
  id: string;
  sessionId: string;
  submittedAt: string;
  submittedByUserId: string;
  submissionResult?: unknown | null;
  partsData?: unknown | null;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export type PartsOrderSessionDetail = PartsOrderSession & {
  items: PartsOrderItem[];
  submissions: PartsOrderSubmission[];
};

export type PartsOrderItemList = {
  items: PartsOrderItem[];
};

export type PartsOrderItemInput = {
  itemId?: string;
  /** @minLength 1 */
  partName: string;
  partNumber?: string | null;
  brand?: string | null;
  brandDisplayName?: string | null;
  /** @minimum 0 */
  quantity?: number;
  /** @minimum 0 */
  unitPrice?: number;
  /** @minimum 0 */
  totalPrice?: number;
  description?: string | null;
  category?: string | null;
  imageUrl?: string | null;
  partTypeId?: number | null;
  taxonomy?: unknown | null;
  originalData?: unknown | null;
};

export type ReplacePartsOrderItemsRequest = {
  items: PartsOrderItemInput[];
};

export type CheckAvailabilityResponseOrdersItemSupplier = {
  name?: string;
  id?: number;
  [key: string]: unknown | null;
};

export type CheckAvailabilityResponseOrdersItemStore = {
  name?: string;
  id?: number;
  [key: string]: unknown | null;
};

export type CheckAvailabilityResponseOrdersItemDelivery = {
  name?: string | null;
  price?: number;
};

export type CheckAvailabilityResponseOrdersItemPaymentType =
  (typeof CheckAvailabilityResponseOrdersItemPaymentType)[keyof typeof CheckAvailabilityResponseOrdersItemPaymentType];

export const CheckAvailabilityResponseOrdersItemPaymentType = {
  CreditCard: 'CreditCard',
  Wholesale: 'Wholesale',
} as const;

export type CheckAvailabilityResponseOrdersItemPartsItemPrice = {
  price?: number;
  cost?: number;
  list?: number;
  coreCharge?: number;
  [key: string]: unknown | null;
};

export type CheckAvailabilityResponseOrdersItemPartsItemBrand = {
  brandName?: string;
  brandId?: number;
  [key: string]: unknown | null;
};

export type CheckAvailabilityResponseOrdersItemPartsItem = {
  orderItemId: string;
  partId: string;
  lineCardId?: string | null;
  quantity: number;
  partName?: string;
  partNumber?: string;
  price?: CheckAvailabilityResponseOrdersItemPartsItemPrice;
  brand?: CheckAvailabilityResponseOrdersItemPartsItemBrand;
  imageUrl?: string | null;
  availability?: unknown | null;
  storesAvailability?: unknown | null;
  [key: string]: unknown | null;
};

export type CheckAvailabilityResponseOrdersItem = {
  supplier?: CheckAvailabilityResponseOrdersItemSupplier;
  store?: CheckAvailabilityResponseOrdersItemStore;
  totalPrice?: number;
  tax?: number;
  totalDiscount?: number;
  coreCharge?: number;
  fet?: number;
  shippingPrice?: number;
  delivery?: CheckAvailabilityResponseOrdersItemDelivery;
  paymentType?: CheckAvailabilityResponseOrdersItemPaymentType;
  parts?: CheckAvailabilityResponseOrdersItemPartsItem[];
  poNumber?: string | null;
  notes?: string | null;
  [key: string]: unknown | null;
};

export type UnavailableItem = {
  orderItemId: string;
  partId: string;
  quantity: number;
  errors: string[];
  supplier?: string;
  store?: string;
};

export type CheckAvailabilityResponse = {
  available: boolean;
  orders?: CheckAvailabilityResponseOrdersItem[];
  unavailableItems?: UnavailableItem[];
  customCartSessionId?: string;
};

export type CheckAvailabilityRequest = {
  /**
   * PartsTech orderItemIds to check. Omit for full session cart.
   * @items.minLength 1
   */
  itemIds?: string[];
};

export type SubmitSelectedResponse = {
  sessionId: string;
  orders: (unknown | null)[];
  [key: string]: unknown | null;
};

export type SubmitSelectedRequest = {
  /**
   * @minItems 1
   * @items.minLength 1
   */
  itemIds: string[];
  /** @minLength 1 */
  customCartSessionId: string;
};

export type SubmitPartsOrderResponse = {
  sessionId: string;
  orders: (unknown | null)[];
  [key: string]: unknown | null;
};

export type SubmitOrderRequest = {
  /**
   * PartsTech orderItemIds to exclude (unavailable items)
   * @items.minLength 1
   */
  excludeItemIds?: string[];
};

export type PartsOrderArrivedResponse = {
  contextType: string;
  contextRepairOrderId: string | null;
};

export type UpdatePartsOrderQuantitiesRequestUpdatesItem = {
  /** PartsTech orderItemId (stored as itemId) */
  orderItemId: string;
  /**
   * New quantity
   * @minimum 1
   */
  quantity: number;
};

export type UpdatePartsOrderQuantitiesRequest = {
  /** @minItems 1 */
  updates: UpdatePartsOrderQuantitiesRequestUpdatesItem[];
};

export type SetVendorSessionRequest = {
  /** @minLength 1 */
  vendorSessionId: string;
};

export type ConversationCustomer = {
  firstName: string;
  lastName: string | null;
  phone: string | null;
};

export type Conversation = {
  id: string;
  shopId: string;
  customerId: string;
  status: string;
  lastMessageAt: string | null;
  unreadCount: number | null;
  createdAt: string;
  updatedAt: string;
  customer?: ConversationCustomer;
};

export type ConversationList = Conversation[];

export type CreateConversationRequest = {
  /** Customer ID to start a conversation with */
  customerId?: string;
  /** Phone number to start a conversation with */
  phoneNumber?: string;
};

export type Message = {
  id: string;
  conversationId: string;
  senderType: string;
  senderId: string | null;
  content: string | null;
  messageType: string;
  twilioMessageSid: string | null;
  mediaUrls: string[] | null;
  deliveryStatus: string | null;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * Stable discriminator for the frontend
 */
export type SendMessageNeedsOptInResponseCode =
  (typeof SendMessageNeedsOptInResponseCode)[keyof typeof SendMessageNeedsOptInResponseCode];

export const SendMessageNeedsOptInResponseCode = {
  needs_optin: 'needs_optin',
} as const;

/**
 * `never` when no consent row exists; `opted_out` when the most recent row is opted_out
 */
export type SendMessageNeedsOptInResponseLastConsentStatus =
  (typeof SendMessageNeedsOptInResponseLastConsentStatus)[keyof typeof SendMessageNeedsOptInResponseLastConsentStatus];

export const SendMessageNeedsOptInResponseLastConsentStatus = {
  never: 'never',
  opted_out: 'opted_out',
} as const;

export type SendMessageNeedsOptInResponse = {
  /** Human-readable explanation */
  error: string;
  /** Stable discriminator for the frontend */
  code: SendMessageNeedsOptInResponseCode;
  /** `never` when no consent row exists; `opted_out` when the most recent row is opted_out */
  lastConsentStatus: SendMessageNeedsOptInResponseLastConsentStatus;
};

/**
 * Present only on the out-of-hours-reply variant of 422
 */
export type SendMessageOutOfHoursResponseCode =
  (typeof SendMessageOutOfHoursResponseCode)[keyof typeof SendMessageOutOfHoursResponseCode];

export const SendMessageOutOfHoursResponseCode = {
  out_of_hours_reply: 'out_of_hours_reply',
} as const;

export type SendMessageOutOfHoursResponse = {
  /** Human-readable explanation */
  error: string;
  /** Present only on the out-of-hours-reply variant of 422 */
  code?: SendMessageOutOfHoursResponseCode;
  /** ISO-8601 timestamp at which a reply would be allowed (present only on the out-of-hours-reply variant) */
  scheduledFor?: string;
};

export type SendMessageRequest = {
  /**
   * Message content
   * @minLength 1
   */
  content: string;
};

export type MessageListResponse = {
  messages: Message[];
  total: number;
  page: number;
  pageSize: number;
};

export type WorkflowSubStage = {
  id: string;
  stageId: string | null;
  name: string;
  displayName: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  requiresApproval: boolean;
  autoAdvance: boolean;
  createdAt: string | null;
};

export type WorkflowStageWithSubStages = WorkflowStage & {
  subStages: WorkflowSubStage[];
};

export type WorkflowStageWithSubStagesListResponse = WorkflowStageWithSubStages[];

export type StarterEstimateResponse = {
  repairOrderId: string;
  /** Opaque token for the public estimate URL */
  publicToken: string;
  sampleCustomerId: string;
};

/**
 * Which channels actually dispatched a message during this send
 */
export type SendStarterEstimateResponseDelivered = {
  email: boolean;
  sms: boolean;
};

export type SendStarterEstimateResponse = {
  /** Public estimate URL the owner can share */
  publicUrl: string;
  /** Which channels actually dispatched a message during this send */
  delivered: SendStarterEstimateResponseDelivered;
};

/**
 * Delivery channel for the sample estimate
 */
export type SendStarterEstimateRequestChannel =
  (typeof SendStarterEstimateRequestChannel)[keyof typeof SendStarterEstimateRequestChannel];

export const SendStarterEstimateRequestChannel = {
  email: 'email',
  sms: 'sms',
  copy_link: 'copy_link',
} as const;

export type SendStarterEstimateRequest = {
  /** Delivery channel for the sample estimate */
  channel: SendStarterEstimateRequestChannel;
  /** Owner-supplied recipient email — also persisted onto the sample customer record. Required when channel = email. */
  email?: string;
  /** Owner-supplied recipient phone in E.164 — also persisted onto the sample customer record. Required when channel = sms. */
  phone?: string;
};

export type SetupPanelStateItemsItem = {
  /** Stable item identifier the frontend keys on */
  id: string;
  /** Human-readable label for the item */
  label: string;
  /** Whether the underlying signal is satisfied for this shop */
  completed: boolean;
};

export type SetupPanelState = {
  /** Per-item completion state for the dashboard setup panel */
  items: SetupPanelStateItemsItem[];
};

export type NotificationItem = {
  id: string;
  userId: string;
  shopId: string;
  type: string;
  title: string;
  message: string;
  data?: unknown | null;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
};

export type NotificationList = NotificationItem[];

export type NotificationPreferences = {
  id: string;
  userId: string;
  shopId: string;
  appointmentNotificationsEnabled: boolean;
  estimateNotificationsEnabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type UpsertNotificationPreferencesRequest = {
  appointmentNotificationsEnabled?: boolean;
  estimateNotificationsEnabled?: boolean;
};

export type CouponsReportRowDiscountType =
  (typeof CouponsReportRowDiscountType)[keyof typeof CouponsReportRowDiscountType];

export const CouponsReportRowDiscountType = {
  percent: 'percent',
  fixed: 'fixed',
} as const;

export type CouponsReportRowEligibilitySegment =
  (typeof CouponsReportRowEligibilitySegment)[keyof typeof CouponsReportRowEligibilitySegment];

export const CouponsReportRowEligibilitySegment = {
  all: 'all',
  first_time: 'first_time',
  returning: 'returning',
  customer_tag: 'customer_tag',
  specific_customer: 'specific_customer',
} as const;

export type CouponsReportRow = {
  couponId: string;
  couponName: string;
  couponCode: string;
  discountType: CouponsReportRowDiscountType;
  discountValue: number;
  isActive: boolean;
  isArchived: boolean;
  redemptions: number;
  uniqueCustomers: number;
  totalDiscounted: number;
  avgPerRedemption: number;
  eligibilitySegment: CouponsReportRowEligibilitySegment;
};

export type CouponsReportSummary = {
  totalRedemptions: number;
  totalDiscounted: number;
  activeCoupons: number;
};

export type CouponsReportResponse = {
  rows: CouponsReportRow[];
  summary: CouponsReportSummary;
};

/**
 * Filter by discount source
 */
export type DiscountsReportRowSource = (typeof DiscountsReportRowSource)[keyof typeof DiscountsReportRowSource];

export const DiscountsReportRowSource = {
  customer: 'customer',
  coupon: 'coupon',
  manual: 'manual',
  rebate: 'rebate',
} as const;

export type DiscountsReportRowDiscountType =
  (typeof DiscountsReportRowDiscountType)[keyof typeof DiscountsReportRowDiscountType];

export const DiscountsReportRowDiscountType = {
  percent: 'percent',
  fixed: 'fixed',
} as const;

export type DiscountsReportRow = {
  discountId: string;
  repairOrderId: string;
  roNumber: string;
  roDate: string;
  customerId: string | null;
  customerName: string | null;
  /** Filter by discount source */
  source: DiscountsReportRowSource;
  couponId: string | null;
  couponName: string | null;
  couponCode: string | null;
  discountType: DiscountsReportRowDiscountType;
  discountValue: number;
  appliedAmount: number;
  allocatedToParts: number;
  allocatedToLabor: number;
  allocatedToTires: number;
  allocatedToSubs: number;
  allocatedToFees: number;
  advisorId: string | null;
  advisorName: string | null;
  reason: string | null;
};

/**
 * appliedAmount summed per source key
 */
export type DiscountsReportSummaryBySource = { [key: string]: number };

export type DiscountsReportSummary = {
  /** Sum of appliedAmount across all rows */
  totalDiscounted: number;
  rowCount: number;
  /** appliedAmount summed per source key */
  bySource: DiscountsReportSummaryBySource;
};

export type DiscountsReportResponse = {
  rows: DiscountsReportRow[];
  summary: DiscountsReportSummary;
};

/**
 * Available values for each filter dimension
 */
export type ReportResponseFilterOptions = { [key: string]: string[] };

export type ReportSummary = {
  /** Total spend for the report period */
  totalSpend: number;
  /** Total number of items purchased */
  totalItems: number;
  /** Name of the top group by spend */
  topGroup: string;
  /** Average cost per item */
  avgCostPerItem: number;
  /** Percentage change vs previous equivalent period (null if no previous data) */
  periodChange: number | null;
};

export type ReportTimeSeriesItem = {
  /** Time period label (e.g., 2024-01-01, 2024-W01, 2024-01) */
  period: string;
  /** Total spend for this period */
  spend: number;
};

export type ReportGroupedItem = {
  /** Group label (supplier name, brand, category, etc.) */
  name: string;
  /** Total quantity of items in this group */
  qty: number;
  /** Total cost for this group */
  totalCost: number;
  /** Average cost per item in this group */
  avgCost: number;
  /** Percentage of total spend represented by this group */
  pctOfTotal: number;
};

export type ReportLineItem = {
  partName: string;
  partNumber: string | null;
  brand: string | null;
  category: string | null;
  supplier: string | null;
  tireSize: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  orderedAt: string;
};

export type ReportResponse = {
  summary: ReportSummary;
  timeSeries: ReportTimeSeriesItem[];
  grouped: ReportGroupedItem[];
  items: ReportLineItem[];
  /** Available values for each filter dimension */
  filterOptions?: ReportResponseFilterOptions;
};

/**
 * Date range covered by this report
 */
export type TechEfficiencyResponsePeriod = {
  /** Report period start date (YYYY-MM-DD) */
  startDate: string;
  /** Report period end date (YYYY-MM-DD) */
  endDate: string;
};

/**
 * Aggregated shop-wide metrics for the period
 */
export type TechEfficiencyResponseSummary = {
  /** Shop-wide average efficiency (null if no data) */
  shopAvgEfficiency: number | null;
  /** Shop-wide average productivity (null if no data) */
  shopAvgProductivity: number | null;
  /** Shop-wide average proficiency (null if no data) */
  shopAvgProficiency: number | null;
  /** Total billed hours across all technicians */
  totalBilledHours: number;
  /** Total actual hours across all technicians */
  totalActualHours: number;
  /** Total shift hours across all technicians */
  totalShiftHours: number;
  /** Number of technicians included in the report */
  technicianCount: number;
};

export type TechEfficiencyRow = {
  /** Technician UUID */
  technicianId: string;
  /** Technician first name */
  firstName: string;
  /** Technician last name */
  lastName: string;
  /** Technician calendar color (hex) */
  technicianColor: string;
  /** Whether the technician is currently active */
  isActiveTechnician: boolean;
  /** Total billed (flagged) hours in the period */
  billedHours: number;
  /** Total actual (clock) hours in the period */
  actualHours: number;
  /** Total scheduled shift hours in the period */
  shiftHours: number;
  /** Billed ÷ actual hours (null if no actual hours) */
  efficiency: number | null;
  /** Billed ÷ shift hours (null if no shift hours recorded) */
  productivity: number | null;
  /** Actual ÷ shift hours (null if no shift hours recorded) */
  proficiency: number | null;
  /** Number of closed labor operations in the period */
  laborOpCount: number;
  /** Labor operations without a matching time clock entry */
  unpairedLaborOpCount: number;
};

export type TechEfficiencyResponse = {
  /** Date range covered by this report */
  period: TechEfficiencyResponsePeriod;
  /** Aggregated shop-wide metrics for the period */
  summary: TechEfficiencyResponseSummary;
  /** Per-technician efficiency breakdown */
  technicians: TechEfficiencyRow[];
};

/**
 * Technician identity information
 */
export type TechEfficiencyDetailResponseTechnician = {
  /** Technician UUID */
  id: string;
  /** Technician first name */
  firstName: string;
  /** Technician last name */
  lastName: string;
  /** Technician calendar color (hex) */
  technicianColor: string;
};

export type TechEfficiencyDetailLaborOp = {
  /** Labor operation UUID */
  laborOpId: string;
  /** Parent repair order UUID */
  repairOrderId: string;
  /** Human-readable repair order number */
  repairOrderNumber: string;
  /** Year/make/model summary of the vehicle */
  vehicleSummary: string;
  /** Customer display name */
  customerName: string;
  /** Labor operation description */
  description: string;
  /** ISO timestamp when the labor operation was closed */
  closedAt: string;
  /** Billed (flagged) hours for this operation */
  billedHours: number;
  /** Actual (clock) hours for this operation (null if unpaired) */
  actualHours: number | null;
  /** Billed ÷ actual hours (null if unpaired) */
  efficiency: number | null;
};

export type TechEfficiencyDetailResponse = {
  /** Technician identity information */
  technician: TechEfficiencyDetailResponseTechnician;
  /** Individual labor operations for this technician in the period */
  laborOps: TechEfficiencyDetailLaborOp[];
};

export type PresignedUploadResponse = {
  uploadUrl: string;
  key: string;
  publicUrl?: string;
};

export type DviFeatureRequiredErrorError =
  (typeof DviFeatureRequiredErrorError)[keyof typeof DviFeatureRequiredErrorError];

export const DviFeatureRequiredErrorError = {
  feature_required: 'feature_required',
} as const;

export type DviFeatureRequiredErrorFeature =
  (typeof DviFeatureRequiredErrorFeature)[keyof typeof DviFeatureRequiredErrorFeature];

export const DviFeatureRequiredErrorFeature = {
  dvi_photos: 'dvi_photos',
  dvi_video: 'dvi_video',
} as const;

export type DviFeatureRequiredError = {
  error: DviFeatureRequiredErrorError;
  feature: DviFeatureRequiredErrorFeature;
  message: string;
};

export type DviFileTooLargeErrorError = (typeof DviFileTooLargeErrorError)[keyof typeof DviFileTooLargeErrorError];

export const DviFileTooLargeErrorError = {
  file_too_large: 'file_too_large',
} as const;

export type DviFileTooLargeErrorMediaType =
  (typeof DviFileTooLargeErrorMediaType)[keyof typeof DviFileTooLargeErrorMediaType];

export const DviFileTooLargeErrorMediaType = {
  image: 'image',
  video: 'video',
} as const;

export type DviFileTooLargeError = {
  error: DviFileTooLargeErrorError;
  mediaType: DviFileTooLargeErrorMediaType;
  /**
   * @minimum 0
   * @exclusiveMinimum true
   */
  maxBytes: number;
  message: string;
};

export type PresignedUploadBodyBucket = (typeof PresignedUploadBodyBucket)[keyof typeof PresignedUploadBodyBucket];

export const PresignedUploadBodyBucket = {
  avatars: 'avatars',
  'dvi-files': 'dvi-files',
  'contact-files': 'contact-files',
  'public-assets': 'public-assets',
} as const;

export type PresignedUploadBody = {
  bucket: PresignedUploadBodyBucket;
  /** @minLength 1 */
  fileName: string;
  /** @minLength 1 */
  contentType: string;
  /**
   * @minimum 0
   * @exclusiveMinimum true
   */
  contentLength?: number;
};

export type PresignedDownloadResponse = {
  downloadUrl: string;
};

export type PresignedDownloadBodyBucket =
  (typeof PresignedDownloadBodyBucket)[keyof typeof PresignedDownloadBodyBucket];

export const PresignedDownloadBodyBucket = {
  avatars: 'avatars',
  'dvi-files': 'dvi-files',
  'contact-files': 'contact-files',
  'public-assets': 'public-assets',
} as const;

export type PresignedDownloadBody = {
  bucket: PresignedDownloadBodyBucket;
  /** @minLength 1 */
  key: string;
};

export type DeleteObjectBodyBucket = (typeof DeleteObjectBodyBucket)[keyof typeof DeleteObjectBodyBucket];

export const DeleteObjectBodyBucket = {
  avatars: 'avatars',
  'dvi-files': 'dvi-files',
  'contact-files': 'contact-files',
  'public-assets': 'public-assets',
} as const;

export type DeleteObjectBody = {
  bucket: DeleteObjectBodyBucket;
  /** @minLength 1 */
  key: string;
};

export type PaymentPushPreferences = {
  shopId: string;
  dismissedAt: string | null;
  dismissedByUserId: string | null;
  snoozeEmptyStateUntil: string | null;
  snoozeSendInvoiceUntil: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UpdatePaymentPushPreferencesBody =
  | {
      action: 'snooze';
      surface: 'empty_state' | 'send_invoice';
    }
  | {
      action: 'dismiss';
    }
  | {
      action: 'reset';
    }
  | {
      action: 'skip_onboarding';
    };

export type LogoutParams = {
  /**
   * Set to "true" to revoke all sessions for the user
   */
  all?: string;
};

export type UploadAvatarBody = {
  file: Blob;
};

export type HandleSubscriptionWebhook200 = {
  received: boolean;
};

export type HandleSubscriptionWebhook400 = {
  error: string;
};

export type HandleSubscriptionWebhook500 = {
  error: string;
};

export type HandleSubscriptionWebhookTest200 = {
  received: boolean;
};

export type HandleSubscriptionWebhookTest400 = {
  error: string;
};

export type HandleSubscriptionWebhookTest500 = {
  error: string;
};

export type GetAvailableSlotsParams = {
  /**
   * Shop UUID
   */
  shopId: string;
  /**
   * Date to check for available slots
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  date: string;
};

export type GetVehicleYearsParams = {
  /**
   * Filter by make ID
   */
  make?: number | null;
  /**
   * Filter by model ID
   */
  model?: number | null;
};

export type GetVehicleMakesParams = {
  /**
   * Filter by vehicle year
   */
  year?: number | null;
  /**
   * Filter by model ID
   */
  model?: number | null;
  /**
   * Filter by submodel ID
   */
  submodel?: number | null;
};

export type GetVehicleModelsParams = {
  /**
   * Filter by vehicle year
   */
  year?: number | null;
  /**
   * Filter by make ID
   */
  make?: number | null;
  /**
   * Filter by submodel ID
   */
  submodel?: number | null;
};

export type GetVehicleSubmodelsParams = {
  /**
   * Vehicle year (required)
   */
  year?: number | null;
  /**
   * Make ID (required)
   */
  make?: number | null;
  /**
   * Model ID (required)
   */
  model?: number | null;
  /**
   * Filter by engine ID
   */
  engine?: number | null;
  /**
   * Filter by region
   */
  region?: GetVehicleSubmodelsRegion;
};

export type GetVehicleSubmodelsRegion = (typeof GetVehicleSubmodelsRegion)[keyof typeof GetVehicleSubmodelsRegion];

export const GetVehicleSubmodelsRegion = {
  US: 'US',
  CA: 'CA',
  MX: 'MX',
} as const;

export type GetVehicleEnginesParams = {
  /**
   * Vehicle year (required)
   */
  year?: number | null;
  /**
   * Make ID (required)
   */
  make?: number | null;
  /**
   * Model ID (required)
   */
  model?: number | null;
  /**
   * Filter by submodel ID
   */
  submodel?: number | null;
  /**
   * Filter by region
   */
  region?: GetVehicleEnginesRegion;
};

export type GetVehicleEnginesRegion = (typeof GetVehicleEnginesRegion)[keyof typeof GetVehicleEnginesRegion];

export const GetVehicleEnginesRegion = {
  US: 'US',
  CA: 'CA',
  MX: 'MX',
} as const;

export type QuickBooksOAuthCallbackParams = {
  /**
   * OAuth authorization code from Intuit
   */
  code?: string;
  /**
   * HMAC-signed state token issued by the auth-url endpoint
   */
  state?: string;
  /**
   * QuickBooks company realm ID
   */
  realmId?: string;
  /**
   * OAuth error code when the user denied access
   */
  error?: string;
};

export type PartsOrderCallbackParams = {
  /**
   * User ID
   */
  uid: string;
  /**
   * Repair order ID
   */
  repair_order_id?: string;
  /**
   * Inventory item ID to link (skips creating new inventory items)
   */
  link_inventory_item_id?: string;
};

export type GetRedirectTokenParams = {
  kind: GetRedirectTokenKind;
  /**
   * @minLength 8
   */
  token: string;
};

export type GetRedirectTokenKind = (typeof GetRedirectTokenKind)[keyof typeof GetRedirectTokenKind];

export const GetRedirectTokenKind = {
  estimate: 'estimate',
  invoice: 'invoice',
  dvi: 'dvi',
  appointment: 'appointment',
} as const;

export type ListUsersParams = {
  /**
   * Filter users by role
   */
  role?: ListUsersRole;
};

export type ListUsersRole = (typeof ListUsersRole)[keyof typeof ListUsersRole];

export const ListUsersRole = {
  shop_owner: 'shop_owner',
  manager: 'manager',
  technician: 'technician',
  front_desk: 'front_desk',
  shop_admin: 'shop_admin',
  system_admin: 'system_admin',
  admin: 'admin',
  service_advisor: 'service_advisor',
  support_rep: 'support_rep',
} as const;

export type GetUserAuditLogsParams = {
  /**
   * Page number (1-indexed)
   * @minimum 1
   */
  page?: number;
  /**
   * Number of items per page
   * @minimum 1
   * @maximum 100
   */
  perPage?: number;
};

export type GetUserLoginHistoryParams = {
  /**
   * Page number (1-indexed)
   * @minimum 1
   */
  page?: number;
  /**
   * Number of items per page
   * @minimum 1
   * @maximum 100
   */
  perPage?: number;
};

export type UploadShopLogoBody = {
  file: Blob;
};

export type RemoveOrganizationUserParams = {
  evictFromShops?: boolean | null;
};

export type ListCustomersParams = {
  /**
   * Page number
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Search term for filtering
   * @maxLength 200
   */
  search?: string;
};

export type UploadCustomerFileBody = {
  /** CSV, TSV, TXT, or VCF file (max 10MB) */
  file: Blob;
};

export type GetVehicleTireSizesParams = {
  /**
   * Vehicle ID
   */
  vehicleId?: number | null;
};

export type ResolveVehicleParams = {
  /**
   * PartsTech year
   */
  year?: number | null;
  /**
   * PartsTech make ID
   */
  make?: number | null;
  /**
   * PartsTech model ID
   */
  model?: number | null;
  /**
   * PartsTech submodel ID
   */
  submodel?: number | null;
  /**
   * PartsTech engine ID
   */
  engine?: number | null;
};

export type ListVehiclesParams = {
  /**
   * Filter vehicles by customer ID
   */
  customerId?: string;
  /**
   * Search by VIN, license plate, make, or model (case-insensitive)
   */
  search?: string;
  /**
   * Maximum number of results to return
   * @minimum 1
   * @maximum 100
   */
  limit?: number;
  /**
   * When true, includes archived vehicles in results. Defaults to false.
   */
  includeArchived?: boolean | null;
};

export type RegenerateVehicleImage202Status =
  (typeof RegenerateVehicleImage202Status)[keyof typeof RegenerateVehicleImage202Status];

export const RegenerateVehicleImage202Status = {
  pending: 'pending',
} as const;

export type RegenerateVehicleImage202 = {
  status: RegenerateVehicleImage202Status;
};

export type GetAppointmentsParams = {
  /**
   * Start date (inclusive)
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  from?: string;
  /**
   * End date (inclusive)
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  to?: string;
  /**
   * Filter by customer UUID
   */
  customerId?: string;
  /**
   * Comma-separated status filter (e.g. scheduled,arrived)
   */
  status?: string;
  /**
   * Filter by repair order UUID
   */
  repairOrderId?: string;
};

export type ListBoardRepairOrdersParams = {
  /**
   * Filter to only repair orders assigned to this technician user
   */
  technicianUserId?: string;
};

export type ListRepairOrdersParams = {
  /**
   * Page number
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Search by repair order number, customer name, or vehicle
   */
  search?: string;
  /**
   * Filter by customer ID
   */
  customerId?: string;
  /**
   * Filter by vehicle ID
   */
  vehicleId?: string;
  /**
   * Filter by archived status
   */
  isArchived?: boolean | null;
  /**
   * Filter by workflow stage ID
   */
  workflowStageId?: string;
  /**
   * Filter by workflow stage
   */
  stage?: ListRepairOrdersStage;
  /**
   * Filter to only repair orders assigned to this technician user
   */
  technicianUserId?: string;
};

export type ListRepairOrdersStage = (typeof ListRepairOrdersStage)[keyof typeof ListRepairOrdersStage];

export const ListRepairOrdersStage = {
  intake: 'intake',
  in_progress: 'in_progress',
  invoice: 'invoice',
  closed: 'closed',
} as const;

export type ApplyDiscount409 =
  | {
      error: string;
      conflictingDiscountIds: string[];
    }
  | {
      error: string;
      reason: 'coupon_already_applied';
      existingDiscountId: string;
    };

export type ApplyDiscount422 = {
  error: string;
  reason: string;
};

export type ListCouponsParams = {
  status?: CouponStatus;
  segment?: ListCouponsSegment;
  search?: string;
  /**
   * @minimum 0
   * @exclusiveMinimum true
   */
  page?: number;
  /**
   * @minimum 0
   * @maximum 100
   * @exclusiveMinimum true
   */
  pageSize?: number;
};

export type ListCouponsSegment = (typeof ListCouponsSegment)[keyof typeof ListCouponsSegment];

export const ListCouponsSegment = {
  all: 'all',
  first_time: 'first_time',
  returning: 'returning',
  customer_tag: 'customer_tag',
  specific_customer: 'specific_customer',
} as const;

export type ListEligibleCouponsParams = {
  customerId: string;
  repairOrderId: string;
};

export type ListInventoryItemsParams = {
  /**
   * Page number
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Search term for filtering by name, part number, or brand
   */
  search?: string;
  /**
   * Filter by item type
   */
  itemType?: ListInventoryItemsItemType;
  /**
   * Filter by supplier UUID
   */
  supplierId?: string;
  /**
   * Filter items where current quantity is below minimum quantity
   */
  lowStock?: boolean | null;
  /**
   * Filter by tire size (partial match)
   */
  tireSize?: string;
  /**
   * Exclude items of this type
   */
  excludeItemType?: ListInventoryItemsExcludeItemType;
};

export type ListInventoryItemsItemType = (typeof ListInventoryItemsItemType)[keyof typeof ListInventoryItemsItemType];

export const ListInventoryItemsItemType = {
  part: 'part',
  tire: 'tire',
  fluid: 'fluid',
  supply: 'supply',
} as const;

export type ListInventoryItemsExcludeItemType =
  (typeof ListInventoryItemsExcludeItemType)[keyof typeof ListInventoryItemsExcludeItemType];

export const ListInventoryItemsExcludeItemType = {
  part: 'part',
  tire: 'tire',
  fluid: 'fluid',
  supply: 'supply',
} as const;

export type GetLatestInventoryImportParams = {
  /**
   * ISO timestamp — only imports created at or after this time are considered
   */
  since: string;
};

export type ListSuppliersParams = {
  /**
   * Page number
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Search term for filtering by supplier name
   */
  search?: string;
  /**
   * Filter by supplier type
   */
  supplierType?: ListSuppliersSupplierType;
};

export type ListSuppliersSupplierType = (typeof ListSuppliersSupplierType)[keyof typeof ListSuppliersSupplierType];

export const ListSuppliersSupplierType = {
  aftermarket: 'aftermarket',
  tire: 'tire',
} as const;

export type SearchLaborOperationsParams = {
  /**
   * Base vehicle ID from Motor
   */
  baseVehicleId: string;
  /**
   * Optional search term for server-side filtering
   */
  query?: string;
};

export type GetLaborOperationDetailsParams = {
  /**
   * Base vehicle ID from Motor
   */
  baseVehicleId: string;
  /**
   * Taxonomy ID from Motor to fetch details for
   */
  taxonomyId: string;
};

export type ListInvoicesParams = {
  /**
   * Page number
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Filter by status
   */
  status?: ListInvoicesStatus;
  /**
   * Search by invoice number or customer name
   */
  search?: string;
  /**
   * Filter by repair order ID
   */
  repairOrderId?: string;
};

export type ListInvoicesStatus = (typeof ListInvoicesStatus)[keyof typeof ListInvoicesStatus];

export const ListInvoicesStatus = {
  draft: 'draft',
  open: 'open',
  partially_paid: 'partially_paid',
  paid: 'paid',
  voided: 'voided',
  uncollectible: 'uncollectible',
} as const;

export type ListInvoices200 = InvoiceListResponse & {
  stats: InvoiceListStats;
};

export type SearchQuickBooksCustomersParams = {
  /**
   * Search term matched against the QuickBooks customer name or email (case-insensitive)
   */
  q?: string;
};

export type ListInspectionsParams = {
  /**
   * Filter inspections by vehicle ID
   */
  vehicleId: string;
};

export type ListInspectionAttachmentsParams = {
  /**
   * Filter attachments by inspection item ID
   */
  itemId?: string;
};

export type ListFeesParams = {
  /**
   * Include soft-deleted fees
   */
  includeInactive?: (typeof ListFeesIncludeInactive)[keyof typeof ListFeesIncludeInactive];
};

export const ListFeesIncludeInactive = { true: 'true', false: 'false' } as const;
export type ListSubletVendorsParams = {
  /**
   * Case-insensitive name filter
   */
  search?: string;
  /**
   * When true, excludes inactive vendors
   */
  activeOnly?: (typeof ListSubletVendorsActiveOnly)[keyof typeof ListSubletVendorsActiveOnly];
};

export const ListSubletVendorsActiveOnly = { true: 'true', false: 'false' } as const;
export type ClockInAttendanceBody = {
  isBreak?: boolean;
};

export type ListTimeEntriesParams = {
  userId?: string;
  entryType?: EntryType;
  status?: EntryStatus;
  from?: string;
  to?: string;
  /**
   * @minimum 1
   * @maximum 500
   */
  limit?: number;
  /**
   * @minimum 0
   */
  offset?: number | null;
};

export type ListPendingApprovalsParams = {
  weekStart: string;
  weekEnd: string;
  userId?: string;
};

export type AssistantChatBodyMessagesItemRole =
  (typeof AssistantChatBodyMessagesItemRole)[keyof typeof AssistantChatBodyMessagesItemRole];

export const AssistantChatBodyMessagesItemRole = {
  user: 'user',
  assistant: 'assistant',
} as const;

export type AssistantChatBodyMessagesItem = {
  role: AssistantChatBodyMessagesItemRole;
  content: string;
  images?: string[];
};

export type AssistantChatBodyConfirmActionArgs = { [key: string]: unknown | null };

export type AssistantChatBodyConfirmAction = {
  toolCallId: string;
  confirmed: boolean;
  action?: string;
  args?: AssistantChatBodyConfirmActionArgs;
};

export type AssistantChatBodyTaskResponse = {
  taskId: string;
  step: string;
  selectedId: string;
  selectedLabel?: string;
};

export type AssistantChatBodyConcernsResponse = {
  confirmed: boolean;
  concerns?: string[];
};

export type AssistantChatBodyAppointmentSlotResponseServiceType =
  (typeof AssistantChatBodyAppointmentSlotResponseServiceType)[keyof typeof AssistantChatBodyAppointmentSlotResponseServiceType];

export const AssistantChatBodyAppointmentSlotResponseServiceType = {
  'drop-off': 'drop-off',
  wait: 'wait',
  'tow-in': 'tow-in',
} as const;

export type AssistantChatBodyAppointmentSlotResponse = {
  /** @pattern ^\d{4}-\d{2}-\d{2}$ */
  date: string;
  /** @pattern ^\d{2}:\d{2}$ */
  startTime: string;
  /** @pattern ^\d{2}:\d{2}$ */
  endTime: string;
  serviceType: AssistantChatBodyAppointmentSlotResponseServiceType;
};

export type AssistantChatBodyTaskState = { [key: string]: unknown | null };

export type AssistantChatBody = {
  /**
   * @minItems 1
   * @maxItems 50
   */
  messages: AssistantChatBodyMessagesItem[];
  conversationId?: string;
  confirmAction?: AssistantChatBodyConfirmAction;
  taskResponse?: AssistantChatBodyTaskResponse;
  concernsResponse?: AssistantChatBodyConcernsResponse;
  appointmentSlotResponse?: AssistantChatBodyAppointmentSlotResponse;
  taskState?: AssistantChatBodyTaskState;
  stateToken?: string;
};

export type AssistantChat200 = {
  streaming: true;
};

export type PairJobsWithItemsBodyPartsItem = {
  id?: string;
  name: string;
  category?: string;
};

export type PairJobsWithItemsBodyTiresItem = {
  id?: string;
  name: string;
  category?: string;
};

export type PairJobsWithItemsBodyLaborItem = {
  id?: string;
  name: string;
  category?: string;
  description?: string;
};

export type PairJobsWithItemsBodyExistingJobsItem = {
  name: string;
  job_type: string;
};

export type PairJobsWithItemsBody = {
  parts: PairJobsWithItemsBodyPartsItem[];
  tires: PairJobsWithItemsBodyTiresItem[];
  labor: PairJobsWithItemsBodyLaborItem[];
  existingJobs: PairJobsWithItemsBodyExistingJobsItem[];
};

export type PairJobsWithItems200PairingsItemConfidence =
  (typeof PairJobsWithItems200PairingsItemConfidence)[keyof typeof PairJobsWithItems200PairingsItemConfidence];

export const PairJobsWithItems200PairingsItemConfidence = {
  high: 'high',
  medium: 'medium',
  low: 'low',
} as const;

export type PairJobsWithItems200PairingsItemItemsPartsItem = {
  id: string;
  name: string;
  reason?: string;
};

export type PairJobsWithItems200PairingsItemItemsTiresItem = {
  id: string;
  name: string;
  reason?: string;
};

export type PairJobsWithItems200PairingsItemItemsLaborItem = {
  id: string;
  name: string;
  reason?: string;
};

export type PairJobsWithItems200PairingsItemItems = {
  parts: PairJobsWithItems200PairingsItemItemsPartsItem[];
  tires: PairJobsWithItems200PairingsItemItemsTiresItem[];
  labor: PairJobsWithItems200PairingsItemItemsLaborItem[];
};

export type PairJobsWithItems200PairingsItem = {
  jobName: string;
  confidence: PairJobsWithItems200PairingsItemConfidence;
  items: PairJobsWithItems200PairingsItemItems;
};

export type PairJobsWithItems200UnpairedPartsItem = {
  id: string;
  name: string;
  reason?: string;
};

export type PairJobsWithItems200UnpairedTiresItem = {
  id: string;
  name: string;
  reason?: string;
};

export type PairJobsWithItems200UnpairedLaborItem = {
  id: string;
  name: string;
  reason?: string;
};

export type PairJobsWithItems200Unpaired = {
  parts: PairJobsWithItems200UnpairedPartsItem[];
  tires: PairJobsWithItems200UnpairedTiresItem[];
  labor: PairJobsWithItems200UnpairedLaborItem[];
};

export type PairJobsWithItems200 = {
  pairings: PairJobsWithItems200PairingsItem[];
  unpaired: PairJobsWithItems200Unpaired;
};

export type ListCannedJobsParams = {
  /**
   * Search by name
   */
  search?: string;
  /**
   * Filter by category
   */
  category?: string;
  /**
   * Filter by job type
   */
  jobType?: ListCannedJobsJobType;
  /**
   * Filter by active status
   */
  isActive?: ListCannedJobsIsActive;
};

export type ListCannedJobsJobType = (typeof ListCannedJobsJobType)[keyof typeof ListCannedJobsJobType];

export const ListCannedJobsJobType = {
  repair: 'repair',
  tire: 'tire',
} as const;

export type ListCannedJobsIsActive = (typeof ListCannedJobsIsActive)[keyof typeof ListCannedJobsIsActive];

export const ListCannedJobsIsActive = {
  true: 'true',
  false: 'false',
} as const;

export type SearchPartTypesParams = {
  /**
   * Part type name to search (min 2 chars)
   * @minLength 2
   */
  name: string;
};

export type GetPartsCategoriesParams = {
  /**
   * Filter by category name
   */
  name?: string;
};

export type GetPartsSubcategoriesParams = {
  /**
   * Filter by subcategory name
   */
  name?: string;
  /**
   * Filter by category ID
   */
  category?: number | null;
};

export type GetPartsPartTypesParams = {
  /**
   * Filter by part type name
   */
  name?: string;
  /**
   * Filter by category ID
   */
  category?: number | null;
  /**
   * Filter by subcategory ID
   */
  subcategory?: number | null;
  /**
   * Records per page
   */
  perPage?: (typeof GetPartsPartTypesPerPage)[keyof typeof GetPartsPartTypesPerPage];
};

export const GetPartsPartTypesPerPage = { NUMBER_10: 10, NUMBER_25: 25, NUMBER_50: 50, NUMBER_100: 100 } as const;
export type ListAllPartsOrderItemsParams = {
  status?: ListAllPartsOrderItemsStatus;
  contextType?: ListAllPartsOrderItemsContextType;
  search?: string;
  /**
   * @minimum 1
   */
  page?: number;
  /**
   * @minimum 1
   * @maximum 200
   */
  limit?: number;
};

export type ListAllPartsOrderItemsStatus =
  (typeof ListAllPartsOrderItemsStatus)[keyof typeof ListAllPartsOrderItemsStatus];

export const ListAllPartsOrderItemsStatus = {
  ordered: 'ordered',
  shipped: 'shipped',
  arrived: 'arrived',
  returned: 'returned',
} as const;

export type ListAllPartsOrderItemsContextType =
  (typeof ListAllPartsOrderItemsContextType)[keyof typeof ListAllPartsOrderItemsContextType];

export const ListAllPartsOrderItemsContextType = {
  repair_order: 'repair_order',
  inventory: 'inventory',
} as const;

export type ListPartsOrderSessionsParams = {
  contextType?: ListPartsOrderSessionsContextType;
  contextRepairOrderId?: string;
  status?: ListPartsOrderSessionsStatus;
};

export type ListPartsOrderSessionsContextType =
  (typeof ListPartsOrderSessionsContextType)[keyof typeof ListPartsOrderSessionsContextType];

export const ListPartsOrderSessionsContextType = {
  repair_order: 'repair_order',
  inventory: 'inventory',
} as const;

export type ListPartsOrderSessionsStatus =
  (typeof ListPartsOrderSessionsStatus)[keyof typeof ListPartsOrderSessionsStatus];

export const ListPartsOrderSessionsStatus = {
  draft: 'draft',
  submitted: 'submitted',
  partially_received: 'partially_received',
  received: 'received',
  cancelled: 'cancelled',
} as const;

export type ListMessagesParams = {
  /**
   * Page number
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
};

export type GetCouponsReportParams = {
  /**
   * Start date for the report period (YYYY-MM-DD)
   */
  startDate: string;
  /**
   * End date for the report period (YYYY-MM-DD)
   */
  endDate: string;
  /**
   * Response format
   */
  format?: GetCouponsReportFormat;
  /**
   * Filter to a single coupon
   */
  couponId?: string;
};

export type GetCouponsReportFormat = (typeof GetCouponsReportFormat)[keyof typeof GetCouponsReportFormat];

export const GetCouponsReportFormat = {
  json: 'json',
  csv: 'csv',
} as const;

export type GetDiscountsReportParams = {
  /**
   * Start date for the report period (YYYY-MM-DD)
   */
  startDate: string;
  /**
   * End date for the report period (YYYY-MM-DD)
   */
  endDate: string;
  /**
   * Response format
   */
  format?: GetDiscountsReportFormat;
  /**
   * Filter by discount source
   */
  source?: GetDiscountsReportSource;
  /**
   * Filter to a specific coupon
   */
  couponId?: string;
  /**
   * Filter by service advisor user ID
   */
  advisorId?: string;
  /**
   * Filter by customer ID
   */
  customerId?: string;
};

export type GetDiscountsReportFormat = (typeof GetDiscountsReportFormat)[keyof typeof GetDiscountsReportFormat];

export const GetDiscountsReportFormat = {
  json: 'json',
  csv: 'csv',
} as const;

export type GetDiscountsReportSource = (typeof GetDiscountsReportSource)[keyof typeof GetDiscountsReportSource];

export const GetDiscountsReportSource = {
  customer: 'customer',
  coupon: 'coupon',
  manual: 'manual',
  rebate: 'rebate',
} as const;

export type GetPartsPurchasesReportParams = {
  /**
   * Start date for the report period (YYYY-MM-DD)
   */
  startDate: string;
  /**
   * End date for the report period (YYYY-MM-DD)
   */
  endDate: string;
  /**
   * Response format
   */
  format?: GetPartsPurchasesReportFormat;
  /**
   * Dimension to group parts purchase data by
   */
  groupBy: GetPartsPurchasesReportGroupBy;
  /**
   * Filter by supplier name
   */
  supplier?: string;
  /**
   * Filter by brand
   */
  brand?: string;
  /**
   * Filter by category
   */
  category?: string;
  /**
   * Filter by part type
   */
  partType?: string;
  /**
   * Filter by service advisor
   */
  serviceAdvisor?: string;
};

export type GetPartsPurchasesReportFormat =
  (typeof GetPartsPurchasesReportFormat)[keyof typeof GetPartsPurchasesReportFormat];

export const GetPartsPurchasesReportFormat = {
  json: 'json',
  csv: 'csv',
} as const;

export type GetPartsPurchasesReportGroupBy =
  (typeof GetPartsPurchasesReportGroupBy)[keyof typeof GetPartsPurchasesReportGroupBy];

export const GetPartsPurchasesReportGroupBy = {
  supplier: 'supplier',
  brand: 'brand',
  category: 'category',
  partType: 'partType',
  serviceAdvisor: 'serviceAdvisor',
} as const;

export type GetTirePurchasesReportParams = {
  /**
   * Start date for the report period (YYYY-MM-DD)
   */
  startDate: string;
  /**
   * End date for the report period (YYYY-MM-DD)
   */
  endDate: string;
  /**
   * Response format
   */
  format?: GetTirePurchasesReportFormat;
  /**
   * Dimension to group tire purchase data by
   */
  groupBy: GetTirePurchasesReportGroupBy;
  /**
   * Filter by tire size
   */
  size?: string;
  /**
   * Filter by brand
   */
  brand?: string;
  /**
   * Filter by supplier name
   */
  supplier?: string;
  /**
   * Filter by tire type
   */
  type?: string;
  /**
   * Filter by service advisor
   */
  serviceAdvisor?: string;
};

export type GetTirePurchasesReportFormat =
  (typeof GetTirePurchasesReportFormat)[keyof typeof GetTirePurchasesReportFormat];

export const GetTirePurchasesReportFormat = {
  json: 'json',
  csv: 'csv',
} as const;

export type GetTirePurchasesReportGroupBy =
  (typeof GetTirePurchasesReportGroupBy)[keyof typeof GetTirePurchasesReportGroupBy];

export const GetTirePurchasesReportGroupBy = {
  size: 'size',
  brand: 'brand',
  supplier: 'supplier',
  type: 'type',
  serviceAdvisor: 'serviceAdvisor',
} as const;

export type GetTechEfficiencyReportParams = {
  /**
   * Start date for the report period (YYYY-MM-DD or ISO datetime)
   */
  startDate: string;
  /**
   * End date for the report period (YYYY-MM-DD or ISO datetime)
   */
  endDate: string;
  /**
   * Include inactive technicians in the report
   */
  includeInactive?: GetTechEfficiencyReportIncludeInactive;
  /**
   * Response format
   */
  format?: GetTechEfficiencyReportFormat;
};

export type GetTechEfficiencyReportIncludeInactive =
  (typeof GetTechEfficiencyReportIncludeInactive)[keyof typeof GetTechEfficiencyReportIncludeInactive];

export const GetTechEfficiencyReportIncludeInactive = {
  true: 'true',
  false: 'false',
} as const;

export type GetTechEfficiencyReportFormat =
  (typeof GetTechEfficiencyReportFormat)[keyof typeof GetTechEfficiencyReportFormat];

export const GetTechEfficiencyReportFormat = {
  json: 'json',
  csv: 'csv',
} as const;

export type GetTechEfficiencyReportDetailParams = {
  /**
   * Start date for the detail period (YYYY-MM-DD or ISO datetime)
   */
  startDate: string;
  /**
   * End date for the detail period (YYYY-MM-DD or ISO datetime)
   */
  endDate: string;
};
